import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  ChevronUp,
  ChevronDown,
  Play,
  Pause,
  Star,
  Plus,
  Home,
  Tv,
  Film,
  TrendingUp,
  Heart,
  User,
  Bell,
  Music,
  Image as ImageIcon,
  Video,
  Loader2,
  AlertCircle,
  RefreshCw,
  Clock,
  X,
  Calendar,
  Eye,
  ExternalLink,
  MoreVertical,
  Pencil,
  Check,
  Trash2,
  ListPlus,
  Download,
  Share2,
  History,
  Info,
  Users,
  Settings,
  PlayCircle,
  ListVideo,
  Save,
  Volume2,
  Volume1,
  VolumeX,
  Shuffle,
  Repeat,
  SkipBack,
  SkipForward,
  Sparkles,
  Maximize,
  Minimize,
  Minimize2,
  RotateCcw,
  RotateCw,
  List,
  BookOpen
} from 'lucide-react';
import { apiCall } from '../services/api';
import { toast } from 'sonner';
import { useMiniPlayer } from '../context/MiniPlayerContext';
import { useSpotify } from '../context/SpotifyContext';

// Extracted Components
import { LiveTVSection, YouTubeMusicVideosSection } from '../components/media_factory';
import TVShowsSection from '../components/media_factory/TVShowsSection';

// Portal component to render modals at document body level
const Portal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

// 3-Dot Menu Component - rendered via Portal for proper z-index
const MediaCardMenu = ({ item, onClose, onAction, plexUrl, position }) => {
  const menuRef = useRef(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const menuItems = [
    { icon: PlayCircle, label: 'Watch Together...', action: 'watch-together' },
    { icon: ListPlus, label: 'Play Next', action: 'play-next' },
    { icon: ListVideo, label: 'Add to Queue', action: 'add-queue' },
    { icon: Plus, label: 'Add to...', action: 'add-to' },
    { divider: true },
    { icon: Check, label: item.viewCount > 0 ? 'Mark as Unwatched' : 'Mark as Watched', action: item.viewCount > 0 ? 'mark-unwatched' : 'mark-watched' },
    { icon: RefreshCw, label: 'Refresh Metadata', action: 'refresh-metadata' },
    { icon: Settings, label: 'Analyze', action: 'analyze' },
    { icon: Search, label: 'Match...', action: 'match' },
    { divider: true },
    { icon: Download, label: 'Download', action: 'download' },
    { icon: Settings, label: 'Optimize...', action: 'optimize' },
    { icon: Users, label: 'Grant Access...', action: 'grant-access' },
    { icon: Trash2, label: 'Delete', action: 'delete', danger: true },
    { divider: true },
    { icon: History, label: 'View Play History', action: 'play-history' },
    { icon: AlertCircle, label: 'Report Issue...', action: 'report-issue' },
    { icon: Info, label: 'Get Info', action: 'get-info' },
  ];

  return (
    <Portal>
      <div 
        ref={menuRef}
        className="fixed w-56 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl overflow-hidden"
        style={{ 
          top: position?.top || 0, 
          left: position?.left || 0,
          zIndex: 99999 
        }}
        onClick={(e) => e.stopPropagation()}
      >
      {showConfirmDelete ? (
        <div className="p-4">
          <p className="text-white text-sm mb-3">Delete &ldquo;{item.title}&rdquo;?</p>
          <p className="text-gray-400 text-xs mb-4">This will permanently remove the file from your library.</p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowConfirmDelete(false)}
              className="flex-1 px-3 py-2 bg-gray-700 text-white rounded text-sm hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={() => onAction('delete-confirm', item)}
              className="flex-1 px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-500"
            >
              Delete
            </button>
          </div>
        </div>
      ) : (
        <div className="py-1 max-h-96 overflow-y-auto">
          {menuItems.map((menuItem, index) => {
            if (menuItem.divider) {
              return <div key={index} className="border-t border-gray-700 my-1" />;
            }
            const Icon = menuItem.icon;
            return (
              <button
                key={index}
                onClick={() => {
                  if (menuItem.action === 'delete') {
                    setShowConfirmDelete(true);
                  } else {
                    onAction(menuItem.action, item);
                    if (!['add-to', 'get-info', 'play-history'].includes(menuItem.action)) {
                      onClose();
                    }
                  }
                }}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-gray-800 transition-colors ${
                  menuItem.danger ? 'text-red-400 hover:text-red-300' : 'text-gray-200'
                }`}
              >
                <Icon size={16} className="flex-shrink-0" />
                <span>{menuItem.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
    </Portal>
  );
};

// Edit Modal Component
const EditMediaModal = ({ item, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    titleSort: '',
    originalTitle: '',
    summary: '',
    tagline: '',
    contentRating: '',
    year: '',
    originallyAvailableAt: '',
    studio: '',
    rating: '',
    genres: '',
    directors: '',
    actors: '',
  });
  const [newThumbnail, setNewThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const thumbnailInputRef = useRef(null);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await apiCall(`/plex/item/${item.ratingKey}/info`);
        if (res.status === 'success' && res.info) {
          setInfo(res.info);
          setFormData({
            title: res.info.title || '',
            titleSort: res.info.titleSort || '',
            originalTitle: res.info.originalTitle || '',
            summary: res.info.summary || '',
            tagline: res.info.tagline || '',
            contentRating: res.info.contentRating || '',
            year: res.info.year || '',
            originallyAvailableAt: res.info.originallyAvailableAt || '',
            studio: res.info.studio || '',
            rating: res.info.rating || '',
            genres: (res.info.genres || []).join(', '),
            directors: (res.info.directors || []).join(', '),
            actors: (res.info.actors || []).join(', '),
          });
        }
      } catch (err) {
        console.error('Failed to fetch item info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [item.ratingKey]);

  // Handle thumbnail file selection
  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewThumbnail(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setThumbnailPreview(previewUrl);
    }
  };

  // Upload thumbnail to Plex
  const uploadThumbnail = async () => {
    if (!newThumbnail) return true; // No thumbnail to upload
    
    setUploadingThumb(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', newThumbnail);
      
      // Use relative URL to go through the proxy (avoids CORS issues)
      const response = await fetch(`/api/plex/item/${item.ratingKey}/poster`, {
        method: 'POST',
        body: formDataUpload,
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to upload thumbnail');
      }
      
      if (result.status === 'success') {
        console.log('Poster uploaded successfully');
        return true;
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Failed to upload thumbnail:', err);
      alert('Failed to upload thumbnail: ' + err.message);
      return false;
    } finally {
      setUploadingThumb(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // First upload thumbnail if changed
      if (newThumbnail) {
        const thumbSuccess = await uploadThumbnail();
        if (!thumbSuccess) {
          setSaving(false);
          return;
        }
      }

      const updateData = {
        title: formData.title,
        titleSort: formData.titleSort || undefined,
        originalTitle: formData.originalTitle || undefined,
        summary: formData.summary,
        tagline: formData.tagline || undefined,
        contentRating: formData.contentRating || undefined,
        year: formData.year ? parseInt(formData.year) : undefined,
        originallyAvailableAt: formData.originallyAvailableAt || undefined,
        studio: formData.studio || undefined,
        rating: formData.rating ? parseFloat(formData.rating) : undefined,
        genres: formData.genres ? formData.genres.split(',').map(g => g.trim()).filter(Boolean) : undefined,
        directors: formData.directors ? formData.directors.split(',').map(d => d.trim()).filter(Boolean) : undefined,
        actors: formData.actors ? formData.actors.split(',').map(a => a.trim()).filter(Boolean) : undefined,
      };

      const res = await apiCall(`/plex/item/${item.ratingKey}/metadata`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      if (res.status === 'success') {
        onSave?.(updateData);
        onClose();
      }
    } catch (err) {
      console.error('Failed to update metadata:', err);
      alert('Failed to save changes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (thumbnailPreview) {
        URL.revokeObjectURL(thumbnailPreview);
      }
    };
  }, [thumbnailPreview]);

  return (
    <Portal>
      <div 
        className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
        style={{ zIndex: 99999 }}
        onClick={onClose}
      >
        <div 
          className="bg-gray-900 border border-gray-600 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <Pencil size={20} className="text-cyan-400" />
              <h2 className="text-xl font-bold text-white">Edit Metadata</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-cyan-400" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex gap-6">
                {/* Poster with Edit */}
                <div className="flex-shrink-0">
                  <div className="relative group">
                    {thumbnailPreview || info?.thumb ? (
                      <img 
                        src={thumbnailPreview || info.thumb} 
                        alt={info?.title || 'Poster'}
                        className="w-40 h-60 object-cover rounded-lg border border-gray-700"
                      />
                    ) : (
                      <div className="w-40 h-60 bg-gray-800 rounded-lg flex items-center justify-center border border-gray-700">
                        <Film size={40} className="text-gray-600" />
                      </div>
                    )}
                    {/* Overlay for changing thumbnail */}
                    <div 
                      className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center cursor-pointer"
                      onClick={() => thumbnailInputRef.current?.click()}
                    >
                      <ImageIcon size={24} className="text-white mb-2" />
                      <span className="text-white text-sm font-medium">Change Poster</span>
                      {newThumbnail && (
                        <span className="text-cyan-400 text-xs mt-1">New image selected</span>
                      )}
                    </div>
                    <input
                      ref={thumbnailInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailChange}
                      className="hidden"
                    />
                  </div>
                  {/* Clear new thumbnail button */}
                  {newThumbnail && (
                    <button
                      onClick={() => {
                        setNewThumbnail(null);
                        setThumbnailPreview(null);
                        if (thumbnailInputRef.current) {
                          thumbnailInputRef.current.value = '';
                        }
                      }}
                      className="mt-2 w-full text-xs text-red-400 hover:text-red-300"
                    >
                      Remove new image
                    </button>
                  )}
                </div>

                {/* Form Fields */}
                <div className="flex-1 space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>

                  {/* Title Sort & Original Title */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Sort Title</label>
                      <input
                        type="text"
                        value={formData.titleSort}
                        onChange={(e) => handleChange('titleSort', e.target.value)}
                        placeholder="e.g., Matrix, The"
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Original Title</label>
                      <input
                        type="text"
                        value={formData.originalTitle}
                        onChange={(e) => handleChange('originalTitle', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                  </div>

                  {/* Year & Content Rating */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Year</label>
                      <input
                        type="number"
                        value={formData.year}
                        onChange={(e) => handleChange('year', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Content Rating</label>
                      <input
                        type="text"
                        value={formData.contentRating}
                        onChange={(e) => handleChange('contentRating', e.target.value)}
                        placeholder="e.g., PG-13"
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Rating (0-10)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="10"
                        value={formData.rating}
                        onChange={(e) => handleChange('rating', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                  </div>

                  {/* Studio & Release Date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Studio</label>
                      <input
                        type="text"
                        value={formData.studio}
                        onChange={(e) => handleChange('studio', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Release Date</label>
                      <input
                        type="date"
                        value={formData.originallyAvailableAt}
                        onChange={(e) => handleChange('originallyAvailableAt', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                  </div>

                  {/* Tagline */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Tagline</label>
                    <input
                      type="text"
                      value={formData.tagline}
                      onChange={(e) => handleChange('tagline', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>

                  {/* Summary */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Summary</label>
                    <textarea
                      value={formData.summary}
                      onChange={(e) => handleChange('summary', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 resize-none"
                    />
                  </div>

                  {/* Genres */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Genres (comma-separated)</label>
                    <input
                      type="text"
                      value={formData.genres}
                      onChange={(e) => handleChange('genres', e.target.value)}
                      placeholder="Action, Adventure, Comedy"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>

                  {/* Directors */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Directors (comma-separated)</label>
                    <input
                      type="text"
                      value={formData.directors}
                      onChange={(e) => handleChange('directors', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>

                  {/* Actors */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Cast (comma-separated)</label>
                    <input
                      type="text"
                      value={formData.actors}
                      onChange={(e) => handleChange('actors', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>

                  {/* File Info (Read Only) */}
                  {info?.file?.path && (
                    <div className="mt-6 pt-4 border-t border-gray-700">
                      <h3 className="text-sm font-medium text-gray-400 mb-2">File Information</h3>
                      <div className="text-xs text-gray-500 space-y-1">
                        <p className="truncate">Path: {info.file.path}</p>
                        {info.file.size && (
                          <p>Size: {(info.file.size / (1024 * 1024 * 1024)).toFixed(2)} GB</p>
                        )}
                        {info.media && (
                          <p>
                            {info.media.width}x{info.media.height} • {info.media.videoCodec?.toUpperCase()} • {info.media.audioCodec?.toUpperCase()}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || uploadingThumb}
              className="flex items-center gap-2 px-6 py-2 bg-cyan-500 text-black font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50"
            >
              {(saving || uploadingThumb) ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
              {uploadingThumb ? 'Uploading Poster...' : saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

// Play History Modal
const PlayHistoryModal = ({ item, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await apiCall(`/plex/item/${item.ratingKey}/play-history`);
        setHistory(res);
      } catch (err) {
        console.error('Failed to fetch play history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [item.ratingKey]);

  return (
    <Portal>
      <div 
        className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
        style={{ zIndex: 99999 }}
        onClick={onClose}
      >
        <div 
          className="bg-gray-900 border border-gray-600 rounded-xl w-full max-w-md overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <History size={20} className="text-cyan-400" />
              <h2 className="text-lg font-bold text-white">Play History</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={32} className="animate-spin text-cyan-400" />
              </div>
            ) : history ? (
              <div className="space-y-4">
                <h3 className="text-white font-medium">{history.title}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Times Played</span>
                    <span className="text-white font-medium">{history.viewCount || 0}</span>
                  </div>
                  {history.lastViewedAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Last Watched</span>
                      <span className="text-white">{new Date(history.lastViewedAt * 1000).toLocaleDateString()}</span>
                    </div>
                  )}
                  {history.viewOffset > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Resume Position</span>
                      <span className="text-white">{Math.floor(history.viewOffset / 60000)}:{String(Math.floor((history.viewOffset % 60000) / 1000)).padStart(2, '0')}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-center py-4">No play history available</p>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
};

// Match Modal Component - Search and apply metadata matches
const MatchModal = ({ item, onClose, onMatch }) => {
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [applying, setApplying] = useState(false);
  const [matches, setMatches] = useState([]);
  const [searchTitle, setSearchTitle] = useState('');
  const [searchYear, setSearchYear] = useState('');
  const [selectedMatch, setSelectedMatch] = useState(null);

  // Fetch initial matches on mount
  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const res = await apiCall(`/plex/item/${item.ratingKey}/matches`);
        if (res.status === 'success') {
          setMatches(res.matches || []);
          setSearchTitle(res.currentTitle || item.title || '');
          setSearchYear(res.currentYear?.toString() || item.year?.toString() || '');
        }
      } catch (err) {
        console.error('Failed to fetch matches:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMatches();
  }, [item.ratingKey, item.title, item.year]);

  // Search for matches with custom title/year
  const handleSearch = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchTitle) params.append('title', searchTitle);
      if (searchYear) params.append('year', searchYear);
      
      const res = await apiCall(`/plex/item/${item.ratingKey}/matches?${params.toString()}`);
      if (res.status === 'success') {
        setMatches(res.matches || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  // Apply the selected match
  const handleApplyMatch = async () => {
    if (!selectedMatch) return;
    
    setApplying(true);
    try {
      const res = await apiCall(`/plex/item/${item.ratingKey}/match`, {
        method: 'POST',
        body: JSON.stringify({
          guid: selectedMatch.guid,
          name: selectedMatch.name
        })
      });
      
      if (res.status === 'success') {
        onMatch?.(selectedMatch);
        onClose();
      }
    } catch (err) {
      console.error('Failed to apply match:', err);
      alert('Failed to apply match: ' + err.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Portal>
      <div 
        className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
        style={{ zIndex: 99999 }}
        onClick={onClose}
      >
        <div 
          className="bg-gray-900 border border-gray-600 rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <Search size={20} className="text-cyan-400" />
              <h2 className="text-xl font-bold text-white">Match Media</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Current Item Info */}
          <div className="p-4 border-b border-gray-700 bg-gray-800/50">
            <div className="flex items-center gap-4">
              {item.thumb ? (
                <img src={item.thumb} alt={item.title} className="w-16 h-24 object-cover rounded" />
              ) : (
                <div className="w-16 h-24 bg-gray-700 rounded flex items-center justify-center">
                  <Film size={24} className="text-gray-500" />
                </div>
              )}
              <div>
                <h3 className="text-white font-medium">{item.title}</h3>
                {item.year && <p className="text-gray-400 text-sm">{item.year}</p>}
                <p className="text-gray-500 text-xs mt-1">Current metadata</p>
              </div>
            </div>
          </div>

          {/* Search Form */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchTitle}
                  onChange={(e) => setSearchTitle(e.target.value)}
                  placeholder="Title"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <div className="w-24">
                <input
                  type="number"
                  value={searchYear}
                  onChange={(e) => setSearchYear(e.target.value)}
                  placeholder="Year"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={searching}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {searching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                Search
              </button>
            </div>
          </div>

          {/* Matches List */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={32} className="animate-spin text-cyan-400" />
              </div>
            ) : matches.length === 0 ? (
              <div className="text-center py-12">
                <Search size={48} className="text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No matches found</p>
                <p className="text-gray-500 text-sm mt-2">Try adjusting the title or year</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-gray-400 text-sm mb-3">{matches.length} potential matches found</p>
                {matches.map((match, index) => (
                  <div
                    key={match.guid || index}
                    onClick={() => setSelectedMatch(match)}
                    className={`p-3 rounded-lg cursor-pointer transition-all border ${
                      selectedMatch?.guid === match.guid
                        ? 'bg-cyan-500/20 border-cyan-500'
                        : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {match.thumb ? (
                        <img 
                          src={match.thumb} 
                          alt={match.name}
                          className="w-12 h-18 object-cover rounded flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-12 h-18 bg-gray-700 rounded flex-shrink-0 flex items-center justify-center">
                          <Film size={16} className="text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-white font-medium truncate">{match.name}</h4>
                          {match.year && (
                            <span className="text-gray-400 text-sm flex-shrink-0">({match.year})</span>
                          )}
                          {match.score && (
                            <span className="text-xs px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full flex-shrink-0">
                              {Math.round(match.score)}% match
                            </span>
                          )}
                        </div>
                        {match.summary && (
                          <p className="text-gray-400 text-sm mt-1 line-clamp-2">{match.summary}</p>
                        )}
                      </div>
                      {selectedMatch?.guid === match.guid && (
                        <Check size={20} className="text-cyan-400 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-700">
            <p className="text-gray-500 text-sm">
              {selectedMatch ? `Selected: ${selectedMatch.name}` : 'Select a match to apply'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyMatch}
                disabled={!selectedMatch || applying}
                className="flex items-center gap-2 px-6 py-2 bg-cyan-500 text-black font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {applying ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Check size={16} />
                )}
                {applying ? 'Applying...' : 'Apply Match'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

// Movie Card Component with 3-dot menu and edit
const MediaCard = ({ item, onClick, onMenuAction, plexUrl, isInWatchlist, onToggleWatchlist }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuButtonRef = useRef(null);
  const inWatchlist = isInWatchlist?.(item) || false;

  const handleMenuClick = (e) => {
    e.stopPropagation();
    // Calculate position based on button location
    if (menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      // Position menu below and to the left of the button
      setMenuPosition({
        top: rect.bottom + 4,
        left: Math.max(rect.right - 224, 8) // 224 = menu width (w-56 = 14rem = 224px)
      });
    }
    setShowMenu(true);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    onMenuAction?.('edit', item);
  };

  return (
    <div 
      className="flex-shrink-0 w-full group cursor-pointer relative"
      onClick={() => onClick?.(item)}
    >
      <div className="relative overflow-hidden rounded-lg bg-gray-900 transition-all duration-300 hover:shadow-[0_0_25px_rgba(255,255,255,0.15)] group-hover:scale-105">
        {item.thumb ? (
          <img 
            src={item.thumb} 
            alt={item.title}
            className="w-full aspect-[2/3] object-cover rounded-lg"
            loading="lazy"
          />
        ) : (
          <div className="w-full aspect-[2/3] bg-gray-800 flex items-center justify-center rounded-lg">
            <Film size={24} className="text-gray-600 md:w-10 md:h-10" />
          </div>
        )}
        
        {/* Top action buttons (always visible on hover) */}
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={handleEditClick}
            className="p-1.5 bg-black/70 hover:bg-cyan-500 rounded-full transition-colors"
            title="Edit"
          >
            <Pencil size={14} className="text-white" />
          </button>
          <button 
            ref={menuButtonRef}
            onClick={handleMenuClick}
            className="p-1.5 bg-black/70 hover:bg-gray-600 rounded-full transition-colors relative"
            title="More options"
          >
            <MoreVertical size={14} className="text-white" />
          </button>
        </div>

        {/* 3-dot dropdown menu */}
        {showMenu && (
          <MediaCardMenu 
            item={item}
            position={menuPosition}
            onClose={() => setShowMenu(false)}
            onAction={(action, menuItem) => {
              onMenuAction?.(action, menuItem);
              if (!['get-info', 'play-history', 'add-to'].includes(action)) {
                setShowMenu(false);
              }
            }}
            plexUrl={plexUrl}
          />
        )}

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3 pointer-events-none">
          <button 
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center mb-2 hover:scale-110 transition-transform pointer-events-auto"
            onClick={(e) => {
              e.stopPropagation();
              onClick?.(item);
            }}
          >
            <Play size={20} className="text-black ml-1" fill="black" />
          </button>
          <div className="flex gap-2 pointer-events-auto">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onMenuAction?.('add-to', item);
              }}
              className="p-2 bg-gray-800/80 rounded-full hover:bg-gray-700 transition-colors"
              title="Add to Playlist"
            >
              <Plus size={16} className="text-white" />
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onToggleWatchlist?.(item);
              }}
              className={`p-2 rounded-full transition-all duration-300 ${
                inWatchlist 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-gray-800/80 hover:bg-gray-700'
              }`}
              title={inWatchlist ? 'Remove from My List' : 'Add to My List'}
            >
              <Heart 
                size={16} 
                className={inWatchlist ? 'text-white' : 'text-white'} 
                fill={inWatchlist ? 'currentColor' : 'none'}
              />
            </button>
          </div>
        </div>

        {/* Watched indicator */}
        {item.viewCount > 0 && (
          <div className="absolute top-1 left-1 md:top-2 md:left-2 w-4 h-4 md:w-5 md:h-5 bg-cyan-500 rounded-full flex items-center justify-center">
            <Check size={10} className="text-black md:w-3 md:h-3" />
          </div>
        )}

        {/* Progress bar for on-deck items */}
        {item.progress && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
            <div className="h-full bg-cyan-400" style={{ width: `${item.progress}%` }} />
          </div>
        )}
      </div>
      {/* Rating and Year - Hidden on very small screens */}
      <div className="hidden sm:flex items-center gap-1 md:gap-2 mt-1 md:mt-2 text-[10px] md:text-xs text-gray-400">
        {item.rating && (
          <>
            <Star size={10} className="text-yellow-500 fill-yellow-500 md:w-3 md:h-3" />
            <span>{item.rating.toFixed(1)}</span>
            <span>•</span>
          </>
        )}
        <span>{item.year || item.type}</span>
      </div>
      {/* Title */}
      <h4 className="text-white text-[11px] sm:text-xs md:text-sm font-medium mt-1 line-clamp-2">{item.title}</h4>
      {item.grandparentTitle && (
        <p className="text-gray-500 text-[10px] md:text-xs truncate hidden sm:block">{item.grandparentTitle}</p>
      )}
    </div>
  );
};

// Music Track Card
const MusicCard = ({ item, onClick }) => (
  <div 
    className="flex items-center gap-3 p-3 rounded-lg bg-gray-900 hover:bg-gray-800 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all cursor-pointer group"
    onClick={() => onClick?.(item)}
  >
    {item.thumb ? (
      <img src={item.thumb} alt={item.title} className="w-12 h-12 rounded object-cover" />
    ) : (
      <div className="w-12 h-12 rounded bg-gray-800 flex items-center justify-center">
        <Music size={20} className="text-gray-600" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <h4 className="text-white text-sm font-medium truncate">{item.title}</h4>
      <p className="text-gray-400 text-xs truncate">{item.grandparentTitle || item.parentTitle}</p>
    </div>
    <button className="opacity-0 group-hover:opacity-100 p-2 bg-cyan-500 rounded-full transition-all">
      <Play size={14} className="text-black" fill="black" />
    </button>
  </div>
);

// Library Content Component (extracted to properly use hooks)
const LibraryContent = ({ 
  activeTab, 
  libraries, 
  libraryContent, 
  libraryLoading, 
  selectedLetter, 
  setSelectedLetter, 
  filterByLetter, 
  handleItemClick,
  handleMenuAction,
  plexUrl,
  alphabet,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  filterGenre,
  setFilterGenre,
  filterYear,
  setFilterYear,
  filterActor,
  setFilterActor,
  filterRating,
  setFilterRating,
  onAddToPlaylist,
  isInWatchlist,
  toggleWatchlistItem
}) => {
  const libraryId = activeTab.replace('library-', '');
  const library = libraries.find(l => l.id === libraryId);
  const rawContent = libraryContent[libraryId];
  const content = useMemo(() => rawContent || [], [rawContent]);
  const isLoading = libraryLoading[libraryId];
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 105;
  
  // State for artist filter (for Music Videos)
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [artistImages, setArtistImages] = useState({}); // { normalizedName: imageUrl }
  
  // Check if this is the Music Videos library (memoized)
  const isMusicVideos = useMemo(() => 
    library?.title?.toLowerCase().includes('music video') || false
  , [library?.title]);
  
  // Extract artists from music video titles (format: "Artist - Song Title")
  const artists = useMemo(() => {
    if (!isMusicVideos) return [];
    
    const artistMap = new Map(); // normalized name -> { name, displayName, count, thumb }
    
    content.forEach(item => {
      const title = item.title || '';
      // Try to extract artist from "Artist - Song" format
      const dashIndex = title.indexOf(' - ');
      if (dashIndex > 0) {
        let artistName = title.substring(0, dashIndex).trim();
        // Clean up common variations
        artistName = artistName.replace(/\s*\(.*\)\s*$/, '').trim(); // Remove parentheses
        artistName = artistName.replace(/\s*ft\.?\s*.*/i, '').trim(); // Remove "ft." features
        artistName = artistName.replace(/\s*feat\.?\s*.*/i, '').trim(); // Remove "feat." features
        
        if (artistName && artistName.length > 1) {
          // Normalize key for deduplication (lowercase, remove special chars)
          const normalizedKey = artistName.toLowerCase().replace(/[^a-z0-9]/g, '');
          
          if (artistMap.has(normalizedKey)) {
            const existing = artistMap.get(normalizedKey);
            existing.count++;
            // Keep the more common capitalization (first occurrence)
          } else {
            artistMap.set(normalizedKey, {
              name: normalizedKey,
              displayName: artistName,
              count: 1,
              thumb: item.thumb // Use first video's thumbnail as artist image
            });
          }
        }
      }
    });
    
    // Sort alphabetically by display name
    return Array.from(artistMap.values())
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [content, isMusicVideos]);
  
  // Get available letters for artist alphabet navigation
  const artistAlphabet = useMemo(() => {
    const letters = new Set();
    artists.forEach(artist => {
      const firstChar = (artist.displayName || '')[0]?.toUpperCase();
      if (firstChar) {
        if (/[A-Z]/.test(firstChar)) {
          letters.add(firstChar);
        } else {
          letters.add('#'); // Numbers and special chars
        }
      }
    });
    return ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')].filter(l => letters.has(l));
  }, [artists]);
  
  // Ref for scrolling artist row
  const artistRowRef = React.useRef(null);
  
  // Scroll to artist starting with letter
  const scrollToArtistLetter = (letter) => {
    if (!artistRowRef.current) return;
    
    // Find the first artist button that starts with this letter
    const buttons = artistRowRef.current.querySelectorAll('button[data-artist]');
    for (const btn of buttons) {
      const artistName = btn.getAttribute('data-artist') || '';
      const firstChar = artistName[0]?.toUpperCase();
      const matches = letter === '#' 
        ? firstChar && !/[A-Z]/.test(firstChar)
        : firstChar === letter;
      
      if (matches) {
        btn.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        break;
      }
    }
  };
  
  // Fetch artist images from Deezer (free, no auth required)
  const artistImagesFetchedRef = React.useRef(false);
  
  useEffect(() => {
    if (!isMusicVideos || artists.length === 0 || artistImagesFetchedRef.current) return;
    
    artistImagesFetchedRef.current = true;
    
    const fetchArtistImages = async () => {
      const newImages = {};
      
      // Fetch in batches of 30
      for (let i = 0; i < artists.length; i += 30) {
        const batch = artists.slice(i, i + 30);
        
        try {
          const response = await fetch('/api/plex/artist-images', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              artists: batch.map(a => ({ name: a.name, displayName: a.displayName }))
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            data.results?.forEach(result => {
              if (result.image) {
                newImages[result.name] = result.image;
              }
            });
          }
        } catch (err) {
          console.error('Failed to fetch artist images batch:', err);
        }
      }
      
      if (Object.keys(newImages).length > 0) {
        setArtistImages(newImages);
      }
    };
    
    fetchArtistImages();
  }, [artists, isMusicVideos]);
  
  // Extract unique genres, years, actors, and ratings from content
  const filterOptions = useMemo(() => {
    const genres = new Set();
    const years = new Set();
    const actors = new Set();
    const ratings = new Set();
    
    content.forEach(item => {
      if (item.genres) {
        item.genres.forEach(g => genres.add(g));
      }
      if (item.year) years.add(item.year);
      if (item.actors) {
        // Include all actors from each movie
        item.actors.forEach(a => actors.add(a));
      }
      if (item.contentRating) ratings.add(item.contentRating);
    });
    
    return {
      genres: Array.from(genres).sort(),
      years: Array.from(years).sort((a, b) => b - a),
      actors: Array.from(actors).sort(),
      ratings: Array.from(ratings).sort()
    };
  }, [content]);
  
  // Apply all filters and sorting
  const filteredAndSortedContent = useMemo(() => {
    let result = [...content];
    
    // Filter by letter
    if (selectedLetter) {
      result = result.filter(item => {
        const firstChar = (item.title || '')[0]?.toUpperCase();
        if (selectedLetter === '#') {
          return firstChar && !/[A-Z]/.test(firstChar);
        }
        return firstChar === selectedLetter;
      });
    }
    
    // Filter by artist (for Music Videos) - uses normalized key
    if (selectedArtist) {
      result = result.filter(item => {
        const title = item.title || '';
        const dashIndex = title.indexOf(' - ');
        if (dashIndex > 0) {
          let artistName = title.substring(0, dashIndex).trim();
          artistName = artistName.replace(/\s*\(.*\)\s*$/, '').trim();
          artistName = artistName.replace(/\s*ft\.?\s*.*/i, '').trim();
          artistName = artistName.replace(/\s*feat\.?\s*.*/i, '').trim();
          const normalizedKey = artistName.toLowerCase().replace(/[^a-z0-9]/g, '');
          return normalizedKey === selectedArtist;
        }
        return false;
      });
    }
    
    // Filter by genre
    if (filterGenre) {
      result = result.filter(item => 
        item.genres && item.genres.includes(filterGenre)
      );
    }
    
    // Filter by year
    if (filterYear) {
      result = result.filter(item => item.year === parseInt(filterYear));
    }
    
    // Filter by actor
    if (filterActor) {
      result = result.filter(item => 
        item.actors && item.actors.includes(filterActor)
      );
    }
    
    // Filter by content rating (PG-13, R, etc.)
    if (filterRating) {
      result = result.filter(item => item.contentRating === filterRating);
    }
    
    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'title':
          comparison = (a.title || '').localeCompare(b.title || '');
          break;
        case 'year':
          comparison = (a.year || 0) - (b.year || 0);
          break;
        case 'rating':
          comparison = (a.rating || 0) - (b.rating || 0);
          break;
        case 'addedAt':
          comparison = (a.addedAt || 0) - (b.addedAt || 0);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });
    
    return result;
  }, [content, selectedLetter, selectedArtist, filterGenre, filterYear, filterActor, filterRating, sortBy, sortOrder]);
  
  // Get available letters (letters that have content)
  const availableLetters = useMemo(() => {
    const letters = new Set();
    content.forEach(item => {
      const firstChar = (item.title || '')[0]?.toUpperCase();
      if (firstChar) {
        if (/[A-Z]/.test(firstChar)) {
          letters.add(firstChar);
        } else {
          letters.add('#');
        }
      }
    });
    return letters;
  }, [content]);
  
  // Clear all filters
  const clearAllFilters = () => {
    setSelectedLetter(null);
    setSelectedArtist(null);
    setFilterGenre('');
    setFilterYear('');
    setFilterActor('');
    setFilterRating('');
    setSortBy('addedAt');
    setSortOrder('desc');
    setCurrentPage(1);
  };
  
  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLetter, selectedArtist, filterGenre, filterYear, filterActor, filterRating, sortBy, sortOrder]);
  
  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);
  
  const hasActiveFilters = selectedLetter || selectedArtist || filterGenre || filterYear || filterActor || filterRating || sortBy !== 'year' || sortOrder !== 'desc';
  
  // Pagination calculations
  const totalItems = filteredAndSortedContent.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedContent = useMemo(() => 
    filteredAndSortedContent.slice(startIndex, endIndex),
    [filteredAndSortedContent, startIndex, endIndex]
  );
  
  // Find selected artist display name for the tag
  const selectedArtistDisplay = selectedArtist 
    ? artists.find(a => a.name === selectedArtist)?.displayName || selectedArtist
    : null;
  
  return (
    <div className="space-y-4">
      {/* Artist Section for Music Videos - Always on top */}
      {isMusicVideos && artists.length > 0 && !isLoading && (
        <div className="w-full">
          <div className="flex items-center gap-2 mb-2">
            <Users size={20} className="text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">Artists ({artists.length})</h3>
            {selectedArtistDisplay && (
              <button
                onClick={() => setSelectedArtist(null)}
                className="flex items-center gap-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full text-xs hover:bg-cyan-500/30 transition-colors"
              >
                {selectedArtistDisplay}
                <X size={12} />
              </button>
            )}
          </div>
          
          {/* Alphabet Navigation Row */}
          <div className="flex items-center gap-1 mb-3 overflow-x-auto pb-1 scrollbar-hide">
            {artistAlphabet.map((letter) => (
              <button
                key={letter}
                onClick={() => scrollToArtistLetter(letter)}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-sm font-medium rounded-lg bg-gray-800/50 text-gray-300 hover:bg-cyan-500/20 hover:text-cyan-400 transition-all"
              >
                {letter}
              </button>
            ))}
          </div>
          
          {/* Artists Row */}
          <div 
            ref={artistRowRef}
            className="flex gap-3 overflow-x-auto pb-3 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide"
          >
            {artists.map((artist) => (
              <button
                key={artist.name}
                data-artist={artist.displayName}
                onClick={() => setSelectedArtist(selectedArtist === artist.name ? null : artist.name)}
                className={`flex-shrink-0 flex flex-col items-center gap-2 p-2 rounded-xl transition-all ${
                  selectedArtist === artist.name 
                    ? 'bg-cyan-500/20 ring-2 ring-cyan-500' 
                    : 'bg-gray-800/50 hover:bg-gray-700/50'
                }`}
                title={`${artist.displayName} (${artist.count} videos)`}
              >
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden bg-gradient-to-br from-purple-500/30 to-cyan-500/30">
                  <img 
                    src={artistImages[artist.name] || artist.thumb} 
                    alt={artist.displayName}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <span className="text-xs text-gray-300 text-center max-w-[80px] truncate">{artist.displayName}</span>
                <span className="text-[10px] text-gray-500">{artist.count} videos</span>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Main Content with optional alphabet sidebar */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Main Content Area */}
        <div className="flex-1 order-2 md:order-1">
          {/* Header with title and count */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 md:gap-4 flex-wrap">
              <h2 className="text-xl md:text-2xl font-bold text-white">{library?.title}</h2>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1.5 px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs hover:bg-red-500/30 transition-colors"
                >
                  Clear filters
                  <X size={12} />
                </button>
              )}
            </div>
          <span className="text-gray-400 text-sm">
            {isLoading ? 'Loading...' : totalPages > 1 
              ? `Page ${currentPage} of ${totalPages} (${totalItems} items)` 
              : `${filteredAndSortedContent.length} of ${content.length} items`
            }
          </span>
        </div>
        
        {/* Filter Controls - Only for movie/video libraries */}
        {library?.type !== 'artist' && !isLoading && content.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-800">
            {/* Sort dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-black border border-white/40 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white hover:border-white transition-colors"
            >
              <option value="title">Sort: Name</option>
              <option value="year">Sort: Year</option>
              <option value="rating">Sort: Score</option>
              <option value="addedAt">Sort: Date Added</option>
            </select>
            
            {/* Sort order */}
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 bg-black border border-white/40 rounded-lg hover:border-white transition-colors"
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortOrder === 'asc' ? (
                <ChevronRight size={16} className="text-white rotate-[-90deg]" />
              ) : (
                <ChevronRight size={16} className="text-white rotate-90" />
              )}
            </button>
            
            {/* Genre filter */}
            {filterOptions.genres.length > 0 && (
              <select
                value={filterGenre}
                onChange={(e) => setFilterGenre(e.target.value)}
                className="bg-black border border-white/40 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white hover:border-white transition-colors"
              >
                <option value="">All Genres</option>
                {filterOptions.genres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
            )}
            
            {/* Year filter */}
            {filterOptions.years.length > 0 && (
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="bg-black border border-white/40 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white hover:border-white transition-colors"
              >
                <option value="">All Years</option>
                {filterOptions.years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            )}
            
            {/* Actor filter */}
            {filterOptions.actors.length > 0 && (
              <select
                value={filterActor}
                onChange={(e) => setFilterActor(e.target.value)}
                className="bg-black border border-white/40 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white hover:border-white transition-colors max-w-[150px]"
              >
                <option value="">All Actors</option>
                {filterOptions.actors.map(actor => (
                  <option key={actor} value={actor}>{actor}</option>
                ))}
              </select>
            )}
            {/* Content Rating Filter (PG-13, R, etc.) */}
            {filterOptions.ratings.length > 0 && (
              <select
                value={filterRating}
                onChange={(e) => setFilterRating(e.target.value)}
                className="bg-black border border-white/40 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white hover:border-white transition-colors max-w-[120px]"
              >
                <option value="">All Rated</option>
                {filterOptions.ratings.map(rating => (
                  <option key={rating} value={rating}>{rating}</option>
                ))}
              </select>
            )}
          </div>
        )}
        
        {isLoading || content.length === 0 ? (
          <div className="text-center py-12">
            <Loader2 size={32} className="animate-spin text-cyan-400 mx-auto mb-4" />
            <p className="text-gray-400">Loading all {library?.title?.toLowerCase()}...</p>
          </div>
        ) : library?.type === 'artist' ? (
          // Music grid
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredAndSortedContent.map((item, idx) => (
              <MusicCard key={`lib-${item.ratingKey}-${idx}`} item={item} onClick={handleItemClick} />
            ))}
          </div>
        ) : isMusicVideos ? (
          // Music Videos grid - horizontal cards
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="music-videos-grid">
            {filteredAndSortedContent.map((item, idx) => (
              <div 
                key={`lib-${item.ratingKey}-${idx}`}
                className="group relative bg-gray-900 rounded-xl overflow-hidden hover:ring-2 hover:ring-cyan-500/50 transition-all cursor-pointer"
                onClick={() => handleItemClick(item)}
              >
                {/* Thumbnail - 16:9 horizontal */}
                <div className="relative aspect-video">
                  {item.thumb ? (
                    <img 
                      src={item.thumb}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <Film size={32} className="text-gray-600" />
                    </div>
                  )}
                  {/* Play button overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                      <Play size={24} className="text-black ml-1" fill="black" />
                    </div>
                  </div>
                  {/* Duration badge */}
                  {item.duration && (
                    <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 rounded text-xs text-white">
                      {Math.floor(item.duration / 60000)}:{String(Math.floor((item.duration % 60000) / 1000)).padStart(2, '0')}
                    </div>
                  )}
                </div>
                {/* Title */}
                <div className="p-3">
                  <h3 className="text-sm font-medium text-white line-clamp-2">{item.title}</h3>
                  {item.year && (
                    <p className="text-xs text-gray-400 mt-1">{item.year}</p>
                  )}
                </div>
                {/* 3-dot menu */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuAction('add-to', item);
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity text-white hover:bg-black/80"
                >
                  <MoreVertical size={16} />
                </button>
              </div>
            ))}
            {filteredAndSortedContent.length === 0 && (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-400">No items match your filters</p>
              </div>
            )}
          </div>
        ) : (
          // Movie/Video grid - 3 cols on mobile, scales up on larger screens
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 md:gap-4" data-testid="movie-grid">
            {paginatedContent.map((item, idx) => (
              <MediaCard 
                key={`lib-${item.ratingKey}-${idx}`} 
                item={item} 
                onClick={handleItemClick}
                onMenuAction={handleMenuAction}
                plexUrl={plexUrl}
                isInWatchlist={isInWatchlist}
                onToggleWatchlist={toggleWatchlistItem}
              />
            ))}
            {filteredAndSortedContent.length === 0 && (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-400">No items match your filters</p>
              </div>
            )}
          </div>
        )}
        
        {/* Pagination Controls */}
        {!isLoading && totalItems > itemsPerPage && library?.type !== 'artist' && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-700">
            <div className="text-sm text-gray-400">
              Showing {startIndex + 1}-{endIndex} of {totalItems} items
            </div>
            <div className="flex items-center gap-2">
              {/* Previous button */}
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === 1
                    ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                    : 'bg-gray-800 text-white hover:bg-gray-700'
                }`}
              >
                <ChevronLeft size={16} className="inline" /> Prev
              </button>
              
              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 7;
                  
                  if (totalPages <= maxVisiblePages) {
                    // Show all pages
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Show first, last, current and neighbors
                    if (currentPage <= 4) {
                      for (let i = 1; i <= 5; i++) pages.push(i);
                      pages.push('...');
                      pages.push(totalPages);
                    } else if (currentPage >= totalPages - 3) {
                      pages.push(1);
                      pages.push('...');
                      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
                    } else {
                      pages.push(1);
                      pages.push('...');
                      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                      pages.push('...');
                      pages.push(totalPages);
                    }
                  }
                  
                  return pages.map((page, idx) => (
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-cyan-500 text-black'
                            : 'bg-gray-800 text-white hover:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ));
                })()}
              </div>
              
              {/* Next button */}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === totalPages
                    ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                    : 'bg-gray-800 text-white hover:bg-gray-700'
                }`}
              >
                Next <ChevronRight size={16} className="inline" />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Alphabet Navigation - Horizontal on mobile, Vertical on desktop */}
      {!isLoading && content.length > 0 && library?.type !== 'artist' && (
        <div className="order-1 md:order-2 md:flex-shrink-0 md:sticky md:top-6 md:self-start" data-testid="alphabet-nav">
          {/* Mobile: Horizontal scrollable */}
          <div className="flex md:hidden items-center gap-1 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            <button
              onClick={() => setSelectedLetter(null)}
              className={`flex-shrink-0 px-2 py-1 text-xs font-bold rounded transition-all ${
                !selectedLetter ? 'bg-white text-black' : 'text-gray-300 bg-gray-800'
              }`}
            >
              ALL
            </button>
            {alphabet.map((letter) => {
              const hasContent = availableLetters.has(letter);
              const isSelected = selectedLetter === letter;
              return (
                <button
                  key={letter}
                  onClick={() => setSelectedLetter(isSelected ? null : letter)}
                  disabled={!hasContent}
                  className={`flex-shrink-0 w-7 h-7 flex items-center justify-center text-xs font-medium rounded transition-all ${
                    isSelected ? 'bg-white text-black' : hasContent ? 'text-gray-300 bg-gray-800' : 'text-gray-600 bg-gray-900'
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
          
          {/* Desktop: Vertical sidebar */}
          <div className="hidden md:flex flex-col items-center py-2 px-1 bg-black border border-white/20 rounded-lg border border-gray-800">
            <button
              onClick={() => setSelectedLetter(null)}
              className={`w-8 h-7 flex items-center justify-center text-xs font-bold rounded transition-all mb-1 ${
                !selectedLetter ? 'bg-white text-black' : 'text-gray-300 hover:text-white hover:bg-gray-800'
              }`}
              title="Show all"
              data-testid="alphabet-all"
            >
              ALL
            </button>
            <div className="w-6 h-px bg-gray-700 mb-1" />
            {alphabet.map((letter) => {
              const hasContent = availableLetters.has(letter);
              const isSelected = selectedLetter === letter;
              
              return (
                <button
                  key={letter}
                  onClick={() => setSelectedLetter(isSelected ? null : letter)}
                  disabled={!hasContent}
                  data-testid={`alphabet-${letter === '#' ? 'num' : letter}`}
                  className={`w-8 h-7 flex items-center justify-center text-sm font-medium rounded transition-all ${
                    isSelected ? 'bg-white text-black' : hasContent ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-600 cursor-not-allowed'
                  }`}
                  title={hasContent ? `Show ${letter}` : `No items for ${letter}`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// Detail Modal Component with Video Player
const MediaDetailModal = ({ item, onClose, plexUrl, relatedMovies = [], allMovies = [], onNavigate, autoPlay = false }) => {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [streamInfo, setStreamInfo] = useState(null);
  const [streamLoading, setStreamLoading] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [mouseIdle, setMouseIdle] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(autoPlay);
  
  // Cast and Trailer state
  const [fullCast, setFullCast] = useState([]);
  const [showAllCast, setShowAllCast] = useState(false);
  const [trailer, setTrailer] = useState(null);
  const [showTrailer, setShowTrailer] = useState(false);
  const [trailerLoading, setTrailerLoading] = useState(false);
  
  // Actor details state
  const [selectedActor, setSelectedActor] = useState(null);
  const [actorDetails, setActorDetails] = useState(null);
  const [actorLoading, setActorLoading] = useState(false);
  
  // Summary expand state
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  
  // Crew dropdown state
  const [crewExpanded, setCrewExpanded] = useState(false);
  
  // Extended crew state (from TMDB)
  const [extendedCrew, setExtendedCrew] = useState({ editors: [], soundDesigners: [], composers: [] });
  
  const videoRef = React.useRef(null);
  const mouseTimeoutRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const prevItemRef = React.useRef(item?.ratingKey);
  
  // Video playback state for custom controls
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoVolume, setVideoVolume] = useState(1);
  const [videoMuted, setVideoMuted] = useState(false);
  
  // Video seeking/dragging state for touch support
  const [isVideoSeeking, setIsVideoSeeking] = useState(false);
  const [seekPreviewTime, setSeekPreviewTime] = useState(0);
  const videoProgressBarRef = useRef(null);
  
  // Continue watching state
  const [showContinueModal, setShowContinueModal] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  
  // Center play overlay state - shown when tapping video
  const [showCenterPlayOverlay, setShowCenterPlayOverlay] = useState(false);
  const centerOverlayTimeoutRef = useRef(null);
  
  // Update shouldAutoPlay when autoPlay prop changes or item changes
  React.useEffect(() => {
    if (autoPlay && item?.ratingKey !== prevItemRef.current) {
      // Reset playing state for the new item
      setIsPlaying(false);
      setStreamInfo(null);
      setShouldAutoPlay(true);
      prevItemRef.current = item?.ratingKey;
    }
  }, [autoPlay, item?.ratingKey]);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Captions state
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [captionsStatus, setCaptionsStatus] = useState('unknown'); // unknown, not_generated, generating, ready
  const [captionsProgress, setCaptionsProgress] = useState(0);
  const [captionsUrl, setCaptionsUrl] = useState(null);
  const [parsedCaptions, setParsedCaptions] = useState([]);
  const [currentCaption, setCurrentCaption] = useState('');
  const [debugVideoTime, setDebugVideoTime] = useState(0);
  const [captionOffset, setCaptionOffset] = useState(0); // Offset in seconds (positive = delay captions, negative = advance captions)

  // Chapters state
  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [chaptersSource, setChaptersSource] = useState(null); // 'plex', 'ai', 'fallback'
  const [showChaptersPanel, setShowChaptersPanel] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(null);

  // Fetch chapters when item changes
  React.useEffect(() => {
    if (item?.ratingKey) {
      fetchChapters(item.ratingKey);
    }
  }, [item?.ratingKey]);

  // Fetch full cast when item changes
  React.useEffect(() => {
    const fetchCast = async () => {
      if (!item?.ratingKey) return;
      
      try {
        const castRes = await apiCall(`/plex/item/${item.ratingKey}/cast`);
        if (castRes.status === 'success' && castRes.cast) {
          setFullCast(castRes.cast);
        }
      } catch (err) {
        console.error('Failed to fetch cast:', err);
      }
    };
    
    fetchCast();
  }, [item?.ratingKey]);
  
  // Fetch extended crew (editor, sound designer) from TMDB
  React.useEffect(() => {
    const fetchExtendedCrew = async () => {
      if (!item?.ratingKey) return;
      
      try {
        const crewRes = await apiCall(`/plex/item/${item.ratingKey}/crew`);
        if (crewRes.status === 'success') {
          setExtendedCrew({
            editors: crewRes.editors || [],
            soundDesigners: crewRes.soundDesigners || [],
            composers: crewRes.composers || []
          });
        }
      } catch (err) {
        console.error('Failed to fetch extended crew:', err);
      }
    };
    
    // Small delay to prioritize other requests
    const timer = setTimeout(fetchExtendedCrew, 300);
    return () => clearTimeout(timer);
  }, [item?.ratingKey]);
  
  // Fetch trailer separately to avoid blocking
  React.useEffect(() => {
    const fetchTrailer = async () => {
      if (!item?.ratingKey) return;
      
      setTrailerLoading(true);
      try {
        const trailerRes = await fetch(`${window.location.origin}/api/plex/item/${item.ratingKey}/trailer`);
        if (trailerRes.ok) {
          const data = await trailerRes.json();
          if (data.status === 'success' && data.trailer) {
            setTrailer(data.trailer);
          }
        }
      } catch (err) {
        console.error('Failed to fetch trailer:', err);
      } finally {
        setTrailerLoading(false);
      }
    };
    
    // Small delay to let other requests go first
    const timer = setTimeout(fetchTrailer, 500);
    return () => clearTimeout(timer);
  }, [item?.ratingKey]);

  // Fetch actor details when an actor is selected
  const handleActorClick = async (actor) => {
    if (!actor?.name) return;
    
    setSelectedActor(actor);
    setActorLoading(true);
    setActorDetails(null);
    
    try {
      const response = await fetch(`${window.location.origin}/api/plex/actor/${encodeURIComponent(actor.name)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setActorDetails(data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch actor details:', err);
    } finally {
      setActorLoading(false);
    }
  };

  // Update current chapter based on video progress
  React.useEffect(() => {
    if (!chapters.length || !videoProgress) return;
    
    const current = chapters.find((chapter, index) => {
      const nextChapter = chapters[index + 1];
      const endTime = nextChapter ? nextChapter.startTime : Infinity;
      return videoProgress >= chapter.startTime && videoProgress < endTime;
    });
    
    if (current && current.id !== currentChapter?.id) {
      setCurrentChapter(current);
    }
  }, [videoProgress, chapters]);

  // Fetch chapters from Plex or generate with AI
  const fetchChapters = async (ratingKey) => {
    setChaptersLoading(true);
    try {
      // First try to get Plex chapters
      const plexRes = await apiCall(`/plex/item/${ratingKey}/chapters`);
      
      if (plexRes.hasChapters && plexRes.chapters.length > 0) {
        setChapters(plexRes.chapters);
        setChaptersSource('plex');
        console.log('Loaded Plex chapters:', plexRes.chapters.length);
      } else {
        // No Plex chapters - try AI generation
        console.log('No Plex chapters found, generating with AI...');
        const aiRes = await apiCall(`/plex/item/${ratingKey}/generate-chapters`, {
          method: 'POST'
        });
        
        if (aiRes.hasChapters && aiRes.chapters.length > 0) {
          setChapters(aiRes.chapters);
          setChaptersSource(aiRes.source);
          console.log('Generated AI chapters:', aiRes.chapters.length);
        } else {
          setChapters([]);
          setChaptersSource(null);
        }
      }
    } catch (err) {
      console.error('Failed to fetch chapters:', err);
      setChapters([]);
      setChaptersSource(null);
    } finally {
      setChaptersLoading(false);
    }
  };

  // Jump to a specific chapter
  const jumpToChapter = (chapter) => {
    if (videoRef.current && chapter.startTime !== undefined) {
      videoRef.current.currentTime = chapter.startTime;
      setCurrentChapter(chapter);
      // Auto-close panel after selecting (optional, for cleaner UX)
      // setShowChaptersPanel(false);
    }
  };

  // Update video progress
  React.useEffect(() => {
    if (!videoRef.current || !isPlaying) return;
    
    const video = videoRef.current;
    
    const handleTimeUpdate = () => setVideoProgress(video.currentTime);
    const handleDurationChange = () => setVideoDuration(video.duration);
    const handlePlay = () => setVideoPlaying(true);
    const handlePause = () => setVideoPlaying(false);
    const handleVolumeChange = () => {
      setVideoVolume(video.volume);
      setVideoMuted(video.muted);
    };
    
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('volumechange', handleVolumeChange);
    
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [isPlaying, streamInfo]);

  // Auto-play when navigating from ended video
  // Note: Effect moved to after startPlaying is defined

  // Format time for display
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle video play/pause
  const toggleVideoPlay = () => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  // Handle video screen tap - show overlay instead of toggling play
  const handleVideoTap = (e) => {
    e.stopPropagation();
    // Show center play overlay
    setShowCenterPlayOverlay(true);
    setShowControls(true);
    setMouseIdle(false);
    
    // Clear existing timeout
    if (centerOverlayTimeoutRef.current) {
      clearTimeout(centerOverlayTimeoutRef.current);
    }
    
    // Hide overlay after 3 seconds if video is playing
    centerOverlayTimeoutRef.current = setTimeout(() => {
      if (videoPlaying) {
        setShowCenterPlayOverlay(false);
        setMouseIdle(true);
      }
    }, 3000);
  };

  // Handle center play button click
  const handleCenterPlayClick = (e) => {
    e.stopPropagation();
    toggleVideoPlay();
    
    // If starting to play, auto-hide overlay after delay
    if (!videoPlaying) {
      if (centerOverlayTimeoutRef.current) {
        clearTimeout(centerOverlayTimeoutRef.current);
      }
      centerOverlayTimeoutRef.current = setTimeout(() => {
        setShowCenterPlayOverlay(false);
        setMouseIdle(true);
      }, 2000);
    }
  };

  // Save video progress to localStorage
  const saveVideoProgress = useCallback(() => {
    if (videoRef.current && item?.ratingKey && videoProgress > 30) {
      const progressData = {
        ratingKey: item.ratingKey,
        title: item.title,
        progress: videoProgress,
        duration: videoDuration,
        timestamp: Date.now(),
        thumbnail: item.thumb
      };
      
      // Get existing saved progress
      const savedList = JSON.parse(localStorage.getItem('movieProgress') || '{}');
      savedList[item.ratingKey] = progressData;
      localStorage.setItem('movieProgress', JSON.stringify(savedList));
    }
  }, [item?.ratingKey, item?.title, item?.thumb, videoProgress, videoDuration]);

  // Load saved progress for current movie
  const loadSavedProgress = useCallback(() => {
    if (item?.ratingKey) {
      const savedList = JSON.parse(localStorage.getItem('movieProgress') || '{}');
      const saved = savedList[item.ratingKey];
      
      // Only show continue option if saved within 7 days and not near end (>95%)
      if (saved && Date.now() - saved.timestamp < 7 * 24 * 60 * 60 * 1000) {
        const percentComplete = (saved.progress / saved.duration) * 100;
        if (percentComplete > 5 && percentComplete < 95) {
          setSavedProgress(saved);
          setShowContinueModal(true);
          return true;
        }
      }
    }
    return false;
  }, [item?.ratingKey]);

  // Continue from saved progress
  const handleContinueWatching = () => {
    if (savedProgress && videoRef.current) {
      videoRef.current.currentTime = savedProgress.progress;
      setShowContinueModal(false);
      videoRef.current.play();
    }
  };

  // Start from beginning
  const handleStartFromBeginning = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setShowContinueModal(false);
      
      // Clear saved progress for this movie
      const savedList = JSON.parse(localStorage.getItem('movieProgress') || '{}');
      if (item?.ratingKey) {
        delete savedList[item.ratingKey];
        localStorage.setItem('movieProgress', JSON.stringify(savedList));
      }
      
      videoRef.current.play();
    }
  };

  // Save progress when leaving or periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (videoPlaying && videoProgress > 30) {
        saveVideoProgress();
      }
    }, 30000); // Save every 30 seconds
    
    return () => clearInterval(interval);
  }, [videoPlaying, videoProgress, saveVideoProgress]);

  // Save progress when component unmounts or video pauses
  useEffect(() => {
    return () => {
      saveVideoProgress();
    };
  }, [saveVideoProgress]);

  // Check for saved progress when video loads
  useEffect(() => {
    if (streamInfo && videoRef.current) {
      loadSavedProgress();
    }
  }, [streamInfo, loadSavedProgress]);

  // Seek video
  const seekVideo = (e) => {
    if (!videoRef.current || !videoProgressBarRef.current) return;
    const rect = videoProgressBarRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    videoRef.current.currentTime = percent * videoDuration;
  };

  // Handle touch/mouse start for seeking
  const handleSeekStart = (e) => {
    e.preventDefault();
    setIsVideoSeeking(true);
    const rect = videoProgressBarRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setSeekPreviewTime(percent * videoDuration);
  };

  // Handle touch/mouse move for seeking
  const handleSeekMove = useCallback((e) => {
    if (!isVideoSeeking || !videoProgressBarRef.current) return;
    const rect = videoProgressBarRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setSeekPreviewTime(percent * videoDuration);
  }, [isVideoSeeking, videoDuration]);

  // Handle touch/mouse end for seeking
  const handleSeekEnd = useCallback(() => {
    if (!isVideoSeeking) return;
    if (videoRef.current && seekPreviewTime !== undefined) {
      videoRef.current.currentTime = seekPreviewTime;
    }
    setIsVideoSeeking(false);
  }, [isVideoSeeking, seekPreviewTime]);

  // Add/remove global event listeners for seeking
  React.useEffect(() => {
    if (isVideoSeeking) {
      const onMove = (e) => handleSeekMove(e);
      const onEnd = () => handleSeekEnd();
      
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onEnd);
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('touchend', onEnd);
      
      return () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onEnd);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onEnd);
      };
    }
  }, [isVideoSeeking, handleSeekMove, handleSeekEnd]);

  // Change volume
  const changeVolume = (e) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newVolume = Math.max(0, Math.min(1, percent));
    videoRef.current.volume = newVolume;
    videoRef.current.muted = false;
  };

  // Skip forward 15 seconds
  const skipForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(videoRef.current.currentTime + 15, videoDuration);
    }
  };

  // Skip backward 15 seconds
  const skipBackward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(videoRef.current.currentTime - 15, 0);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // Listen for fullscreen changes (handles Escape key and other exits)
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Check caption status when item changes
  React.useEffect(() => {
    if (item?.ratingKey) {
      checkCaptionStatus(item.ratingKey);
    }
  }, [item?.ratingKey]);

  // Parse VTT timestamp to seconds
  const parseVttTime = (timeStr) => {
    const parts = timeStr.split(':');
    if (parts.length === 3) {
      const [hours, minutes, seconds] = parts;
      return parseFloat(hours) * 3600 + parseFloat(minutes) * 60 + parseFloat(seconds);
    } else if (parts.length === 2) {
      const [minutes, seconds] = parts;
      return parseFloat(minutes) * 60 + parseFloat(seconds);
    }
    return 0;
  };

  // Fetch and parse captions when URL is available
  React.useEffect(() => {
    if (captionsStatus === 'ready' && captionsUrl) {
      console.log('🎬 Fetching captions from:', captionsUrl);
      fetch(captionsUrl)
        .then(res => res.text())
        .then(vttText => {
          console.log('🎬 VTT content received, parsing...');
          const lines = vttText.split('\n');
          const captions = [];
          let i = 0;
          
          // Skip WEBVTT header
          while (i < lines.length && !lines[i].includes('-->')) {
            i++;
          }
          
          while (i < lines.length) {
            const line = lines[i].trim();
            if (line.includes('-->')) {
              const [startTime, endTime] = line.split('-->').map(t => t.trim());
              const start = parseVttTime(startTime);
              const end = parseVttTime(endTime);
              
              // Collect text lines until empty line
              let text = '';
              i++;
              while (i < lines.length && lines[i].trim() !== '') {
                text += (text ? ' ' : '') + lines[i].trim();
                i++;
              }
              
              if (text) {
                captions.push({ start, end, text });
              }
            }
            i++;
          }
          
          console.log('🎬 Parsed', captions.length, 'caption cues');
          setParsedCaptions(captions);
        })
        .catch(err => {
          console.error('🎬 Failed to fetch captions:', err);
        });
    }
  }, [captionsStatus, captionsUrl]);

  // Update current caption based on video time
  React.useEffect(() => {
    if (!captionsEnabled || parsedCaptions.length === 0) {
      return;
    }
    
    console.log('🎬 Starting caption sync. Cues:', parsedCaptions.length, 'Offset:', captionOffset);
    
    const updateCaption = () => {
      const video = videoRef.current;
      if (!video) return;
      
      // Apply offset to current time (subtract offset to shift captions)
      const currentTime = video.currentTime - captionOffset;
      setDebugVideoTime(video.currentTime);
      
      // Find active caption for adjusted time
      const activeCue = parsedCaptions.find(
        cue => currentTime >= cue.start && currentTime <= cue.end
      );
      
      // Always update the caption state
      setCurrentCaption(activeCue ? activeCue.text : '');
    };
    
    // Update frequently for smooth captions
    const interval = setInterval(updateCaption, 100);
    updateCaption(); // Initial update
    
    // Also listen for seeking events
    const video = videoRef.current;
    if (video) {
      video.addEventListener('seeked', updateCaption);
      video.addEventListener('seeking', updateCaption);
    }
    
    return () => {
      clearInterval(interval);
      if (video) {
        video.removeEventListener('seeked', updateCaption);
        video.removeEventListener('seeking', updateCaption);
      }
    };
  }, [captionsEnabled, parsedCaptions, captionOffset]);

  const checkCaptionStatus = async (ratingKey, showToast = false) => {
    try {
      console.log('🎬 Checking caption status for:', ratingKey);
      // Use window.location.origin for runtime URL resolution (avoids build-time env var caching issues)
      const baseUrl = window.location.origin;
      const response = await fetch(`${baseUrl}/api/captions/status/${ratingKey}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('🎬 Caption status response:', data);
      setCaptionsStatus(data.status);
      
      if (data.status === 'ready') {
        const url = `${baseUrl}/api/captions/get/${ratingKey}`;
        console.log('🎬 Setting captions URL:', url);
        setCaptionsUrl(url);
        if (showToast) {
          // Auto-enable captions when ready
          setCaptionsEnabled(true);
        }
      } else if (data.status === 'generating') {
        setCaptionsProgress(data.progress || 0);
        // Poll silently
        setTimeout(() => checkCaptionStatus(ratingKey, true), 2000);
      } else if (data.status === 'failed') {
        setCaptionsStatus('not_generated');
      } else {
        console.log('🎬 Caption status is:', data.status);
      }
    } catch (err) {
      console.error('🎬 Failed to check caption status:', err);
      setCaptionsStatus('unknown');
    }
  };

  const generateCaptions = async () => {
    console.log('🎬 generateCaptions called. Item:', item?.ratingKey);
    
    if (!item?.ratingKey) {
      console.error('🎬 No ratingKey available!');
      toast.error('Cannot generate captions - no movie selected');
      return;
    }
    
    setCaptionsStatus('generating');
    setCaptionsProgress(0);
    
    // Use window.location.origin for runtime URL resolution (avoids build-time env var caching issues)
    const baseUrl = window.location.origin;
    const apiUrl = `${baseUrl}/api/captions/generate/${item.ratingKey}`;
    console.log('🎬 Calling API:', apiUrl);
    
    try {
      const response = await fetch(apiUrl, {
        method: 'POST'
      });
      
      console.log('🎬 API Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('🎬 API Response data:', data);
      
      if (data.status === 'ready') {
        setCaptionsStatus('ready');
        setCaptionsUrl(`${baseUrl}/api/captions/get/${item.ratingKey}`);
        setCaptionsEnabled(true);
      } else if (data.status === 'failed') {
        throw new Error(data.error || 'Caption generation failed');
      } else {
        // Poll for updates silently
        setTimeout(() => checkCaptionStatus(item.ratingKey, true), 2000);
      }
    } catch (err) {
      console.error('🎬 Failed to generate captions:', err);
      setCaptionsStatus('not_generated');
      toast.error(`Captions not available for this movie`, { id: 'caption-gen', duration: 3000 });
    }
  };

  const toggleCaptions = () => {
    console.log('🎬 CC Button clicked! Current status:', captionsStatus, 'Item:', item?.ratingKey);
    
    if (captionsStatus === 'ready') {
      const newEnabled = !captionsEnabled;
      setCaptionsEnabled(newEnabled);
      
      // Directly manipulate text tracks
      if (videoRef.current) {
        const video = videoRef.current;
        console.log('🎬 Video textTracks count:', video.textTracks.length);
        
        for (let i = 0; i < video.textTracks.length; i++) {
          video.textTracks[i].mode = newEnabled ? 'showing' : 'hidden';
          console.log('🎬 Track', i, 'mode set to:', video.textTracks[i].mode);
        }
      }
      toast.success(newEnabled ? 'Captions enabled' : 'Captions disabled', { duration: 1500 });
    } else if (captionsStatus === 'not_generated' || captionsStatus === 'unknown') {
      console.log('🎬 Starting caption generation...');
      generateCaptions();
    } else if (captionsStatus === 'generating') {
      console.log('🎬 Captions are being generated, please wait...');
      toast.info('Captions are being generated, please wait...', { duration: 2000 });
    }
  };

  // Get current index in the movies list
  const currentIndex = React.useMemo(() => {
    const movies = allMovies.length > 0 ? allMovies : relatedMovies;
    const idx = movies.findIndex(m => m.ratingKey === item?.ratingKey);
    return idx >= 0 ? idx : 0;
  }, [item, allMovies, relatedMovies]);

  const moviesList = allMovies.length > 0 ? allMovies : relatedMovies;
  const hasNavigation = moviesList.length > 1;

  // Compute smart related movies based on cast and genres
  const smartRelatedMovies = React.useMemo(() => {
    if (!item || allMovies.length === 0) return relatedMovies;
    
    const currentActors = new Set((item.actors || []).map(a => a.toLowerCase()));
    const currentGenres = new Set((item.genres || []).map(g => g.toLowerCase()));
    const currentRatingKey = item.ratingKey;
    
    // Score each movie based on shared cast and genres
    const scoredMovies = allMovies
      .filter(m => m.ratingKey !== currentRatingKey)
      .map(movie => {
        let score = 0;
        let matchReasons = [];
        
        // Check for shared actors (higher weight)
        const movieActors = (movie.actors || []).map(a => a.toLowerCase());
        const sharedActors = movieActors.filter(a => currentActors.has(a));
        if (sharedActors.length > 0) {
          score += sharedActors.length * 10; // 10 points per shared actor
          matchReasons.push(`cast`);
        }
        
        // Check for shared genres
        const movieGenres = (movie.genres || []).map(g => g.toLowerCase());
        const sharedGenres = movieGenres.filter(g => currentGenres.has(g));
        if (sharedGenres.length > 0) {
          score += sharedGenres.length * 3; // 3 points per shared genre
          matchReasons.push(`genre`);
        }
        
        return { ...movie, score, matchReasons, sharedActors };
      })
      .filter(m => m.score > 0) // Only include movies with some match
      .sort((a, b) => b.score - a.score); // Sort by score descending
    
    // If we have enough scored movies, use them; otherwise fall back to relatedMovies
    if (scoredMovies.length >= 4) {
      return scoredMovies.slice(0, 12);
    }
    
    // Mix scored movies with genre fallback
    return [...scoredMovies, ...relatedMovies.filter(m => 
      !scoredMovies.find(s => s.ratingKey === m.ratingKey)
    )].slice(0, 12);
  }, [item, allMovies, relatedMovies]);

  // Navigate to next movie and auto-play
  const goToNextMovie = React.useCallback(() => {
    console.log('goToNextMovie called, moviesList:', moviesList.length, 'currentIndex:', currentIndex);
    if (moviesList.length === 0 || !onNavigate) {
      console.log('Cannot navigate: no movies or no onNavigate');
      return;
    }
    const nextIndex = (currentIndex + 1) % moviesList.length;
    const nextMovie = moviesList[nextIndex];
    console.log('Next movie:', nextMovie?.title, 'at index:', nextIndex);
    if (nextMovie) {
      // Stop current video if playing
      if (isPlaying) {
        stopPlaying();
      }
      // Navigate to next movie - pass autoPlay flag
      console.log('Navigating to next movie with autoPlay: true');
      onNavigate(nextMovie, { autoPlay: true });
    }
  }, [moviesList, currentIndex, onNavigate, isPlaying]);

  // Navigate to previous movie
  const goToPreviousMovie = React.useCallback(() => {
    if (moviesList.length === 0 || !onNavigate) return;
    const prevIndex = currentIndex <= 0 ? moviesList.length - 1 : currentIndex - 1;
    const prevMovie = moviesList[prevIndex];
    if (prevMovie) {
      // Stop current video if playing
      if (isPlaying) {
        stopPlaying();
      }
      onNavigate(prevMovie, { autoPlay: true });
    }
  }, [moviesList, currentIndex, onNavigate, isPlaying]);

  // Handle mouse movement to show/hide controls
  const handleMouseMove = React.useCallback(() => {
    setMouseIdle(false);
    setShowControls(true);
    
    if (mouseTimeoutRef.current) {
      clearTimeout(mouseTimeoutRef.current);
    }
    
    mouseTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setMouseIdle(true);
      }
    }, 3000); // Hide after 3 seconds of no movement
  }, [isPlaying]);

  // Keyboard navigation - works on both info page and video player
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          goToNextMovie();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goToPreviousMovie();
          break;
        case 'Escape':
          e.preventDefault();
          if (isPlaying) {
            stopPlaying();
          } else {
            onClose();
          }
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, goToNextMovie, goToPreviousMovie, onClose]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (mouseTimeoutRef.current) {
        clearTimeout(mouseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!item?.ratingKey) return;
      setLoading(true);
      try {
        const res = await apiCall(`/plex/item/${item.ratingKey}`);
        setDetails(res.item || item);
      } catch (err) {
        console.error('Failed to fetch details:', err);
        setDetails(item);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [item]);

  const displayItem = details || item;
  
  // Format duration from milliseconds to hours/minutes
  const formatDuration = (ms) => {
    if (!ms) return null;
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Get stream URL and start playing
  const startPlaying = async () => {
    if (!item?.ratingKey) return;
    setStreamLoading(true);
    try {
      const res = await apiCall(`/plex/stream/${item.ratingKey}`);
      setStreamInfo(res);
      setIsPlaying(true);
      
      // Explicitly play video after user interaction (required for mobile)
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            console.error('Video play failed:', err);
          });
        }
      }, 100);
    } catch (err) {
      console.error('Failed to get stream:', err);
      // Fallback to opening in Plex
      openInPlex();
    } finally {
      setStreamLoading(false);
    }
  };

  // Open in Plex Web
  const openInPlex = () => {
    const plexWebUrl = `${plexUrl}/web/index.html#!/server/~~/details?key=%2Flibrary%2Fmetadata%2F${item.ratingKey}`;
    window.open(plexWebUrl, '_blank');
  };

  // Watch in Plex player
  const watchInPlex = () => {
    const playUrl = `${plexUrl}/web/index.html#!/server/~~/play?key=%2Flibrary%2Fmetadata%2F${item.ratingKey}`;
    window.open(playUrl, '_blank');
  };

  // Close player
  const stopPlaying = () => {
    setIsPlaying(false);
    setStreamInfo(null);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  // Toggle watchlist
  const toggleWatchlist = () => {
    setInWatchlist(!inWatchlist);
  };

  // Auto-play when navigating from ended video (placed after startPlaying is defined)
  React.useEffect(() => {
    if (shouldAutoPlay && item?.ratingKey && !isPlaying && !streamLoading) {
      console.log('Auto-playing next video:', item?.title);
      const timer = setTimeout(() => {
        startPlaying();
        setShouldAutoPlay(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [shouldAutoPlay, item?.ratingKey, isPlaying, streamLoading, startPlaying]);

  if (!item) return null;

  // Video Player View (Full screen when playing)
  if (isPlaying && streamInfo) {
    return (
      <Portal>
        <div 
          className="fixed inset-0 bg-black flex items-center justify-center"
          style={{ zIndex: 99999 }}
          onClick={stopPlaying}
          onMouseMove={handleMouseMove}
        >
        <div 
          ref={containerRef}
          className="relative w-full h-full flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Video Header - Auto-hides */}
          <div 
            className={`absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/90 to-transparent p-6 flex items-center justify-between transition-opacity duration-300 ${
              showControls && !mouseIdle ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <div className="flex items-center gap-4">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  stopPlaying();
                }}
                className="p-3 bg-gray-800/90 hover:bg-gray-700 rounded-full transition-colors border border-gray-600"
              >
                <X size={24} className="text-white" />
              </button>
              <div>
                <h2 className="text-white text-2xl font-bold">{displayItem.title}</h2>
                {displayItem.year && (
                  <p className="text-gray-400 text-sm">{displayItem.year}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-400">
              {streamInfo.videoResolution && (
                <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded">
                  {streamInfo.videoResolution}
                </span>
              )}
              {streamInfo.videoCodec && (
                <span className="px-2 py-1 bg-gray-700 rounded">
                  {streamInfo.videoCodec.toUpperCase()}
                </span>
              )}
            </div>
          </div>

          {/* Navigation Arrows - Auto-hide with mouse */}
          {moviesList.length > 1 && (
            <>
              {/* Previous Movie Arrow */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPreviousMovie();
                }}
                className={`absolute left-4 top-1/2 -translate-y-1/2 z-20 p-4 bg-black/70 hover:bg-cyan-600 rounded-full transition-all duration-300 group ${
                  showControls && !mouseIdle ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'
                }`}
                data-testid="video-prev-btn"
              >
                <ChevronLeft size={36} className="text-white group-hover:scale-110 transition-transform" />
              </button>
              
              {/* Next Movie Arrow */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNextMovie();
                }}
                className={`absolute right-4 top-1/2 -translate-y-1/2 z-20 p-4 bg-black/70 hover:bg-cyan-600 rounded-full transition-all duration-300 group ${
                  showControls && !mouseIdle ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'
                }`}
                data-testid="video-next-btn"
              >
                <ChevronRight size={36} className="text-white group-hover:scale-110 transition-transform" />
              </button>
            </>
          )}

          {/* Movie Counter - Auto-hide */}
          {moviesList.length > 1 && (
            <div 
              className={`absolute top-20 left-1/2 -translate-x-1/2 z-20 px-4 py-2 bg-black/70 rounded-full text-white text-sm transition-all duration-300 ${
                showControls && !mouseIdle ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {currentIndex + 1} / {moviesList.length}
            </div>
          )}

          {/* Video Player - No native controls */}
          <video
            ref={videoRef}
            className="w-full h-full bg-black object-contain cursor-pointer"
            playsInline
            src={streamInfo.proxyDirect}
            onClick={handleVideoTap}
            onLoadedData={() => {
              // Check for saved progress first, don't auto-play if showing continue modal
              const hasSaved = loadSavedProgress();
              if (!hasSaved && videoRef.current) {
                videoRef.current.play().catch(err => {
                  console.warn('Auto-play blocked, user must click play:', err);
                });
              }
            }}
            onError={(e) => {
              console.error('Video error, trying transcode URL');
              // Try transcode URL if direct fails
              if (videoRef.current && streamInfo.transcodeUrl) {
                videoRef.current.src = streamInfo.transcodeUrl;
              }
            }}
            onEnded={() => {
              console.log('Video ended! Calling goToNextMovie...');
              // Clear saved progress since movie is complete
              const savedList = JSON.parse(localStorage.getItem('movieProgress') || '{}');
              if (item?.ratingKey) {
                delete savedList[item.ratingKey];
                localStorage.setItem('movieProgress', JSON.stringify(savedList));
              }
              // Auto-play next movie when current one ends
              if (moviesList.length > 1) {
                goToNextMovie();
              }
            }}
            onPause={() => saveVideoProgress()}
          >
            Your browser does not support video playback.
          </video>

          {/* Center Play/Pause Overlay - Shows when tapping video */}
          <div 
            className={`absolute inset-0 z-25 flex items-center justify-center transition-opacity duration-300 ${
              showCenterPlayOverlay ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={handleVideoTap}
          >
            <button
              onClick={handleCenterPlayClick}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-black/60 hover:bg-black/80 border-2 border-white/50 flex items-center justify-center transition-all transform hover:scale-110"
              data-testid="center-play-btn"
            >
              {videoPlaying ? (
                <div className="flex gap-2">
                  <div className="w-2 h-10 sm:w-2.5 sm:h-12 bg-white rounded-sm" />
                  <div className="w-2 h-10 sm:w-2.5 sm:h-12 bg-white rounded-sm" />
                </div>
              ) : (
                <Play size={40} className="text-white ml-1 sm:ml-2" fill="white" />
              )}
            </button>
          </div>

          {/* Continue Watching Modal */}
          {showContinueModal && savedProgress && (
            <div className="absolute inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
              <div className="bg-gray-900 border border-white/20 rounded-2xl p-6 max-w-md w-full text-center">
                <h3 className="text-xl font-bold text-white mb-2">Continue Watching?</h3>
                <p className="text-gray-400 mb-4">
                  You were {Math.round((savedProgress.progress / savedProgress.duration) * 100)}% through "{savedProgress.title}"
                </p>
                <p className="text-gray-500 text-sm mb-6">
                  Left off at {formatTime(savedProgress.progress)}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleContinueWatching}
                    className="flex-1 px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-semibold transition-colors"
                    data-testid="continue-watching-btn"
                  >
                    Continue
                  </button>
                  <button
                    onClick={handleStartFromBeginning}
                    className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors"
                    data-testid="start-over-btn"
                  >
                    Start Over
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Custom Caption Overlay */}
          {captionsEnabled && currentCaption && (
            <div 
              className="absolute bottom-28 left-0 right-0 z-20 flex justify-center pointer-events-none"
            >
              <div 
                className="bg-black/85 text-white text-2xl md:text-3xl px-6 py-3 rounded-lg max-w-[85%] text-center font-medium"
                style={{ 
                  textShadow: '2px 2px 4px rgba(0,0,0,0.9)',
                  lineHeight: 1.5
                }}
              >
                {currentCaption}
              </div>
            </div>
          )}

          {/* Up Next Overlay - shows 30 seconds before video ends */}
          {videoPlaying && videoDuration > 0 && (videoDuration - videoProgress) <= 30 && (videoDuration - videoProgress) > 0 && moviesList.length > 1 && (() => {
            const nextMovie = moviesList[(currentIndex + 1) % moviesList.length];
            const imageUrl = nextMovie?.thumb || null;
            
            return (
              <div className="absolute top-4 right-4 z-30 bg-black/95 border border-white/30 rounded-xl p-4 max-w-sm shadow-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-white text-sm font-medium">Up Next in {Math.ceil(videoDuration - videoProgress)}s</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-14 bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
                    {imageUrl ? (
                      <img 
                        src={imageUrl}
                        alt={nextMovie?.title || 'Up next'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full items-center justify-center text-gray-500 ${imageUrl ? 'hidden' : 'flex'}`}>
                      <Film size={24} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate mb-1">
                      {nextMovie?.title || 'Next Video'}
                    </p>
                    <p className="text-gray-400 text-xs truncate mb-2">
                      {nextMovie?.year || ''} {nextMovie?.duration ? `• ${Math.round(nextMovie.duration / 60000)}m` : ''}
                    </p>
                    <button 
                      onClick={goToNextMovie}
                      className="bg-white text-black px-3 py-1 rounded text-xs font-semibold hover:bg-gray-200 transition-colors"
                    >
                      Play Now
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Custom Bottom Control Bar */}
          <div 
            className={`absolute bottom-0 left-0 right-0 z-30 bg-gradient-to-t from-black/95 via-black/80 to-transparent transition-opacity duration-300 ${
              showControls && !mouseIdle ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            {/* Progress Bar with Chapter Markers - Enhanced for touch */}
            <div 
              ref={videoProgressBarRef}
              className="w-full h-3 sm:h-1.5 bg-gray-700/50 cursor-pointer group relative touch-none"
              onClick={seekVideo}
              onMouseDown={handleSeekStart}
              onTouchStart={handleSeekStart}
              data-testid="video-progress-bar"
            >
              {/* Chapter Markers */}
              {chapters.length > 0 && videoDuration > 0 && chapters.map((chapter, index) => (
                <div
                  key={chapter.id || index}
                  className="absolute top-0 bottom-0 w-0.5 bg-white/40 hover:bg-white/80 transition-colors z-10 group/marker"
                  style={{ left: `${(chapter.startTime / videoDuration) * 100}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    jumpToChapter(chapter);
                  }}
                  title={chapter.title}
                >
                  {/* Chapter tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black/90 rounded text-xs text-white whitespace-nowrap opacity-0 group-hover/marker:opacity-100 transition-opacity pointer-events-none">
                    {chapter.title}
                  </div>
                </div>
              ))}
              
              {/* Progress fill - shows seek preview when dragging */}
              <div 
                className="h-full bg-cyan-500 relative transition-none"
                style={{ width: `${videoDuration ? ((isVideoSeeking ? seekPreviewTime : videoProgress) / videoDuration) * 100 : 0}%` }}
              >
                {/* Draggable thumb - always visible on mobile, hover on desktop */}
                <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-4 sm:h-4 bg-white rounded-full shadow-lg transition-opacity ${isVideoSeeking ? 'opacity-100 scale-125' : 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100'}`} />
              </div>
              
              {/* Seek time preview tooltip */}
              {isVideoSeeking && (
                <div 
                  className="absolute -top-10 px-2 py-1 bg-black/90 rounded text-xs text-white pointer-events-none"
                  style={{ left: `${(seekPreviewTime / videoDuration) * 100}%`, transform: 'translateX(-50%)' }}
                >
                  {formatTime(seekPreviewTime)}
                </div>
              )}
            </div>

            {/* Current Chapter Display - shows above progress bar */}
            {currentChapter && chapters.length > 0 && (
              <div className="absolute -top-8 left-4 px-3 py-1 bg-black/70 rounded-full text-white text-xs flex items-center gap-2 hidden sm:flex">
                <BookOpen size={12} className="text-cyan-400" />
                <span>{currentChapter.title}</span>
              </div>
            )}

            {/* Controls Row - Mobile Responsive */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-2 sm:py-3 gap-2 sm:gap-0">
              {/* Mobile: Time display on top */}
              <div className="flex sm:hidden items-center justify-center text-white text-xs font-mono">
                {formatTime(isVideoSeeking ? seekPreviewTime : videoProgress)} / {formatTime(videoDuration)}
              </div>
              
              {/* Playback Controls - Centered on mobile */}
              <div className="flex items-center justify-center sm:justify-start gap-1 sm:gap-3">
                {/* Skip Previous Movie - Hidden on mobile portrait */}
                <button 
                  onClick={goToPreviousMovie}
                  className="hidden sm:block p-2 hover:bg-white/10 rounded-full transition-colors"
                  title="Previous movie"
                  data-testid="prev-movie-btn"
                >
                  <SkipBack size={20} className="text-white" fill="white" />
                </button>

                {/* Rewind 15 seconds */}
                <button 
                  onClick={skipBackward}
                  className="p-2 sm:p-2 hover:bg-white/10 rounded-full transition-colors relative group"
                  title="Rewind 15 seconds"
                  data-testid="rewind-btn"
                >
                  <RotateCcw size={20} className="text-white" />
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[8px] sm:text-[9px] font-bold text-white">15</span>
                </button>

                {/* Play/Pause - Larger touch target on mobile */}
                <button 
                  onClick={toggleVideoPlay}
                  className="p-3 sm:p-3 hover:bg-white/20 rounded-full transition-colors bg-white/10"
                  data-testid="play-pause-btn"
                >
                  {videoPlaying ? (
                    <div className="flex gap-1">
                      <div className="w-1 h-5 sm:w-1.5 sm:h-6 bg-white rounded-sm" />
                      <div className="w-1 h-5 sm:w-1.5 sm:h-6 bg-white rounded-sm" />
                    </div>
                  ) : (
                    <Play size={24} className="text-white sm:w-7 sm:h-7" fill="white" />
                  )}
                </button>

                {/* Fast Forward 15 seconds */}
                <button 
                  onClick={skipForward}
                  className="p-2 sm:p-2 hover:bg-white/10 rounded-full transition-colors relative group"
                  title="Fast forward 15 seconds"
                  data-testid="forward-btn"
                >
                  <RotateCw size={20} className="text-white" />
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[8px] sm:text-[9px] font-bold text-white">15</span>
                </button>

                {/* Skip Next Movie - Hidden on mobile portrait */}
                <button 
                  onClick={goToNextMovie}
                  className="hidden sm:block p-2 hover:bg-white/10 rounded-full transition-colors"
                  title="Next movie"
                  data-testid="next-movie-btn"
                >
                  <SkipForward size={20} className="text-white" fill="white" />
                </button>

                {/* Time Display - Desktop only (moved to top on mobile) */}
                <div className="hidden sm:block text-white text-sm font-mono ml-2">
                  {formatTime(isVideoSeeking ? seekPreviewTime : videoProgress)} / {formatTime(videoDuration)}
                </div>
              </div>

              {/* Right Controls - Volume, CC, Fullscreen */}
              <div className="flex items-center justify-center sm:justify-end gap-2 sm:gap-3">
                {/* Volume Control - Hidden on mobile (use device volume) */}
                <div className="hidden sm:flex items-center gap-2 group/volume">
                  <button 
                    onClick={toggleMute}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  >
                    {videoMuted || videoVolume === 0 ? (
                      <VolumeX size={20} className="text-white" />
                    ) : videoVolume < 0.5 ? (
                      <Volume1 size={20} className="text-white" />
                    ) : (
                      <Volume2 size={20} className="text-white" />
                    )}
                  </button>
                  <div 
                    className="w-20 h-1.5 bg-gray-600 rounded-full cursor-pointer hidden group-hover/volume:block"
                    onClick={changeVolume}
                  >
                    <div 
                      className="h-full bg-white rounded-full"
                      style={{ width: `${videoMuted ? 0 : videoVolume * 100}%` }}
                    />
                  </div>
                </div>

                {/* CC Button - AI Captions (compact on mobile) */}
                <button
                  onClick={toggleCaptions}
                  className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded font-bold text-xs sm:text-sm transition-all flex items-center gap-1 sm:gap-1.5 ${
                    captionsStatus === 'generating' 
                      ? 'bg-yellow-500/30 text-yellow-400 cursor-wait border border-yellow-500/50'
                      : captionsEnabled 
                        ? 'bg-cyan-500 text-white' 
                        : 'bg-gray-700/80 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  }`}
                  disabled={captionsStatus === 'generating'}
                  title={
                    captionsStatus === 'generating' 
                      ? `Generating AI captions... ${captionsProgress}%`
                      : captionsStatus === 'ready'
                        ? (captionsEnabled ? 'Turn off captions' : 'Turn on captions')
                        : 'Generate AI captions (powered by Whisper)'
                  }
                  data-testid="cc-button"
                >
                  {captionsStatus === 'generating' ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span className="hidden sm:inline">AI {captionsProgress}%</span>
                      <span className="sm:hidden">{captionsProgress}%</span>
                    </>
                  ) : captionsStatus !== 'ready' ? (
                    <>
                      <Sparkles size={14} />
                      <span className="hidden sm:inline">AI CC</span>
                      <span className="sm:hidden">CC</span>
                    </>
                  ) : (
                    <span className={captionsEnabled ? 'underline' : ''}>CC</span>
                  )}
                </button>

                {/* Caption Sync Offset Controls - Hidden on mobile */}
                {captionsEnabled && captionsStatus === 'ready' && (
                  <div className="hidden sm:flex items-center gap-1 bg-gray-800/80 rounded px-2 py-1">
                    <button
                      onClick={() => setCaptionOffset(prev => prev - 0.5)}
                      className="text-gray-300 hover:text-white px-1 text-sm font-bold"
                      title="Captions earlier (-0.5s)"
                    >
                      -
                    </button>
                    <span className="text-xs text-gray-400 min-w-[50px] text-center">
                      {captionOffset > 0 ? '+' : ''}{captionOffset.toFixed(1)}s
                    </span>
                    <button
                      onClick={() => setCaptionOffset(prev => prev + 0.5)}
                      className="text-gray-300 hover:text-white px-1 text-sm font-bold"
                      title="Captions later (+0.5s)"
                    >
                      +
                    </button>
                  </div>
                )}

                {/* Chapters Button - Hidden on mobile portrait */}
                <div className="relative hidden sm:block">
                  <button
                    onClick={() => setShowChaptersPanel(!showChaptersPanel)}
                    className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded font-bold text-xs sm:text-sm transition-all flex items-center gap-1 sm:gap-1.5 ${
                      chaptersLoading 
                        ? 'bg-yellow-500/30 text-yellow-400 cursor-wait border border-yellow-500/50'
                        : showChaptersPanel
                          ? 'bg-cyan-500 text-white' 
                          : chapters.length > 0
                            ? 'bg-gray-700/80 text-gray-300 hover:bg-gray-600 border border-gray-600'
                            : 'bg-gray-800/50 text-gray-500 border border-gray-700'
                    }`}
                    disabled={chaptersLoading}
                    title={
                      chaptersLoading 
                        ? 'Loading chapters...'
                        : chapters.length > 0
                          ? `${chapters.length} chapters (${chaptersSource === 'ai' ? 'AI Generated' : chaptersSource === 'plex' ? 'From Plex' : 'Basic'})`
                          : 'No chapters available'
                    }
                    data-testid="chapters-button"
                  >
                    {chaptersLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>...</span>
                      </>
                    ) : (
                      <>
                        <BookOpen size={16} />
                        <span>{chapters.length > 0 ? chapters.length : '0'}</span>
                      </>
                    )}
                  </button>

                  {/* Chapters Panel */}
                  {showChaptersPanel && chapters.length > 0 && (
                    <div 
                      className="absolute bottom-full right-0 mb-2 w-96 max-h-[450px] bg-gray-900/95 border border-gray-700 rounded-xl shadow-2xl overflow-hidden backdrop-blur-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                        <div>
                          <h3 className="text-white font-semibold text-sm">Scenes</h3>
                          <p className="text-gray-400 text-xs">
                            {chaptersSource === 'ai' ? 'AI Generated' : chaptersSource === 'plex' ? 'From Media' : 'Basic Chapters'}
                          </p>
                        </div>
                        <button
                          onClick={() => setShowChaptersPanel(false)}
                          className="p-1 hover:bg-gray-700 rounded"
                        >
                          <X size={16} className="text-gray-400" />
                        </button>
                      </div>
                      <div className="overflow-y-auto max-h-80 custom-scrollbar">
                        {chapters.map((chapter, index) => (
                          <button
                            key={chapter.id || index}
                            onClick={() => jumpToChapter(chapter)}
                            className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-800 transition-colors text-left border-b border-gray-800 last:border-b-0 ${
                              currentChapter?.id === chapter.id ? 'bg-cyan-900/30 border-l-2 border-l-cyan-500' : ''
                            }`}
                            data-testid={`chapter-${index}`}
                          >
                            {/* Scene Thumbnail */}
                            <div className="flex-shrink-0 w-24 h-14 rounded-md bg-gray-800 overflow-hidden relative">
                              {chapter.thumb ? (
                                <img 
                                  src={chapter.thumb}
                                  alt={chapter.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : null}
                              <div className={`absolute inset-0 flex items-center justify-center ${chapter.thumb ? 'bg-black/30' : 'bg-gray-700'}`}>
                                {currentChapter?.id === chapter.id ? (
                                  <div className="w-8 h-8 rounded-full bg-cyan-500/80 flex items-center justify-center">
                                    <Play size={16} className="text-white ml-0.5" fill="currentColor" />
                                  </div>
                                ) : (
                                  <span className="text-white/70 text-lg font-bold">{chapter.id || index + 1}</span>
                                )}
                              </div>
                              {/* Duration badge */}
                              <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px] text-white">
                                {formatTime(chapter.startTime)}
                              </div>
                            </div>
                            
                            {/* Scene Info */}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${currentChapter?.id === chapter.id ? 'text-cyan-400' : 'text-white'}`}>
                                {chapter.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Scene {chapter.id || index + 1} • {formatTime(chapter.startTime)} - {formatTime(chapter.endTime)}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Fullscreen - Always visible */}
                <button 
                  onClick={toggleFullscreen}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  data-testid="fullscreen-btn"
                >
                  {isFullscreen ? (
                    <Minimize size={18} className="text-white sm:w-5 sm:h-5" />
                  ) : (
                    <Maximize size={18} className="text-white sm:w-5 sm:h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </Portal>
    );
  }

  // Full-Page Detail View (matching reference design)
  return (
    <Portal>
      <div 
        className="fixed inset-0 bg-black overflow-y-auto"
        style={{ zIndex: 99999 }}
      >
        {/* Hero Section with Background */}
        <div className="relative min-h-screen">
          {/* Background Image */}
          <div className="absolute inset-0">
            {displayItem.art || displayItem.thumb ? (
              <img 
                src={displayItem.art || displayItem.thumb} 
                alt="" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-900 to-black" />
            )}
            {/* Gradient Overlays for readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          </div>

          {/* Back Button */}
          <button 
            onClick={onClose}
            className="absolute top-6 left-6 z-20 flex items-center gap-2 px-4 py-2 bg-black/50 backdrop-blur-sm rounded-full text-white hover:bg-black/70 transition-colors"
          >
            <ChevronLeft size={20} />
            <span className="text-sm font-medium">Back</span>
          </button>

          {/* Navigation Arrows for Movie Info Page */}
          {hasNavigation && (
            <>
              {/* Previous Movie Arrow */}
              <button
                onClick={goToPreviousMovie}
                className="absolute left-6 top-1/2 -translate-y-1/2 z-20 p-4 bg-black/60 hover:bg-cyan-600 rounded-full transition-all hover:scale-110 group"
                data-testid="detail-prev-btn"
                title="Previous movie"
              >
                <ChevronLeft size={32} className="text-white group-hover:scale-110 transition-transform" />
              </button>
              
              {/* Next Movie Arrow */}
              <button
                onClick={goToNextMovie}
                className="absolute right-6 top-1/2 -translate-y-1/2 z-20 p-4 bg-black/60 hover:bg-cyan-600 rounded-full transition-all hover:scale-110 group"
                data-testid="detail-next-btn"
                title="Next movie"
              >
                <ChevronRight size={32} className="text-white group-hover:scale-110 transition-transform" />
              </button>

              {/* Movie Counter */}
              <div className="absolute top-6 right-6 z-20 px-4 py-2 bg-black/50 backdrop-blur-sm rounded-full text-white text-sm">
                {currentIndex + 1} / {moviesList.length}
              </div>
            </>
          )}

          {/* Content */}
          <div className="relative z-10 min-h-screen flex items-center">
            <div className="container mx-auto px-6 md:px-12 lg:px-20 py-20">
              <div className="max-w-2xl">
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 size={48} className="animate-spin text-white" />
                  </div>
                ) : (
                  <>
                    {/* Movie Title */}
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tight uppercase">
                      {displayItem.title}
                    </h1>

                    {/* Crew Info - Director with dropdown for more crew */}
                    {displayItem.directors && displayItem.directors.length > 0 && (
                      <div className="mb-6">
                        <button
                          onClick={() => setCrewExpanded(!crewExpanded)}
                          className="flex items-center gap-2 text-gray-400 text-sm hover:text-gray-300 transition-colors group"
                        >
                          <span className="text-gray-500">Director:</span>
                          <span className="text-white">{displayItem.directors.slice(0, 2).join(', ')}</span>
                          <ChevronDown 
                            size={16} 
                            className={`text-gray-500 group-hover:text-cyan-400 transition-transform duration-200 ${crewExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                        
                        {/* Expanded crew info */}
                        {crewExpanded && (
                          <div className="mt-3 space-y-2 animate-in slide-in-from-top-2 duration-200">
                            {displayItem.writers && displayItem.writers.length > 0 && (
                              <p className="text-gray-400 text-sm">
                                <span className="text-gray-500">Screenplay:</span>{' '}
                                <span className="text-white">{displayItem.writers.slice(0, 3).join(', ')}</span>
                              </p>
                            )}
                            {displayItem.producers && displayItem.producers.length > 0 && (
                              <p className="text-gray-400 text-sm">
                                <span className="text-gray-500">Producer:</span>{' '}
                                <span className="text-white">{displayItem.producers.slice(0, 3).join(', ')}</span>
                              </p>
                            )}
                            {(displayItem.editors?.length > 0 || extendedCrew.editors?.length > 0) && (
                              <p className="text-gray-400 text-sm">
                                <span className="text-gray-500">Editor:</span>{' '}
                                <span className="text-white">
                                  {(displayItem.editors || extendedCrew.editors).slice(0, 2).join(', ')}
                                </span>
                              </p>
                            )}
                            {extendedCrew.soundDesigners?.length > 0 && (
                              <p className="text-gray-400 text-sm">
                                <span className="text-gray-500">Sound:</span>{' '}
                                <span className="text-white">{extendedCrew.soundDesigners.slice(0, 2).join(', ')}</span>
                              </p>
                            )}
                            {(displayItem.composers?.length > 0 || extendedCrew.composers?.length > 0) && (
                              <p className="text-gray-400 text-sm">
                                <span className="text-gray-500">Music:</span>{' '}
                                <span className="text-white">
                                  {(displayItem.composers || extendedCrew.composers).slice(0, 2).join(', ')}
                                </span>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Rating, Year, Meta Row */}
                    <div className="flex flex-wrap items-center gap-4 mb-6">
                      {displayItem.rating && (
                        <div className="flex items-center gap-2">
                          <Star size={18} className="text-yellow-500 fill-yellow-500" />
                          <span className="text-white font-bold text-lg">{displayItem.rating.toFixed(1)}</span>
                          <span className="text-gray-400 text-sm">/ 10</span>
                        </div>
                      )}
                      {displayItem.year && (
                        <span className="text-gray-400">|</span>
                      )}
                      {displayItem.year && (
                        <span className="text-white">{displayItem.year}</span>
                      )}
                      {displayItem.contentRating && (
                        <>
                          <span className="text-gray-400">|</span>
                          <span className="px-2 py-0.5 border border-gray-500 rounded text-white text-sm">
                            {displayItem.contentRating}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Genre Tags */}
                    {displayItem.genres && displayItem.genres.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {displayItem.genres.slice(0, 4).map((genre, idx) => (
                          <span 
                            key={idx}
                            className="px-4 py-1.5 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm"
                          >
                            {genre}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Summary */}
                    {displayItem.summary && (
                      <div className="mb-6 max-w-xl">
                        <p className={`text-gray-300 leading-relaxed ${summaryExpanded ? '' : 'line-clamp-4'}`}>
                          {displayItem.summary}
                        </p>
                        {displayItem.summary.length > 200 && (
                          <button
                            onClick={() => setSummaryExpanded(!summaryExpanded)}
                            className="mt-2 text-cyan-400 hover:text-cyan-300 text-sm font-medium flex items-center gap-1 transition-colors"
                          >
                            {summaryExpanded ? (
                              <>
                                <ChevronUp size={16} />
                                Show Less
                              </>
                            ) : (
                              <>
                                <ChevronDown size={16} />
                                More Details
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Duration & Quality */}
                    <div className="flex items-center gap-4 mb-8 text-gray-400">
                      {displayItem.duration && (
                        <span className="flex items-center gap-2">
                          <Clock size={16} />
                          Duration: {formatDuration(displayItem.duration)}
                        </span>
                      )}
                      {displayItem.media?.videoResolution && (
                        <span className="px-2 py-1 bg-white/10 rounded text-xs text-white uppercase">
                          {displayItem.media.videoResolution}
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-4">
                      <button
                        onClick={startPlaying}
                        disabled={streamLoading}
                        className="flex items-center gap-3 px-8 py-4 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-all transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                      >
                        {streamLoading ? (
                          <Loader2 size={22} className="animate-spin" />
                        ) : (
                          <Play size={22} fill="white" />
                        )}
                        {streamLoading ? 'LOADING...' : 'WATCH NOW'}
                      </button>
                      <button
                        onClick={toggleWatchlist}
                        className={`flex items-center gap-3 px-6 py-4 rounded-lg font-bold transition-all ${
                          inWatchlist 
                            ? 'bg-white/20 text-white border border-white/40' 
                            : 'bg-black/50 text-white border border-white/30 hover:border-white/60'
                        }`}
                      >
                        {inWatchlist ? <Check size={20} /> : <Plus size={20} />}
                        {inWatchlist ? 'IN WATCHLIST' : 'ADD TO WATCHLIST'}
                      </button>
                      <button
                        onClick={watchInPlex}
                        className="flex items-center gap-2 px-4 py-4 bg-black/50 text-white rounded-lg border border-white/30 hover:border-white/60 transition-colors"
                        title="Watch in Plex"
                      >
                        <ExternalLink size={20} />
                      </button>
                    </div>
                    
                    {/* Trailer Thumbnail */}
                    {trailer && (
                      <div className="mt-8">
                        <h4 className="text-gray-400 text-sm mb-3">Watch Trailer</h4>
                        <button
                          onClick={() => setShowTrailer(true)}
                          className="relative w-48 aspect-video rounded-lg overflow-hidden group cursor-pointer border-2 border-transparent hover:border-orange-500 transition-all"
                        >
                          <img 
                            src={trailer.thumbnail} 
                            alt="Trailer"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                              <Play size={24} fill="white" className="text-white ml-1" />
                            </div>
                          </div>
                          <span className="absolute bottom-2 left-2 text-xs text-white bg-black/70 px-2 py-1 rounded">
                            Trailer
                          </span>
                        </button>
                      </div>
                    )}
                    
                    {/* Cast Section with Circle Images */}
                    {fullCast.length > 0 && (
                      <div className="mt-8">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-gray-400 text-sm">Cast</h4>
                          {fullCast.length > 6 && (
                            <button
                              onClick={() => setShowAllCast(true)}
                              className="text-cyan-400 text-sm hover:text-cyan-300 transition-colors"
                            >
                              View All ({fullCast.length})
                            </button>
                          )}
                        </div>
                        <div className="flex gap-4 overflow-x-auto pb-2">
                          {fullCast.slice(0, 6).map((actor, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleActorClick(actor)}
                              className="flex flex-col items-center flex-shrink-0 w-16 group cursor-pointer"
                            >
                              <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-800 border-2 border-gray-700 group-hover:border-cyan-500 transition-colors mb-2">
                                {actor.thumb ? (
                                  <img 
                                    src={actor.thumb.startsWith('http') ? actor.thumb : `/api/plex/image?path=${encodeURIComponent(actor.thumb)}`}
                                    alt={actor.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling?.classList.remove('hidden');
                                    }}
                                  />
                                ) : null}
                                <div className={`${actor.thumb ? 'hidden' : ''} w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800`}>
                                  <span className="text-gray-400 text-lg font-bold">
                                    {actor.name?.charAt(0) || '?'}
                                  </span>
                                </div>
                              </div>
                              <p className="text-white text-xs text-center truncate w-full group-hover:text-cyan-400 transition-colors">{actor.name?.split(' ')[0]}</p>
                              {actor.role && (
                                <p className="text-gray-500 text-[10px] text-center truncate w-full">{actor.role}</p>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Related Movies Section */}
          {smartRelatedMovies && smartRelatedMovies.length > 0 && (
            <div className="relative z-10 bg-black py-12 px-6 md:px-12 lg:px-20">
              <h3 className="text-xl font-bold text-white mb-6">You May Also Like</h3>
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {smartRelatedMovies.slice(0, 10).map((movie, idx) => (
                  <div 
                    key={`related-${movie.ratingKey}-${idx}`}
                    className="flex-shrink-0 w-40 cursor-pointer group"
                    onClick={() => onNavigate && onNavigate(movie)}
                  >
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2">
                      {movie.thumb ? (
                        <img 
                          src={movie.thumb} 
                          alt={movie.title} 
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                          <Film size={32} className="text-gray-600" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Play size={32} className="text-white" fill="white" />
                      </div>
                    </div>
                    <h4 className="text-white text-sm font-medium truncate">{movie.title}</h4>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      {movie.year && <span>{movie.year}</span>}
                      {movie.genres && movie.genres[0] && (
                        <>
                          <span>•</span>
                          <span>{movie.genres[0]}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Trailer Modal */}
      {showTrailer && trailer && (
        <div 
          className="fixed inset-0 z-[100000] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setShowTrailer(false)}
        >
          <div 
            className="relative w-full max-w-4xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowTrailer(false)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors"
            >
              <X size={32} />
            </button>
            <iframe
              src={`${trailer.embedUrl}?autoplay=1`}
              title={trailer.title}
              className="w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
      
      {/* Full Cast Modal */}
      {showAllCast && (
        <div 
          className="fixed inset-0 z-[100000] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setShowAllCast(false)}
        >
          <div 
            className="relative w-full max-w-3xl max-h-[80vh] bg-gray-900 rounded-2xl p-6 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">Full Cast</h3>
              <button
                onClick={() => setShowAllCast(false)}
                className="p-2 hover:bg-gray-800 rounded-full transition-colors"
              >
                <X size={24} className="text-gray-400" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[60vh] pr-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {fullCast.map((actor, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setShowAllCast(false);
                      handleActorClick(actor);
                    }}
                    className="flex flex-col items-center p-3 bg-gray-800/50 rounded-xl hover:bg-gray-800 hover:border-cyan-500 border border-transparent transition-all cursor-pointer"
                  >
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-700 border-2 border-gray-600 mb-3">
                      {actor.thumb ? (
                        <img 
                          src={actor.thumb.startsWith('http') ? actor.thumb : `/api/plex/image?path=${encodeURIComponent(actor.thumb)}`}
                          alt={actor.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`${actor.thumb ? 'hidden' : ''} w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-600 to-gray-700`}>
                        <span className="text-gray-400 text-2xl font-bold">
                          {actor.name?.charAt(0) || '?'}
                        </span>
                      </div>
                    </div>
                    <p className="text-white text-sm font-medium text-center">{actor.name}</p>
                    {actor.role && (
                      <p className="text-gray-400 text-xs text-center mt-1">as {actor.role}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Actor Details Modal */}
      {selectedActor && (
        <div 
          className="fixed inset-0 z-[100000] bg-black/95 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => {
            setSelectedActor(null);
            setActorDetails(null);
          }}
        >
          <div 
            className="relative w-full max-w-4xl bg-gray-900 rounded-2xl overflow-hidden my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with close button */}
            <button
              onClick={() => {
                setSelectedActor(null);
                setActorDetails(null);
              }}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
            >
              <X size={24} className="text-white" />
            </button>
            
            {actorLoading ? (
              <div className="flex items-center justify-center py-32">
                <Loader2 size={48} className="animate-spin text-cyan-500" />
              </div>
            ) : actorDetails ? (
              <div className="p-6">
                {/* Actor Header */}
                <div className="flex flex-col md:flex-row gap-6 mb-8">
                  {/* Actor Photo */}
                  <div className="flex-shrink-0">
                    <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden bg-gray-800 border-4 border-gray-700 mx-auto md:mx-0">
                      {actorDetails.actor?.photo ? (
                        <img 
                          src={actorDetails.actor.photo}
                          alt={selectedActor.name}
                          className="w-full h-full object-cover"
                        />
                      ) : selectedActor.thumb ? (
                        <img 
                          src={selectedActor.thumb.startsWith('http') ? selectedActor.thumb : `/api/plex/image?path=${encodeURIComponent(selectedActor.thumb)}`}
                          alt={selectedActor.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800">
                          <span className="text-gray-400 text-5xl font-bold">
                            {selectedActor.name?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Actor Info */}
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{selectedActor.name}</h2>
                    {selectedActor.role && (
                      <p className="text-cyan-400 text-lg mb-4">as {selectedActor.role}</p>
                    )}
                    
                    {/* Birth Info */}
                    {(actorDetails.actor?.birthday || actorDetails.actor?.place_of_birth) && (
                      <div className="flex flex-wrap gap-4 text-gray-400 text-sm mb-4 justify-center md:justify-start">
                        {actorDetails.actor?.birthday && (
                          <span>Born: {new Date(actorDetails.actor.birthday).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                        )}
                        {actorDetails.actor?.place_of_birth && (
                          <span>{actorDetails.actor.place_of_birth}</span>
                        )}
                      </div>
                    )}
                    
                    {/* Biography */}
                    {actorDetails.actor?.biography && (
                      <p className="text-gray-300 text-sm leading-relaxed line-clamp-4">
                        {actorDetails.actor.biography}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Known For Section (from TMDB) */}
                {actorDetails.actor?.known_for?.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-white mb-4">Known For</h3>
                    <div className="flex gap-4 overflow-x-auto pb-2">
                      {actorDetails.actor.known_for.map((work, idx) => (
                        <div key={idx} className="flex-shrink-0 w-28">
                          <div className="aspect-[2/3] rounded-lg overflow-hidden bg-gray-800 mb-2">
                            {work.poster ? (
                              <img src={work.poster} alt={work.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Film size={32} className="text-gray-600" />
                              </div>
                            )}
                          </div>
                          <p className="text-white text-xs font-medium truncate">{work.title}</p>
                          {work.year && <p className="text-gray-500 text-xs">{work.year}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Filmography in Your Library */}
                {actorDetails.filmography?.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">
                      In Your Library ({actorDetails.total_movies} titles)
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {actorDetails.filmography.slice(0, 10).map((movie, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSelectedActor(null);
                            setActorDetails(null);
                            // Navigate to the movie
                            if (onNavigate && movie.ratingKey) {
                              onNavigate({ ratingKey: movie.ratingKey });
                            }
                          }}
                          className="group cursor-pointer text-left"
                        >
                          <div className="aspect-[2/3] rounded-lg overflow-hidden bg-gray-800 mb-2 border-2 border-transparent group-hover:border-cyan-500 transition-colors">
                            {movie.thumb ? (
                              <img 
                                src={`/api/plex/image?path=${encodeURIComponent(movie.thumb)}`}
                                alt={movie.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Film size={32} className="text-gray-600" />
                              </div>
                            )}
                          </div>
                          <p className="text-white text-sm font-medium truncate group-hover:text-cyan-400 transition-colors">{movie.title}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {movie.year && <span>{movie.year}</span>}
                            {movie.rating && (
                              <span className="flex items-center gap-1">
                                <Star size={10} className="text-yellow-500 fill-yellow-500" />
                                {movie.rating.toFixed(1)}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-400">
                <p>Failed to load actor details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </Portal>
  );
};

const MediaFactory = () => {
  // Mini Player Context for PiP functionality
  const { minimizeVideo, isMinimized, expandVideo, miniPlayerVideo, currentTime } = useMiniPlayer();
  
  // Global Spotify Context for the persistent player bar and Now Playing modal
  const {
    currentTrack: globalCurrentTrack,
    isPlaying: globalIsPlaying,
    setCurrentTrack: setGlobalCurrentTrack,
    setIsPlaying: setGlobalIsPlaying,
    setShowPlayerBar: setGlobalShowPlayerBar,
    setAccessToken: setGlobalAccessToken,
    setProgressMs: setGlobalProgressMs,
    setDurationMs: setGlobalDurationMs,
    setShowNowPlaying,
    setCurrentContextUri,
    setCurrentPlaylist,
    setShuffleState: setGlobalShuffleState,
    setRepeatState: setGlobalRepeatState,
    setPlayerDismissed,
    setVolume: setGlobalVolume,
  } = useSpotify();

  // Helper to check if a track is currently playing
  const isTrackPlaying = (trackUri) => {
    if (!globalCurrentTrack || !globalIsPlaying) return false;
    // Extract track ID from URI (spotify:track:XXXXX)
    const currentTrackId = globalCurrentTrack.uri?.split(':').pop() || globalCurrentTrack.id;
    const checkTrackId = trackUri?.split(':').pop();
    return currentTrackId === checkTrackId;
  };

  // Helper to play or toggle a track
  const handleTrackPlayPause = async (trackUri, contextUri = null, trackList = null, trackIndex = -1) => {
    if (isTrackPlaying(trackUri)) {
      // Track is playing, pause it
      try {
        await apiCall(`/spotify/playback/pause?access_token=${spotifyToken}`, { method: 'POST' });
        setGlobalIsPlaying(false);
        setSpotifyIsPlaying(false);
      } catch (err) {
        console.error('Failed to pause:', err);
      }
    } else {
      // Play the track with optional track list for custom queue
      await playSpotifyTrack(trackUri, contextUri, trackList, trackIndex);
    }
  };
  
  const [activeTab, setActiveTab] = useState('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [plexStatus, setPlexStatus] = useState(null);
  const [libraries, setLibraries] = useState([]);
  const [recentlyAdded, setRecentlyAdded] = useState([]);
  const [onDeck, setOnDeck] = useState([]);
  const [libraryContent, setLibraryContent] = useState({});
  const [libraryLoading, setLibraryLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [carouselIndices, setCarouselIndices] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [autoPlayNext, setAutoPlayNext] = useState(false);
  const [plexUrl, setPlexUrl] = useState('');
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [homePageMovies, setHomePageMovies] = useState([]); // Separate state for home page movies carousel
  const [homePageMusicVideos, setHomePageMusicVideos] = useState([]); // Separate state for home page music videos
  const [heroSlideIndex, setHeroSlideIndex] = useState(0); // Hero slideshow index
  
  // Spotify state
  const [spotifyToken, setSpotifyToken] = useState(localStorage.getItem('spotify_access_token') || null);
  const [spotifyRefreshToken, setSpotifyRefreshToken] = useState(localStorage.getItem('spotify_refresh_token') || null);
  const [spotifyUser, setSpotifyUser] = useState(null);
  const [spotifyPlaylists, setSpotifyPlaylists] = useState([]);
  const [spotifyRecentlyPlayed, setSpotifyRecentlyPlayed] = useState([]);
  const [spotifyCurrentTrack, setSpotifyCurrentTrack] = useState(null);
  const [spotifyIsPlaying, setSpotifyIsPlaying] = useState(false);
  const [spotifyLoading, setSpotifyLoading] = useState(false);
  const [selectedSpotifyPlaylist, setSelectedSpotifyPlaylist] = useState(null);
  const [spotifyPlaylistTracks, setSpotifyPlaylistTracks] = useState([]);
  const [spotifySearchQuery, setSpotifySearchQuery] = useState('');
  const [spotifySearchResults, setSpotifySearchResults] = useState([]);
  const [spotifyMusicTab, setSpotifyMusicTab] = useState('library'); // library, store, playlists, artists, albums
  const [spotifySavedTracks, setSpotifySavedTracks] = useState([]);
  const [spotifySavedAlbums, setSpotifySavedAlbums] = useState([]);
  const [spotifyFollowedArtists, setSpotifyFollowedArtists] = useState([]);
  const [spotifyTopTracks, setSpotifyTopTracks] = useState([]);
  const [spotifyTopArtists, setSpotifyTopArtists] = useState([]);
  const [spotifyNewReleases, setSpotifyNewReleases] = useState([]);
  const [spotifyCategories, setSpotifyCategories] = useState([]);
  const [spotifyRecentLibraryTracks, setSpotifyRecentLibraryTracks] = useState([]); // Newest 20 tracks from user's library
  const [selectedSpotifyArtist, setSelectedSpotifyArtist] = useState(null);
  const [selectedSpotifyAlbum, setSelectedSpotifyAlbum] = useState(null);
  const [spotifyAlbumTracks, setSpotifyAlbumTracks] = useState([]);
  const [spotifyDevices, setSpotifyDevices] = useState([]);
  const [spotifyActiveDevice, setSpotifyActiveDevice] = useState(null);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [spotifyPlayError, setSpotifyPlayError] = useState(null);
  const [spotifyProgress, setSpotifyProgress] = useState(0);
  const [spotifyVolume, setSpotifyVolume] = useState(50);
  const [spotifyShuffle, setSpotifyShuffle] = useState(false);
  const [spotifyRepeat, setSpotifyRepeat] = useState('off'); // off, context, track
  const [showFullPlaylistView, setShowFullPlaylistView] = useState(false);
  const [fullPlaylistData, setFullPlaylistData] = useState(null);
  const [playlistFilter, setPlaylistFilter] = useState({ genre: '', search: '' });
  const [playlistSortBy, setPlaylistSortBy] = useState({ column: 'index', direction: 'asc' });
  const [showPlayerBar, setShowPlayerBar] = useState(true);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);
  const [dragProgressValue, setDragProgressValue] = useState(0);
  const progressBarRef = useRef(null);
  const [draggedTrackIndex, setDraggedTrackIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Store search state
  const [storeSearchQuery, setStoreSearchQuery] = useState('');
  const [storeSearchResults, setStoreSearchResults] = useState({ tracks: [], albums: [], artists: [] });
  const [storeSearchLoading, setStoreSearchLoading] = useState(false);
  const [storeSearchTab, setStoreSearchTab] = useState('all'); // all, tracks, albums, artists
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryPlaylists, setCategoryPlaylists] = useState([]);
  const [categoryPlaylistsLoading, setCategoryPlaylistsLoading] = useState(false);
  const [spotifyArtistDetails, setSpotifyArtistDetails] = useState(null);
  const [artistDetailsLoading, setArtistDetailsLoading] = useState(false);
  
  // All Music Library tab state
  const [allMusicPlaylist, setAllMusicPlaylist] = useState(null);
  const [allMusicTracks, setAllMusicTracks] = useState([]);
  const [allMusicLoading, setAllMusicLoading] = useState(false);
  const [allMusicLoadingMore, setAllMusicLoadingMore] = useState(false);
  const [allMusicTotal, setAllMusicTotal] = useState(0);
  const [allMusicPage, setAllMusicPage] = useState(1);
  const MUSIC_PAGE_SIZE = 500;
  const [allMusicSearch, setAllMusicSearch] = useState('');
  const [draggedLibraryTrack, setDraggedLibraryTrack] = useState(null);
  const [dragOverPlaylist, setDragOverPlaylist] = useState(null);
  const [playlistTracksCache, setPlaylistTracksCache] = useState({}); // Cache: { playlistId: { tracks, total } }
  
  // Library tab filters
  const [libraryFilterGenre, setLibraryFilterGenre] = useState('');
  const [libraryFilterTag, setLibraryFilterTag] = useState('');
  const [librarySortColumn, setLibrarySortColumn] = useState('dateAdded'); // index, title, album, duration, dateAdded
  const [librarySortDirection, setLibrarySortDirection] = useState('desc');
  
  // Compute sorted tracks for the library view - used for queueing in correct order
  const sortedLibraryTracks = useMemo(() => {
    if (!allMusicTracks || allMusicTracks.length === 0) return [];
    
    return allMusicTracks
      .map((item, originalIdx) => ({ ...item, originalIdx }))
      .filter(item => {
        const track = item.track;
        if (!track) return false;
        if (allMusicSearch.trim()) {
          const search = allMusicSearch.toLowerCase();
          return (
            track.name?.toLowerCase().includes(search) ||
            track.artists?.some(a => a.name.toLowerCase().includes(search)) ||
            track.album?.name?.toLowerCase().includes(search)
          );
        }
        return true;
      })
      .sort((a, b) => {
        const trackA = a.track;
        const trackB = b.track;
        let comparison = 0;
        
        switch (librarySortColumn) {
          case 'index':
            comparison = a.originalIdx - b.originalIdx;
            break;
          case 'title':
            comparison = (trackA?.name || '').localeCompare(trackB?.name || '');
            break;
          case 'album':
            comparison = (trackA?.album?.name || '').localeCompare(trackB?.album?.name || '');
            break;
          case 'duration':
            comparison = (trackA?.duration_ms || 0) - (trackB?.duration_ms || 0);
            break;
          case 'dateAdded':
            comparison = new Date(a.added_at || 0) - new Date(b.added_at || 0);
            break;
          default:
            comparison = 0;
        }
        
        return librarySortDirection === 'asc' ? comparison : -comparison;
      });
  }, [allMusicTracks, allMusicSearch, librarySortColumn, librarySortDirection]);
  
  // Library tab drag reorder state
  const [libraryDragIndex, setLibraryDragIndex] = useState(null);
  const [libraryDragOverIndex, setLibraryDragOverIndex] = useState(null);
  
  // Album art popup
  const [showAlbumArtPopup, setShowAlbumArtPopup] = useState(false);
  
  // Create playlist state
  const [showCreateSpotifyPlaylist, setShowCreateSpotifyPlaylist] = useState(false);
  const [newSpotifyPlaylistName, setNewSpotifyPlaylistName] = useState('');
  const [creatingPlaylist, setCreatingPlaylist] = useState(false);
  
  // Artist detail page state
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [artistDetails, setArtistDetails] = useState(null);
  const [artistLoading, setArtistLoading] = useState(false);
  const [showArtistPage, setShowArtistPage] = useState(false);
  const [artistPlaylistSongs, setArtistPlaylistSongs] = useState([]);
  const [artistSongsLoading, setArtistSongsLoading] = useState(false);
  
  // Album detail modal state
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumTracks, setAlbumTracks] = useState([]);
  const [albumLoading, setAlbumLoading] = useState(false);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  
  // YouTube videos state for artist page
  const [artistYouTubeVideos, setArtistYouTubeVideos] = useState([]);
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [selectedYouTubeVideo, setSelectedYouTubeVideo] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [showYouTubePlayer, setShowYouTubePlayer] = useState(false);
  const [youtubeChannelName, setYoutubeChannelName] = useState('');
  const youtubePlayerRef = useRef(null);
  
  // YouTube Music Videos Tab State
  const [youtubeMusicVideos, setYoutubeMusicVideos] = useState([]);
  const [youtubeMusicArtists, setYoutubeMusicArtists] = useState([]);
  const [youtubeMusicLoading, setYoutubeMusicLoading] = useState(false);
  const [selectedYoutubeMusicArtist, setSelectedYoutubeMusicArtist] = useState(null);
  const [youtubeMusicFeaturedVideo, setYoutubeMusicFeaturedVideo] = useState(null);
  const [youtubeMusicPlayerVideo, setYoutubeMusicPlayerVideo] = useState(null);
  const [showYoutubeMusicPlayer, setShowYoutubeMusicPlayer] = useState(false);
  const [youtubeMusicVideoIndex, setYoutubeMusicVideoIndex] = useState(0);
  const [youtubeMusicStartTime, setYoutubeMusicStartTime] = useState(0); // Track start time for seamless minimize/expand
  const [youtubeMusicSortOption, setYoutubeMusicSortOption] = useState('default'); // Sort option for music videos
  const youtubeMusicPlayerRef = useRef(null);
  const CREATR_MUSIC_PLAYLIST_ID = 'PLLBn8EE88CTUSiybRA7wvt8mN4EBcWpW7'; // Creatr Music playlist
  
  // New state for edit and play history modals
  const [editItem, setEditItem] = useState(null);
  const [playHistoryItem, setPlayHistoryItem] = useState(null);
  const [matchItem, setMatchItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Filter states for library content
  const [sortBy, setSortBy] = useState('year'); // title, year, rating, addedAt - default to year
  const [sortOrder, setSortOrder] = useState('desc'); // desc = newest first (arrow down)
  const [filterGenre, setFilterGenre] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterActor, setFilterActor] = useState('');
  const [filterRating, setFilterRating] = useState('');
  
  // Playlist states
  const [playlists, setPlaylists] = useState([]);
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [playlistsFetched, setPlaylistsFetched] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistItems, setPlaylistItems] = useState([]);
  const [playlistItemsLoading, setPlaylistItemsLoading] = useState(false);
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [addToPlaylistItem, setAddToPlaylistItem] = useState(null);
  
  // Watchlist/My List state - persisted to localStorage
  const [watchlistItems, setWatchlistItems] = useState(() => {
    try {
      const saved = localStorage.getItem('mediaFactory_watchlist');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  // Save watchlist to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('mediaFactory_watchlist', JSON.stringify(watchlistItems));
  }, [watchlistItems]);
  
  // Toggle item in watchlist
  const toggleWatchlistItem = useCallback((item) => {
    setWatchlistItems(prev => {
      const exists = prev.some(i => i.ratingKey === item.ratingKey);
      if (exists) {
        return prev.filter(i => i.ratingKey !== item.ratingKey);
      } else {
        return [...prev, { ...item, addedToWatchlist: new Date().toISOString() }];
      }
    });
  }, []);
  
  // Check if item is in watchlist
  const isInWatchlist = useCallback((item) => {
    return watchlistItems.some(i => i.ratingKey === item.ratingKey);
  }, [watchlistItems]);
  
  // Fetch playlists from Plex - defined early so it can be used in handleMenuAction
  const fetchPlaylists = useCallback(async () => {
    setPlaylistsLoading(true);
    try {
      const res = await apiCall('/plex/playlists');
      setPlaylists(res.playlists || []);
      setPlaylistsFetched(true);
    } catch (err) {
      console.error('Failed to fetch playlists:', err);
      setPlaylistsFetched(true); // Mark as fetched even on error to prevent infinite loop
    } finally {
      setPlaylistsLoading(false);
    }
  }, []);
  
  // Handle menu actions from MediaCard
  const handleMenuAction = useCallback(async (action, item) => {
    console.log('Menu action:', action, item?.title);
    
    try {
      setActionLoading(true);
      
      switch (action) {
        case 'edit':
          setEditItem(item);
          break;
        
        case 'mark-watched':
          await apiCall(`/plex/item/${item.ratingKey}/mark-watched`, { method: 'POST' });
          // Update item in state
          setLibraryContent(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(key => {
              updated[key] = updated[key].map(i => 
                i.ratingKey === item.ratingKey ? { ...i, viewCount: (i.viewCount || 0) + 1 } : i
              );
            });
            return updated;
          });
          break;
        
        case 'mark-unwatched':
          await apiCall(`/plex/item/${item.ratingKey}/mark-unwatched`, { method: 'POST' });
          setLibraryContent(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(key => {
              updated[key] = updated[key].map(i => 
                i.ratingKey === item.ratingKey ? { ...i, viewCount: 0 } : i
              );
            });
            return updated;
          });
          break;
        
        case 'refresh-metadata':
          await apiCall(`/plex/item/${item.ratingKey}/refresh-metadata`, { method: 'POST' });
          alert('Metadata refresh started for ' + item.title);
          break;
        
        case 'analyze':
          await apiCall(`/plex/item/${item.ratingKey}/analyze`, { method: 'POST' });
          alert('Analysis started for ' + item.title);
          break;
        
        case 'delete-confirm':
          await apiCall(`/plex/item/${item.ratingKey}`, { method: 'DELETE' });
          // Remove from library content
          setLibraryContent(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(key => {
              updated[key] = updated[key].filter(i => i.ratingKey !== item.ratingKey);
            });
            return updated;
          });
          setRecentlyAdded(prev => prev.filter(i => i.ratingKey !== item.ratingKey));
          setOnDeck(prev => prev.filter(i => i.ratingKey !== item.ratingKey));
          break;
        
        case 'play-history':
          setPlayHistoryItem(item);
          break;
        
        case 'get-info':
          setSelectedItem(item);
          break;
        
        case 'watch-together':
        case 'play-next':
        case 'add-queue':
          const queueRes = await apiCall(`/plex/queue/add/${item.ratingKey}`, { method: 'POST' });
          if (action === 'watch-together') {
            window.open(queueRes.playUrl, '_blank');
          }
          break;
        
        case 'add-to':
          // Show Add to Playlist modal and fetch playlists if needed
          setAddToPlaylistItem(item);
          if (!playlistsFetched) {
            fetchPlaylists();
          }
          break;
        
        case 'download':
          // Open direct download link
          const streamRes = await apiCall(`/plex/stream/${item.ratingKey}`);
          if (streamRes.directUrl) {
            window.open(streamRes.directUrl, '_blank');
          }
          break;
        
        case 'match':
          setMatchItem(item);
          break;
        
        case 'optimize':
        case 'grant-access':
        case 'report-issue':
          // These would need Plex web interface
          const plexWebUrl = `${plexUrl || 'http://76.201.4.251:32400'}/web/index.html#!/server/~~/details?key=%2Flibrary%2Fmetadata%2F${item.ratingKey}`;
          window.open(plexWebUrl, '_blank');
          break;
        
        default:
          console.log('Unhandled action:', action);
      }
    } catch (err) {
      console.error('Menu action error:', err);
      alert('Action failed: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  }, [plexUrl, playlistsFetched, fetchPlaylists]);
  
  // Handle save from edit modal
  const handleEditSave = useCallback((updatedData) => {
    // Update the item in library content
    setLibraryContent(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        updated[key] = updated[key].map(i => 
          i.ratingKey === editItem?.ratingKey ? { ...i, ...updatedData } : i
        );
      });
      return updated;
    });
    setRecentlyAdded(prev => prev.map(i => 
      i.ratingKey === editItem?.ratingKey ? { ...i, ...updatedData } : i
    ));
  }, [editItem]);
  
  // Handle match applied - refresh the item's metadata
  const handleMatchApplied = useCallback((match) => {
    // Show success message
    alert(`Successfully matched to "${match.name}". Metadata is being refreshed.`);
    
    // Update the item's title immediately in the UI
    if (matchItem) {
      const updatedData = { title: match.name, year: match.year };
      setLibraryContent(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          updated[key] = updated[key].map(i => 
            i.ratingKey === matchItem.ratingKey ? { ...i, ...updatedData } : i
          );
        });
        return updated;
      });
      setRecentlyAdded(prev => prev.map(i => 
        i.ratingKey === matchItem.ratingKey ? { ...i, ...updatedData } : i
      ));
    }
  }, [matchItem]);
  
  // Fetch playlists when Playlists tab is active
  useEffect(() => {
    if (activeTab === 'playlists' && !playlistsFetched && !playlistsLoading) {
      fetchPlaylists();
    }
  }, [activeTab, playlistsFetched, playlistsLoading, fetchPlaylists]);
  
  // Handle playlist click - fetch items
  const handlePlaylistClick = useCallback(async (playlist) => {
    setSelectedPlaylist(playlist);
    setPlaylistItems([]);
    setPlaylistItemsLoading(true);
    try {
      const res = await apiCall(`/plex/playlists/${playlist.ratingKey}/items`);
      setPlaylistItems(res.items || []);
    } catch (err) {
      console.error('Failed to fetch playlist items:', err);
      toast.error('Failed to load playlist items. Please try again.');
    } finally {
      setPlaylistItemsLoading(false);
    }
  }, []);
  
  // Handle create playlist
  const handleCreatePlaylist = useCallback(async () => {
    if (!newPlaylistName.trim()) return;
    
    try {
      const res = await apiCall('/plex/playlists', {
        method: 'POST',
        body: JSON.stringify({ title: newPlaylistName.trim(), type: 'video' })
      });
      
      if (res.playlist) {
        setPlaylists(prev => [...prev, res.playlist]);
        setShowCreatePlaylist(false);
        setNewPlaylistName('');
        toast.success(`Playlist "${res.playlist.title}" created!`);
      }
    } catch (err) {
      console.error('Failed to create playlist:', err);
      toast.error('Failed to create playlist: ' + err.message);
    }
  }, [newPlaylistName]);
  
  // Handle delete playlist
  const handleDeletePlaylist = useCallback(async (playlistKey) => {
    if (!confirm('Are you sure you want to delete this playlist?')) return;
    
    try {
      await apiCall(`/plex/playlists/${playlistKey}`, { method: 'DELETE' });
      setPlaylists(prev => prev.filter(p => p.ratingKey !== playlistKey));
      if (selectedPlaylist?.ratingKey === playlistKey) {
        setSelectedPlaylist(null);
        setPlaylistItems([]);
      }
    } catch (err) {
      console.error('Failed to delete playlist:', err);
      alert('Failed to delete playlist: ' + err.message);
    }
  }, [selectedPlaylist]);
  
  // Handle add item to playlist
  const handleAddToPlaylist = useCallback(async (playlistKey, itemKey) => {
    try {
      await apiCall(`/plex/playlists/${playlistKey}/items`, {
        method: 'POST',
        body: JSON.stringify({ ratingKey: itemKey })
      });
      
      // Update playlist count
      setPlaylists(prev => prev.map(p => 
        p.ratingKey === playlistKey 
          ? { ...p, leafCount: (p.leafCount || 0) + 1 }
          : p
      ));
      
      // If we're currently viewing this playlist, refresh the items
      if (selectedPlaylist?.ratingKey === playlistKey) {
        const res = await apiCall(`/plex/playlists/${playlistKey}/items`);
        setPlaylistItems(res.items || []);
      }
      
      setAddToPlaylistItem(null);
      alert('Added to playlist!');
    } catch (err) {
      console.error('Failed to add to playlist:', err);
      alert('Failed to add to playlist: ' + err.message);
    }
  }, [selectedPlaylist]);
  
  // Handle remove item from playlist
  const handleRemoveFromPlaylist = useCallback(async (playlistKey, itemKey) => {
    try {
      await apiCall(`/plex/playlists/${playlistKey}/items/${itemKey}`, { method: 'DELETE' });
      
      setPlaylistItems(prev => prev.filter(item => 
        (item.playlistItemID || item.ratingKey) !== itemKey
      ));
      
      // Update playlist count
      setPlaylists(prev => prev.map(p => 
        p.ratingKey === playlistKey 
          ? { ...p, leafCount: Math.max((p.leafCount || 1) - 1, 0) }
          : p
      ));
    } catch (err) {
      console.error('Failed to remove from playlist:', err);
      alert('Failed to remove from playlist: ' + err.message);
    }
  }, []);
  
  // Alphabet for filtering
  const alphabet = ['#', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
  
  // Filter content by selected letter
  const filterByLetter = useCallback((items, letter) => {
    if (!letter) return items;
    if (letter === '#') {
      return items.filter(item => {
        const firstChar = (item.title || '')[0]?.toUpperCase();
        return firstChar && !/[A-Z]/.test(firstChar);
      });
    }
    return items.filter(item => {
      const firstChar = (item.title || '')[0]?.toUpperCase();
      return firstChar === letter;
    });
  }, []);
  
  // Navigation tabs based on libraries
  const getNavTabs = useCallback(() => {
    const baseTabs = [
      { id: 'home', label: 'Home', icon: Home },
    ];
    
    // Add tabs for each Plex library (except Music - we use Spotify instead)
    libraries.forEach(lib => {
      // Skip music libraries from Plex - we use Spotify for music
      if (lib.type === 'artist' || lib.title.toLowerCase().includes('music')) {
        return;
      }
      
      let icon = Film;
      if (lib.type === 'show') icon = Tv;
      if (lib.type === 'photo') icon = ImageIcon;
      
      baseTabs.push({
        id: `library-${lib.id}`,
        label: lib.title,
        icon: icon,
        libraryId: lib.id
      });
    });
    
    // Add Spotify Music tab
    baseTabs.push({ id: 'spotify-music', label: 'Music', icon: Music });
    baseTabs.push({ id: 'youtube-music-videos', label: 'Music Videos', icon: Video });
    baseTabs.push({ id: 'tv-shows', label: 'TV Shows', icon: Tv });
    baseTabs.push({ id: 'live-tv', label: 'Live TV', icon: Tv });
    baseTabs.push({ id: 'playlists', label: 'Playlists', icon: ListVideo });
    baseTabs.push({ id: 'watchlist', label: 'My List', icon: Heart });
    
    return baseTabs;
  }, [libraries]);

  // Fetch Plex status and data - optimized with batched requests and timeout
  useEffect(() => {
    const fetchPlexData = async () => {
      setLoading(true);
      setError(null);
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Connection timeout')), 15000)
      );
      
      try {
        // Race Plex status check against timeout
        const statusRes = await Promise.race([
          apiCall('/plex/status'),
          timeoutPromise
        ]);
        setPlexStatus(statusRes);
        
        if (statusRes.status !== 'connected') {
          setError('Plex server is not connected. Make sure you\'re on the same network.');
          setLoading(false);
          return;
        }
        
        // Fetch libraries first (needed to identify movies/music videos libraries)
        const libRes = await Promise.race([
          apiCall('/plex/libraries'),
          timeoutPromise
        ]);
        setLibraries(libRes.libraries || []);
        
        // Batch fetch essential data in parallel with individual timeouts
        const moviesLib = libRes.libraries?.find(l => l.title === 'Movies');
        const musicVideosLib = libRes.libraries?.find(l => l.title === 'Music Videos');
        
        // Use Promise.allSettled for resilience - some fetches may fail without breaking others
        const fetchPromises = [
          apiCall('/plex/recently-added?limit=50'),
          apiCall('/plex/on-deck?limit=20'),
        ];
        
        // Only fetch movies/music videos if libraries exist
        // Load ALL movies (limit=0) for the full library
        if (moviesLib) {
          fetchPromises.push(apiCall(`/plex/library/${moviesLib.id}?limit=0`));
        }
        if (musicVideosLib) {
          fetchPromises.push(apiCall(`/plex/library/${musicVideosLib.id}?limit=0`));
        }
        
        const results = await Promise.allSettled(fetchPromises);
        
        // Process results
        if (results[0].status === 'fulfilled') {
          const recentRes = results[0].value;
          setRecentlyAdded(recentRes.items || []);
          if (recentRes.items?.[0]?.plexUrl) {
            setPlexUrl(recentRes.items[0].plexUrl);
          }
        }
        
        if (results[1].status === 'fulfilled') {
          setOnDeck(results[1].value.items || []);
        }
        
        let resultIndex = 2;
        if (moviesLib && results[resultIndex]?.status === 'fulfilled') {
          setHomePageMovies(results[resultIndex].value.items || []);
          resultIndex++;
        }
        
        if (musicVideosLib && results[resultIndex]?.status === 'fulfilled') {
          setHomePageMusicVideos(results[resultIndex].value.items || []);
        }
        
      } catch (err) {
        console.error('Plex fetch error:', err);
        setError(err.message || 'Failed to connect to Plex');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlexData();
  }, []);

  // Fetch library content when tab changes - load ALL items
  useEffect(() => {
    const fetchLibraryContent = async () => {
      if (!activeTab.startsWith('library-')) return;
      
      const libraryId = activeTab.replace('library-', '');
      
      // Skip if already loaded
      if (libraryContent[libraryId]) return;
      
      setLibraryLoading(prev => ({ ...prev, [libraryId]: true }));
      
      try {
        // Fetch ALL items (limit=0 means no limit)
        const res = await apiCall(`/plex/library/${libraryId}?limit=0`);
        setLibraryContent(prev => ({
          ...prev,
          [libraryId]: res.items || []
        }));
        if (res.items?.[0]?.plexUrl && !plexUrl) {
          setPlexUrl(res.items[0].plexUrl);
        }
      } catch (err) {
        console.error('Library content error:', err);
      } finally {
        setLibraryLoading(prev => ({ ...prev, [libraryId]: false }));
      }
    };
    
    fetchLibraryContent();
  }, [activeTab, libraryContent, plexUrl]);

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    const debounceSearch = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiCall(`/plex/search?query=${encodeURIComponent(searchQuery)}`);
        setSearchResults(res.results || []);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    }, 500);
    
    return () => clearTimeout(debounceSearch);
  }, [searchQuery]);

  // Hero slideshow - auto-advance every 10 seconds with fade transition
  const [heroFading, setHeroFading] = useState(false);
  const [randomizedFeaturedMovies, setRandomizedFeaturedMovies] = useState([]);
  
  // Randomize featured movies on initial load
  useEffect(() => {
    if (homePageMovies.length === 0) return;
    
    const featuredMovies = homePageMovies.filter(m => m.rating >= 6).slice(0, 12);
    // Shuffle array using Fisher-Yates algorithm
    const shuffled = [...featuredMovies];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setRandomizedFeaturedMovies(shuffled.slice(0, 8));
  }, [homePageMovies]);
  
  // Auto-advance slideshow every 10 seconds
  useEffect(() => {
    if (activeTab !== 'home' || randomizedFeaturedMovies.length <= 1) return;
    
    const interval = setInterval(() => {
      // Start fade out
      setHeroFading(true);
      
      // After fade out completes, change slide and fade in
      setTimeout(() => {
        setHeroSlideIndex(prev => (prev + 1) % randomizedFeaturedMovies.length);
        setHeroFading(false);
      }, 500); // 500ms fade out duration
    }, 10000); // 10 second delay
    
    return () => clearInterval(interval);
  }, [activeTab, randomizedFeaturedMovies.length]);

  // ========== SPOTIFY INTEGRATION ==========
  
  // Fetch Spotify user profile
  const fetchSpotifyProfile = useCallback(async () => {
    if (!spotifyToken) return;
    try {
      const res = await apiCall(`/spotify/user/profile?access_token=${spotifyToken}`);
      setSpotifyUser(res);
    } catch (err) {
      console.error('Failed to fetch Spotify profile:', err);
      // Token might be expired, try to refresh
      if (spotifyRefreshToken) {
        try {
          const refreshRes = await apiCall(`/spotify/auth/refresh?refresh_token=${spotifyRefreshToken}`, { method: 'POST' });
          if (refreshRes.access_token) {
            setSpotifyToken(refreshRes.access_token);
            localStorage.setItem('spotify_access_token', refreshRes.access_token);
          }
        } catch (refreshErr) {
          console.error('Failed to refresh Spotify token:', refreshErr);
          handleSpotifyLogout();
        }
      }
    }
  }, [spotifyToken, spotifyRefreshToken]);

  // Track if Spotify data has been loaded (for lazy loading)
  const [spotifyDataLoaded, setSpotifyDataLoaded] = useState(false);
  const [spotifyRateLimited, setSpotifyRateLimited] = useState(false);

  // Fetch essential Spotify data when token is available (minimal initial load)
  useEffect(() => {
    if (spotifyToken && !spotifyDataLoaded) {
      // Only fetch profile initially - other data loads lazily when Music tab is active
      fetchSpotifyProfile();
    }
  }, [spotifyToken]);

  // Lazy load Spotify data when Music tab is active (not on initial page load)
  useEffect(() => {
    if (spotifyToken && activeTab === 'spotify-music' && !spotifyDataLoaded && !spotifyLoading) {
      setSpotifyLoading(true);
      fetchSpotifyDataBatched();
    }
  }, [spotifyToken, activeTab, spotifyDataLoaded]);

  // Batched fetch for Spotify data - uses Promise.allSettled for resilience
  const fetchSpotifyDataBatched = async () => {
    if (!spotifyToken) return;
    
    try {
      // Batch all essential requests using Promise.allSettled (handles partial failures)
      const [
        playlistsResult,
        recentResult,
        devicesResult,
        tracksResult,
        topTracksResult,
        topArtistsResult
      ] = await Promise.allSettled([
        apiCall(`/spotify/playlists?access_token=${spotifyToken}`),
        apiCall(`/spotify/recently-played?access_token=${spotifyToken}&limit=20`),
        apiCall(`/spotify/devices?access_token=${spotifyToken}`),
        apiCall(`/spotify/library/tracks?access_token=${spotifyToken}&limit=50`),
        apiCall(`/spotify/top/tracks?access_token=${spotifyToken}&limit=20`),
        apiCall(`/spotify/top/artists?access_token=${spotifyToken}&limit=20`)
      ]);

      // Check for rate limiting in any response
      const hasRateLimit = [playlistsResult, recentResult, devicesResult, tracksResult, topTracksResult, topArtistsResult]
        .some(r => r.status === 'rejected' && r.reason?.message?.includes('rate'));
      
      if (hasRateLimit) {
        setSpotifyRateLimited(true);
        console.warn('Spotify rate limit detected');
      }

      // Process successful results
      if (playlistsResult.status === 'fulfilled') {
        setSpotifyPlaylists(playlistsResult.value.items || []);
      }
      if (recentResult.status === 'fulfilled') {
        setSpotifyRecentlyPlayed(recentResult.value.items || []);
      }
      if (devicesResult.status === 'fulfilled') {
        const devices = devicesResult.value.devices || [];
        setSpotifyDevices(devices);
        const activeDevice = devices.find(d => d.is_active);
        if (activeDevice) {
          setSpotifyActiveDevice(activeDevice);
        }
      }
      if (tracksResult.status === 'fulfilled') {
        setSpotifySavedTracks(tracksResult.value.items || []);
      }
      if (topTracksResult.status === 'fulfilled') {
        setSpotifyTopTracks(topTracksResult.value.items || []);
      }
      if (topArtistsResult.status === 'fulfilled') {
        setSpotifyTopArtists(topArtistsResult.value.items || []);
      }

      setSpotifyDataLoaded(true);
    } catch (err) {
      console.error('Failed to fetch Spotify data:', err);
      if (err.message?.includes('rate') || err.message?.includes('429')) {
        setSpotifyRateLimited(true);
      }
    } finally {
      setSpotifyLoading(false);
    }
  };

  // Fetch additional library data lazily (only when needed)
  const fetchSpotifyLibraryData = async () => {
    if (!spotifyToken) return;
    
    // Use Promise.allSettled for resilience
    const [albumsResult, artistsResult] = await Promise.allSettled([
      apiCall(`/spotify/library/albums?access_token=${spotifyToken}&limit=50`),
      apiCall(`/spotify/library/artists?access_token=${spotifyToken}&limit=50`)
    ]);

    if (albumsResult.status === 'fulfilled') {
      setSpotifySavedAlbums(albumsResult.value.items || []);
    }
    if (artistsResult.status === 'fulfilled') {
      setSpotifyFollowedArtists(artistsResult.value.items || []);
    }
  };

  // Fetch store data (new releases, featured playlists)
  const fetchSpotifyStoreData = async () => {
    if (!spotifyToken) return;
    
    try {
      // Filter new releases to only show Hip-Hop/Rap and R&B genres - always get 12
      const newReleasesRes = await apiCall(`/spotify/new-releases?access_token=${spotifyToken}&limit=12&genres=hip-hop,rap,r%26b,urban`);
      setSpotifyNewReleases(newReleasesRes.items || []);
    } catch (err) {
      console.error('Failed to fetch new releases:', err);
    }
    
    try {
      const categoriesRes = await apiCall(`/spotify/categories?access_token=${spotifyToken}&limit=50`);
      setSpotifyCategories(categoriesRes.items || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  // Search Spotify Store (tracks, albums, artists)
  const searchSpotifyStore = async (query) => {
    if (!spotifyToken || !query.trim()) {
      setStoreSearchResults({ tracks: [], albums: [], artists: [] });
      return;
    }
    
    setStoreSearchLoading(true);
    try {
      const results = await apiCall(`/spotify/search-all?q=${encodeURIComponent(query)}&access_token=${spotifyToken}&limit=20`);
      setStoreSearchResults({
        tracks: results.tracks || [],
        albums: results.albums || [],
        artists: results.artists || []
      });
    } catch (err) {
      console.error('Store search error:', err);
      setStoreSearchResults({ tracks: [], albums: [], artists: [] });
    } finally {
      setStoreSearchLoading(false);
    }
  };

  // Fetch playlists for a specific category
  const fetchCategoryPlaylists = async (category) => {
    if (!spotifyToken || !category?.id) return;
    
    setCategoryPlaylistsLoading(true);
    setSelectedCategory(category);
    setCategoryPlaylists([]);
    
    try {
      // Pass category name to search for playlists (category_playlists API deprecated by Spotify)
      const categoryName = encodeURIComponent(category.name || '');
      const data = await apiCall(`/spotify/categories/${category.id}/playlists?access_token=${spotifyToken}&limit=50&category_name=${categoryName}`);
      setCategoryPlaylists(data.playlists || []);
    } catch (err) {
      console.error('Failed to fetch category playlists:', err);
      setCategoryPlaylists([]);
    } finally {
      setCategoryPlaylistsLoading(false);
    }
  };

  // Close category view
  const closeCategoryView = () => {
    setSelectedCategory(null);
    setCategoryPlaylists([]);
  };

  // Fetch artist details (full catalog)
  const fetchArtistDetails = async (artistId) => {
    if (!spotifyToken || !artistId) return;
    
    setArtistDetailsLoading(true);
    try {
      const artistData = await apiCall(`/spotify/artist/${artistId}?access_token=${spotifyToken}`);
      setSpotifyArtistDetails(artistData);
    } catch (err) {
      console.error('Failed to fetch artist details:', err);
      setSpotifyArtistDetails(null);
    } finally {
      setArtistDetailsLoading(false);
    }
  };

  // =============================================
  // YouTube Music Videos Tab Functions
  // =============================================
  
  // YouTube Music Playlists configuration
  const youtubeMusicPlaylists = [
    { id: 'combined', name: 'All', playlistId: null, isCombined: true }, // Combined playlist of all videos
    { id: '2026', name: '2026', playlistId: 'PLLBn8EE88CTWZKGPm1DGcuy-zEtP_k095' },
    { id: '2025', name: '2025', playlistId: 'PLLBn8EE88CTUSiybRA7wvt8mN4EBcWpW7' },
    { id: '2024', name: '2024', playlistId: 'PLLBn8EE88CTXSSh6MdOE7XkPERjd_JHsB' },
    { id: '2022', name: '2022', playlistId: 'PLDRBmWrhFcd-PYEuGVKaedeNcGXwBz8x6' },
    { id: '2000s', name: '2000s', playlistId: 'PLDRBmWrhFcd8Xdcd7boL7xxk8v7kQHXbx' },
    { id: '90s', name: '90s', playlistId: 'PLDRBmWrhFcd9PUDHDcHFgMqRFGjv79RMp' },
  ];
  
  const [activeYoutubePlaylist, setActiveYoutubePlaylist] = useState('combined');
  const [youtubePlaylistCache, setYoutubePlaylistCache] = useState({});
  
  // Fetch YouTube Music Videos from a specific playlist
  const fetchYoutubeMusicVideos = async (playlistKey = 'combined') => {
    // Check cache first
    if (youtubePlaylistCache[playlistKey]) {
      const cached = youtubePlaylistCache[playlistKey];
      setYoutubeMusicVideos(cached.videos || []);
      setYoutubeMusicArtists(cached.artists || []);
      if (cached.videos && cached.videos.length > 0) {
        setYoutubeMusicFeaturedVideo(cached.videos[0]);
      }
      return;
    }
    
    setYoutubeMusicLoading(true);
    try {
      const playlist = youtubeMusicPlaylists.find(p => p.id === playlistKey) || youtubeMusicPlaylists[0];
      
      // Handle combined "All" playlist - fetch from all playlists and merge
      if (playlist.isCombined) {
        const individualPlaylists = youtubeMusicPlaylists.filter(p => !p.isCombined);
        const allVideos = [];
        const allArtistsMap = new Map();
        
        // Fetch from each playlist
        for (const pl of individualPlaylists) {
          try {
            const res = await apiCall(`/youtube/playlist/${pl.playlistId}?max_results=1000`);
            if (res.status === 'success') {
              // Add videos with source playlist tag
              const videos = (res.videos || []).map(v => ({ ...v, sourcePlaylist: pl.name }));
              allVideos.push(...videos);
              
              // Merge artists
              (res.artists || []).forEach(artist => {
                if (allArtistsMap.has(artist.name)) {
                  // Merge video counts
                  const existing = allArtistsMap.get(artist.name);
                  existing.videos = [...(existing.videos || []), ...(artist.videos || [])];
                } else {
                  allArtistsMap.set(artist.name, { ...artist });
                }
              });
            }
          } catch (err) {
            console.error(`Failed to fetch playlist ${pl.name}:`, err);
          }
        }
        
        // Remove duplicates based on video ID
        const uniqueVideos = Array.from(
          new Map(allVideos.map(v => [v.id, v])).values()
        );
        
        // Sort artists alphabetically
        const sortedArtists = Array.from(allArtistsMap.values()).sort((a, b) => 
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
        
        // Cache the combined results
        setYoutubePlaylistCache(prev => ({
          ...prev,
          [playlistKey]: { videos: uniqueVideos, artists: sortedArtists }
        }));
        
        setYoutubeMusicVideos(uniqueVideos);
        setYoutubeMusicArtists(sortedArtists);
        
        if (uniqueVideos.length > 0) {
          setYoutubeMusicFeaturedVideo(uniqueVideos[0]);
        }
      } else {
        // Fetch single playlist - request up to 1000 videos to get all
        const res = await apiCall(`/youtube/playlist/${playlist.playlistId}?max_results=1000`);
        
        if (res.status === 'success') {
          const videos = res.videos || [];
          // Sort artists alphabetically by name
          const sortedArtists = (res.artists || []).sort((a, b) => 
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          );
          
          // Cache the results
          setYoutubePlaylistCache(prev => ({
            ...prev,
            [playlistKey]: { videos, artists: sortedArtists }
          }));
          
          setYoutubeMusicVideos(videos);
          setYoutubeMusicArtists(sortedArtists);
          
          // Set first video as featured
          if (videos.length > 0) {
            setYoutubeMusicFeaturedVideo(videos[0]);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch YouTube Music Videos:', err);
      toast.error('Failed to load music videos');
    } finally {
      setYoutubeMusicLoading(false);
    }
  };
  
  // Handle playlist tab change
  const handleYoutubePlaylistChange = useCallback((playlistId) => {
    setActiveYoutubePlaylist(playlistId);
    setSelectedYoutubeMusicArtist(null); // Reset artist filter
    fetchYoutubeMusicVideos(playlistId);
  }, []);

  // Lazy load YouTube Music Videos when tab is active (Music Videos tab or Home tab)
  useEffect(() => {
    if ((activeTab === 'youtube-music-videos' || activeTab === 'home') && youtubeMusicVideos.length === 0 && !youtubeMusicLoading) {
      fetchYoutubeMusicVideos(activeYoutubePlaylist);
    }
  }, [activeTab]);

  // Play YouTube Music Video
  // Filter and sort videos by artist (must be defined before functions that use it)
  const filteredYoutubeMusicVideos = useMemo(() => {
    let videos = selectedYoutubeMusicArtist 
      ? youtubeMusicVideos.filter(v => v.artist === selectedYoutubeMusicArtist)
      : [...youtubeMusicVideos];
    
    // Apply sorting
    switch (youtubeMusicSortOption) {
      case 'title-asc':
        videos.sort((a, b) => (a.songTitle || a.title || '').localeCompare(b.songTitle || b.title || ''));
        break;
      case 'title-desc':
        videos.sort((a, b) => (b.songTitle || b.title || '').localeCompare(a.songTitle || a.title || ''));
        break;
      case 'artist-asc':
        videos.sort((a, b) => (a.artist || '').localeCompare(b.artist || ''));
        break;
      case 'artist-desc':
        videos.sort((a, b) => (b.artist || '').localeCompare(a.artist || ''));
        break;
      case 'published-newest':
        // Sort by YouTube publish date (newest first)
        videos.sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0));
        break;
      case 'published-oldest':
        // Sort by YouTube publish date (oldest first)
        videos.sort((a, b) => new Date(a.published_at || 0) - new Date(b.published_at || 0));
        break;
      case 'added-newest':
        // Sort by date added to playlist (newest first)
        videos.sort((a, b) => new Date(b.added_at || 0) - new Date(a.added_at || 0));
        break;
      case 'added-oldest':
        // Sort by date added to playlist (oldest first)
        videos.sort((a, b) => new Date(a.added_at || 0) - new Date(b.added_at || 0));
        break;
      default:
        // Keep original order (default/playlist order)
        break;
    }
    
    return videos;
  }, [youtubeMusicVideos, selectedYoutubeMusicArtist, youtubeMusicSortOption]);

  // Play YouTube Music Video with index tracking
  const playYoutubeMusicVideo = useCallback((video, index = null) => {
    const videoList = filteredYoutubeMusicVideos;
    const videoIndex = index !== null ? index : videoList.findIndex(v => v.id === video.id);
    setYoutubeMusicVideoIndex(videoIndex >= 0 ? videoIndex : 0);
    setYoutubeMusicPlayerVideo(video);
    setYoutubeMusicStartTime(0); // Reset start time for new video
    // Use the persistent MiniVideoPlayer instead of local player
    minimizeVideo(video, videoList, videoIndex >= 0 ? videoIndex : 0, 0);
  }, [filteredYoutubeMusicVideos, minimizeVideo]);

  // Navigate to next music video
  const playNextMusicVideo = useCallback(() => {
    const videoList = filteredYoutubeMusicVideos;
    if (videoList.length === 0) return;
    
    const nextIndex = (youtubeMusicVideoIndex + 1) % videoList.length;
    const nextVideo = videoList[nextIndex];
    setYoutubeMusicVideoIndex(nextIndex);
    setYoutubeMusicPlayerVideo(nextVideo);
    setYoutubeMusicStartTime(0); // Reset start time for new video
  }, [filteredYoutubeMusicVideos, youtubeMusicVideoIndex]);

  // Navigate to previous music video
  const playPreviousMusicVideo = useCallback(() => {
    const videoList = filteredYoutubeMusicVideos;
    if (videoList.length === 0) return;
    
    const prevIndex = youtubeMusicVideoIndex <= 0 ? videoList.length - 1 : youtubeMusicVideoIndex - 1;
    const prevVideo = videoList[prevIndex];
    setYoutubeMusicVideoIndex(prevIndex);
    setYoutubeMusicPlayerVideo(prevVideo);
    setYoutubeMusicStartTime(0); // Reset start time for new video
  }, [filteredYoutubeMusicVideos, youtubeMusicVideoIndex]);

  // Shuffle and play music videos
  const shuffleAndPlayMusicVideos = useCallback(() => {
    const videoList = [...filteredYoutubeMusicVideos];
    if (videoList.length === 0) return;
    
    // Fisher-Yates shuffle algorithm
    for (let i = videoList.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [videoList[i], videoList[j]] = [videoList[j], videoList[i]];
    }
    
    // Pick the first video from shuffled list to play
    const randomVideo = videoList[0];
    
    // Use the mini player context to set the shuffled queue
    minimizeVideo(randomVideo, videoList, 0);
    
    // Also open the full-screen player
    setYoutubeMusicPlayerVideo(randomVideo);
    setYoutubeMusicVideoIndex(0);
    setShowYoutubeMusicPlayer(true);
    
    toast.success('🔀 Shuffle mode activated!');
  }, [filteredYoutubeMusicVideos, minimizeVideo]);

  // Handle YouTube iframe message for video end detection
  useEffect(() => {
    const handleMessage = (event) => {
      // YouTube iframe API sends messages when video state changes
      if (event.origin === 'https://www.youtube.com') {
        try {
          const data = JSON.parse(event.data);
          // State 0 = ended
          if (data.event === 'onStateChange' && data.info === 0) {
            playNextMusicVideo();
          }
        } catch {
          // Not a JSON message or invalid data - silently ignore
        }
      }
    };

    if (showYoutubeMusicPlayer) {
      window.addEventListener('message', handleMessage);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [showYoutubeMusicPlayer, playNextMusicVideo]);

  // Suppress unhandled errors from YouTube iframe (Script error)
  useEffect(() => {
    const handleError = (event) => {
      // Suppress "Script error" from cross-origin YouTube iframe
      if (event.message === 'Script error.' || event.message?.includes('Script error')) {
        event.preventDefault();
        return true;
      }
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Handle expanding from mini player back to full screen
  useEffect(() => {
    if (!isMinimized && miniPlayerVideo) {
      // User clicked expand on mini player - switch to music videos tab and open full screen modal
      setActiveTab('youtube-music-videos');
      
      // Small delay to let the tab switch and data load
      setTimeout(() => {
        const videoIndex = filteredYoutubeMusicVideos.findIndex(v => v.id === miniPlayerVideo.id);
        if (videoIndex >= 0) {
          setYoutubeMusicVideoIndex(videoIndex);
          setYoutubeMusicPlayerVideo(miniPlayerVideo);
          setYoutubeMusicStartTime(currentTime); // Use the time from mini player
          setShowYoutubeMusicPlayer(true);
        } else {
          // Video not in filtered list, just play from mini player data
          setYoutubeMusicPlayerVideo(miniPlayerVideo);
          setYoutubeMusicStartTime(currentTime);
          setShowYoutubeMusicPlayer(true);
        }
      }, 300);
    }
  }, [isMinimized, miniPlayerVideo, currentTime]);

  // Fetch album tracks
  const fetchSpotifyAlbumTracks = async (albumId) => {
    if (!spotifyToken) return;
    try {
      const res = await apiCall(`/spotify/album/${albumId}/tracks?access_token=${spotifyToken}`);
      setSpotifyAlbumTracks(res.items || []);
    } catch (err) {
      console.error('Failed to fetch album tracks:', err);
    }
  };

  // Fetch store data when store tab is selected
  useEffect(() => {
    if (spotifyMusicTab === 'store' && spotifyToken && spotifyNewReleases.length === 0) {
      fetchSpotifyStoreData();
    }
  }, [spotifyMusicTab, spotifyToken]);

  // Fetch Spotify new releases for home page when connected
  useEffect(() => {
    if (activeTab === 'home' && spotifyToken && spotifyNewReleases.length === 0) {
      fetchSpotifyStoreData();
    }
  }, [activeTab, spotifyToken]);

  // Fetch recent library tracks for home page "New Music" section
  useEffect(() => {
    if (activeTab === 'home' && spotifyToken && spotifyRecentLibraryTracks.length === 0) {
      fetchRecentLibraryTracks();
    }
  }, [activeTab, spotifyToken]);

  // Fetch All Music playlist when Library tab is selected
  useEffect(() => {
    if (spotifyMusicTab === 'all-music' && spotifyToken && !allMusicPlaylist) {
      fetchAllMusicPlaylist();
    }
  }, [spotifyMusicTab, spotifyToken, allMusicPlaylist]);

  // Load playlist tracks progressively (first batch immediately, rest in background)
  const loadPlaylistTracks = async (playlist, forceRefresh = false, page = 1) => {
    if (!spotifyToken || !playlist) return;
    
    const playlistId = playlist.id;
    
    setAllMusicPlaylist(playlist);
    setAllMusicLoading(true);
    setAllMusicTracks([]);
    setAllMusicPage(page);
    
    try {
      // First, get the total count to calculate reverse pagination
      const initialBatch = await apiCall(`/spotify/playlist/${playlistId}/tracks?access_token=${spotifyToken}&offset=0&limit=1`);
      const total = initialBatch.total || 0;
      setAllMusicTotal(total);
      
      if (total === 0) {
        setAllMusicTracks([]);
        setAllMusicLoading(false);
        return;
      }
      
      // Calculate total pages
      const totalPages = Math.ceil(total / MUSIC_PAGE_SIZE);
      
      // Reverse page calculation: page 1 should show the newest (last) items
      // If user requests page 1, we want the LAST chunk of tracks
      // If user requests the last page, we want the FIRST chunk of tracks
      const reversedPage = totalPages - page + 1;
      const reverseOffset = (reversedPage - 1) * MUSIC_PAGE_SIZE;
      
      // Fetch tracks in batches of 100 (Spotify's max) up to MUSIC_PAGE_SIZE
      let pageTracks = [];
      let offset = reverseOffset;
      const targetEnd = Math.min(reverseOffset + MUSIC_PAGE_SIZE, total);
      
      while (offset < targetEnd) {
        const batchSize = Math.min(100, targetEnd - offset);
        const batch = await apiCall(`/spotify/playlist/${playlistId}/tracks?access_token=${spotifyToken}&offset=${offset}&limit=${batchSize}`);
        const items = batch.items || [];
        pageTracks = [...pageTracks, ...items];
        offset += batchSize;
        
        // Show progress as tracks load
        setAllMusicTracks([...pageTracks]);
        
        // Stop if we've reached the end
        if (!batch.has_more || items.length < batchSize) break;
      }
      
      // Reverse the tracks so newest appear first within the page
      pageTracks = pageTracks.reverse();
      
      // Fetch REAL stream counts from Spotify81 API (via album tracks)
      // Build track list with album IDs for efficient fetching
      const tracksWithAlbums = pageTracks
        .filter(item => item.track?.id && item.track?.album?.id)
        .map(item => ({
          id: item.track.id,
          album_id: item.track.album.id
        }));
      
      if (tracksWithAlbums.length > 0) {
        try {
          // Call our new backend endpoint that uses Spotify81 API
          const streamResponse = await apiCall(
            `/spotify-streams/batch`,
            {
              method: 'POST',
              body: JSON.stringify({ tracks: tracksWithAlbums })
            }
          );
          
          const playcounts = streamResponse.playcounts || {};
          
          // Merge real play counts into tracks
          pageTracks = pageTracks.map(item => {
            if (item.track?.id && playcounts[item.track.id]) {
              return {
                ...item,
                track: {
                  ...item.track,
                  playcount: playcounts[item.track.id] // Real stream count!
                }
              };
            }
            return item;
          });
          
          console.log(`✅ Fetched real stream counts for ${Object.keys(playcounts).length} tracks`);
        } catch (streamErr) {
          console.warn('Failed to fetch real stream counts, falling back to popularity:', streamErr);
          
          // Fallback: Fetch popularity data from Spotify API
          const trackIds = pageTracks.map(item => item.track?.id).filter(id => id);
          if (trackIds.length > 0) {
            for (let i = 0; i < trackIds.length; i += 50) {
              const batchIds = trackIds.slice(i, i + 50);
              try {
                const popularityResponse = await apiCall(
                  `/spotify/tracks/batch-popularity?access_token=${spotifyToken}`,
                  {
                    method: 'POST',
                    body: JSON.stringify({ track_ids: batchIds })
                  }
                );
                const popularityMap = popularityResponse.tracks || {};
                pageTracks = pageTracks.map(item => {
                  if (item.track?.id && popularityMap[item.track.id]) {
                    return {
                      ...item,
                      track: {
                        ...item.track,
                        popularity: popularityMap[item.track.id].popularity
                      }
                    };
                  }
                  return item;
                });
              } catch (popErr) {
                console.warn('Failed to fetch popularity batch:', popErr);
              }
            }
          }
        }
      }
      
      setAllMusicTracks(pageTracks);
      setAllMusicLoading(false);
    } catch (err) {
      console.error('Failed to load playlist tracks:', err);
      setAllMusicLoading(false);
    }
  };

  // Fetch All Music playlist (find playlist named "All Music" or first alphabetically)
  const fetchAllMusicPlaylist = async () => {
    if (!spotifyToken) return;
    setAllMusicLoading(true);
    try {
      // Fetch all playlists if not already loaded
      let playlists = spotifyPlaylists;
      if (playlists.length === 0) {
        const playlistRes = await apiCall(`/spotify/playlists?access_token=${spotifyToken}`);
        playlists = playlistRes.items || [];
        setSpotifyPlaylists(playlists);
      }
      
      // Sort alphabetically
      const sortedPlaylists = [...playlists].sort((a, b) => a.name.localeCompare(b.name));
      
      // Find All Music playlist or use first alphabetically
      let targetPlaylist = sortedPlaylists.find(p => 
        p.name.toLowerCase().includes('all music') || 
        p.name.toLowerCase() === 'all music'
      );
      
      // If no "All Music" playlist, use the first one alphabetically
      if (!targetPlaylist && sortedPlaylists.length > 0) {
        targetPlaylist = sortedPlaylists[0];
      }
      
      if (targetPlaylist) {
        await loadPlaylistTracks(targetPlaylist);
      } else {
        setAllMusicLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch playlist:', err);
      setAllMusicLoading(false);
    }
  };

  // Fetch the newest 20 tracks from user's library for the home page
  const fetchRecentLibraryTracks = async () => {
    if (!spotifyToken) return;
    
    try {
      // Fetch all playlists if not already loaded
      let playlists = spotifyPlaylists;
      if (playlists.length === 0) {
        const playlistRes = await apiCall(`/spotify/playlists?access_token=${spotifyToken}`);
        playlists = playlistRes.items || [];
        setSpotifyPlaylists(playlists);
      }
      
      // Sort alphabetically and find All Music playlist
      const sortedPlaylists = [...playlists].sort((a, b) => a.name.localeCompare(b.name));
      let targetPlaylist = sortedPlaylists.find(p => 
        p.name.toLowerCase().includes('all music') || 
        p.name.toLowerCase() === 'all music'
      );
      
      // If no "All Music" playlist, use the first one alphabetically
      if (!targetPlaylist && sortedPlaylists.length > 0) {
        targetPlaylist = sortedPlaylists[0];
      }
      
      if (!targetPlaylist) return;
      
      // First get the total count
      const initialBatch = await apiCall(`/spotify/playlist/${targetPlaylist.id}/tracks?access_token=${spotifyToken}&offset=0&limit=1`);
      const total = initialBatch.total || 0;
      
      if (total === 0) return;
      
      // Fetch the last 20 tracks (most recently added)
      const offset = Math.max(0, total - 20);
      const batch = await apiCall(`/spotify/playlist/${targetPlaylist.id}/tracks?access_token=${spotifyToken}&offset=${offset}&limit=20`);
      
      // Reverse so newest is first
      const recentTracks = (batch.items || []).reverse();
      setSpotifyRecentLibraryTracks(recentTracks);
    } catch (err) {
      console.error('Failed to fetch recent library tracks:', err);
    }
  };

  // Add track to playlist (for drag and drop)
  const addTrackToPlaylist = async (trackUri, playlistId) => {
    if (!spotifyToken) return;
    try {
      await apiCall(`/spotify/playlist/${playlistId}/add?access_token=${spotifyToken}&track_uri=${trackUri}`, {
        method: 'POST'
      });
      console.log('✅ Track added to playlist');
      // Refresh playlist tracks if viewing that playlist
      if (selectedSpotifyPlaylist?.id === playlistId) {
        fetchSpotifyPlaylistTracks(playlistId);
      }
    } catch (err) {
      console.error('Failed to add track to playlist:', err);
      alert('Could not add track to playlist. You may not have permission.');
    }
  };

  // Handle drag start for library tracks
  const handleLibraryDragStart = (e, track, index) => {
    setDraggedLibraryTrack(track);
    // If sorted by index, enable reorder mode
    if (librarySortColumn === 'index') {
      setLibraryDragIndex(index);
      e.dataTransfer.effectAllowed = 'move';
    } else {
      e.dataTransfer.effectAllowed = 'copy';
    }
    e.target.style.opacity = '0.5';
  };

  // Handle drag end for library tracks
  const handleLibraryDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedLibraryTrack(null);
    setDragOverPlaylist(null);
    setLibraryDragIndex(null);
    setLibraryDragOverIndex(null);
  };

  // Handle drag over for reordering in library
  const handleLibraryDragOver = (e, index) => {
    e.preventDefault();
    if (librarySortColumn === 'index' && libraryDragIndex !== null) {
      e.dataTransfer.dropEffect = 'move';
      setLibraryDragOverIndex(index);
    }
  };

  // Handle drag leave for reordering
  const handleLibraryDragLeave = () => {
    setLibraryDragOverIndex(null);
  };

  // Handle drop for reordering in library
  const handleLibraryDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (librarySortColumn !== 'index' || libraryDragIndex === null || libraryDragIndex === dropIndex) {
      setLibraryDragIndex(null);
      setLibraryDragOverIndex(null);
      return;
    }

    // Calculate Spotify API parameters
    const rangeStart = libraryDragIndex;
    const insertBefore = dropIndex > libraryDragIndex ? dropIndex + 1 : dropIndex;

    // Optimistic UI update - reorder tracks in state
    const newTracks = [...allMusicTracks];
    const [draggedTrack] = newTracks.splice(libraryDragIndex, 1);
    newTracks.splice(dropIndex, 0, draggedTrack);
    setAllMusicTracks(newTracks);

    // Update cache
    if (allMusicPlaylist?.id) {
      setPlaylistTracksCache(prev => ({
        ...prev,
        [allMusicPlaylist.id]: { tracks: newTracks, total: allMusicTotal }
      }));
    }

    // Save to Spotify permanently
    try {
      const response = await apiCall(
        `/spotify/playlist/${allMusicPlaylist.id}/reorder?access_token=${spotifyToken}&range_start=${rangeStart}&insert_before=${insertBefore}`,
        { method: 'PUT' }
      );
      console.log('✅ Library playlist reordered and saved to Spotify:', response);
    } catch (err) {
      console.error('❌ Failed to save reorder to Spotify:', err);
      // Revert on failure
      const revertedTracks = [...newTracks];
      const [movedTrack] = revertedTracks.splice(dropIndex, 1);
      revertedTracks.splice(libraryDragIndex, 0, movedTrack);
      setAllMusicTracks(revertedTracks);
      
      if (allMusicPlaylist?.id) {
        setPlaylistTracksCache(prev => ({
          ...prev,
          [allMusicPlaylist.id]: { tracks: revertedTracks, total: allMusicTotal }
        }));
      }
      
      alert('Could not save playlist order. You may not have permission to edit this playlist.');
    }

    setLibraryDragIndex(null);
    setLibraryDragOverIndex(null);
  };

  // Create new Spotify playlist
  const handleCreateSpotifyPlaylist = async () => {
    if (!spotifyToken || !newSpotifyPlaylistName.trim()) return;
    
    setCreatingPlaylist(true);
    try {
      const response = await apiCall(
        `/spotify/playlist/create?access_token=${spotifyToken}&name=${encodeURIComponent(newSpotifyPlaylistName.trim())}`,
        { method: 'POST' }
      );
      
      if (response.playlist) {
        // Add the new playlist to the list
        setSpotifyPlaylists(prev => [...prev, response.playlist]);
        
        // Reset and close modal
        setNewSpotifyPlaylistName('');
        setShowCreateSpotifyPlaylist(false);
        
        // Optionally load the new playlist
        loadPlaylistTracks(response.playlist);
      }
    } catch (err) {
      console.error('Failed to create playlist:', err);
      alert('Could not create playlist. Please try again.');
    } finally {
      setCreatingPlaylist(false);
    }
  };

  // Fetch artist details and open artist page
  const openArtistPage = async (artistId, artistName) => {
    if (!spotifyToken) return;
    
    setSelectedArtist({ id: artistId, name: artistName });
    setShowArtistPage(true);
    setArtistLoading(true);
    setArtistDetails(null);
    setArtistYouTubeVideos([]);
    
    try {
      const response = await apiCall(`/spotify/artist/${artistId}?access_token=${spotifyToken}`);
      setArtistDetails(response);
      
      // Also fetch songs from all playlists by this artist
      fetchArtistSongsFromPlaylists(artistId, artistName);
      
      // Fetch YouTube videos for the artist
      fetchArtistYouTubeVideos(artistName);
    } catch (err) {
      console.error('Failed to fetch artist details:', err);
    } finally {
      setArtistLoading(false);
    }
  };

  // Fetch YouTube videos for the artist (from their official channel)
  const fetchArtistYouTubeVideos = async (artistName) => {
    setYoutubeLoading(true);
    setYoutubeChannelName('');
    try {
      const response = await apiCall(`/youtube/artist-videos?artist_name=${encodeURIComponent(artistName)}&max_results=50`);
      setArtistYouTubeVideos(response.videos || []);
      setYoutubeChannelName(response.channel || '');
    } catch (err) {
      console.error('Failed to fetch YouTube videos:', err);
      setArtistYouTubeVideos([]);
    } finally {
      setYoutubeLoading(false);
    }
  };

  // Open YouTube video player modal
  const openYouTubePlayer = (video, index) => {
    setSelectedYouTubeVideo(video);
    setCurrentVideoIndex(index);
    setShowYouTubePlayer(true);
  };

  // Close YouTube player modal
  const closeYouTubePlayer = () => {
    setShowYouTubePlayer(false);
    setSelectedYouTubeVideo(null);
    setCurrentVideoIndex(0);
  };

  // Navigate to next video
  const playNextVideo = () => {
    if (artistYouTubeVideos.length === 0) return;
    const nextIndex = (currentVideoIndex + 1) % artistYouTubeVideos.length;
    setCurrentVideoIndex(nextIndex);
    setSelectedYouTubeVideo(artistYouTubeVideos[nextIndex]);
  };

  // Navigate to previous video
  const playPreviousVideo = () => {
    if (artistYouTubeVideos.length === 0) return;
    const prevIndex = currentVideoIndex === 0 ? artistYouTubeVideos.length - 1 : currentVideoIndex - 1;
    setCurrentVideoIndex(prevIndex);
    setSelectedYouTubeVideo(artistYouTubeVideos[prevIndex]);
  };

  // Handle keyboard navigation for YouTube player
  useEffect(() => {
    if (!showYouTubePlayer) return;
    
    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          playNextVideo();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          playPreviousVideo();
          break;
        case 'Escape':
          event.preventDefault();
          closeYouTubePlayer();
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showYouTubePlayer, currentVideoIndex, artistYouTubeVideos]);

  // Handle YouTube iframe API for auto-play next video
  useEffect(() => {
    if (!showYouTubePlayer || !selectedYouTubeVideo) return;
    
    // Listen for messages from YouTube iframe (for video end detection)
    const handleMessage = (event) => {
      // YouTube sends state changes via postMessage
      if (event.data && typeof event.data === 'string') {
        try {
          const data = JSON.parse(event.data);
          // State 0 = ended
          if (data.event === 'onStateChange' && data.info === 0) {
            playNextVideo();
          }
        } catch (e) {
          // Not a JSON message, ignore
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [showYouTubePlayer, selectedYouTubeVideo, currentVideoIndex, artistYouTubeVideos]);

  // Fetch all songs by artist from user's playlists
  const fetchArtistSongsFromPlaylists = async (artistId, artistName) => {
    if (!spotifyToken) return;
    
    setArtistSongsLoading(true);
    try {
      const response = await apiCall(
        `/spotify/artist/${artistId}/songs-from-playlists?access_token=${spotifyToken}&artist_name=${encodeURIComponent(artistName)}`
      );
      setArtistPlaylistSongs(response.songs || []);
    } catch (err) {
      console.error('Failed to fetch artist songs from playlists:', err);
      setArtistPlaylistSongs([]);
    } finally {
      setArtistSongsLoading(false);
    }
  };

  // Close artist page
  const closeArtistPage = () => {
    setShowArtistPage(false);
    setSelectedArtist(null);
    setArtistDetails(null);
    setArtistPlaylistSongs([]);
    setArtistYouTubeVideos([]);
  };

  // Open album detail modal
  const openAlbumModal = async (album) => {
    if (!spotifyToken || !album) return;
    
    setSelectedAlbum(album);
    setShowAlbumModal(true);
    setAlbumLoading(true);
    setAlbumTracks([]);
    
    try {
      const response = await apiCall(`/spotify/album/${album.id}/tracks?access_token=${spotifyToken}`);
      setAlbumTracks(response.items || []);
    } catch (err) {
      console.error('Failed to fetch album tracks:', err);
    } finally {
      setAlbumLoading(false);
    }
  };

  // Close album modal
  const closeAlbumModal = () => {
    setShowAlbumModal(false);
    setSelectedAlbum(null);
    setAlbumTracks([]);
  };

  // Play entire album
  const playAlbum = async (albumUri) => {
    if (!spotifyToken || !albumUri) return;
    
    try {
      const devices = await apiCall(`/spotify/devices?access_token=${spotifyToken}`);
      const activeDevice = devices.devices?.find(d => d.is_active) || devices.devices?.[0];
      
      if (!activeDevice) {
        setSpotifyPlayError('No active Spotify device found. Open Spotify on your device first.');
        return;
      }

      await apiCall(`/spotify/playback/play-context?access_token=${spotifyToken}&context_uri=${albumUri}&device_id=${activeDevice.id}`, {
        method: 'POST',
      });

      setSpotifyIsPlaying(true);
      setTimeout(() => fetchSpotifyCurrentTrack(), 500);
    } catch (err) {
      console.error('Play album error:', err);
      setSpotifyPlayError(err.message || 'Failed to play album');
    }
  };

  // Handle drag over playlist
  const handlePlaylistDragOver = (e, playlistId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverPlaylist(playlistId);
  };

  // Handle drop on playlist
  const handlePlaylistDrop = async (e, playlistId) => {
    e.preventDefault();
    if (draggedLibraryTrack?.track?.uri) {
      await addTrackToPlaylist(draggedLibraryTrack.track.uri, playlistId);
    }
    setDraggedLibraryTrack(null);
    setDragOverPlaylist(null);
  };

  // Fetch Spotify playlists
  const fetchSpotifyPlaylists = async () => {
    if (!spotifyToken) return;
    try {
      const res = await apiCall(`/spotify/playlists?access_token=${spotifyToken}`);
      setSpotifyPlaylists(res.items || []);
    } catch (err) {
      console.error('Failed to fetch Spotify playlists:', err);
    }
  };

  // Fetch recently played tracks
  const fetchSpotifyRecentlyPlayed = async () => {
    if (!spotifyToken) return;
    try {
      const res = await apiCall(`/spotify/recently-played?access_token=${spotifyToken}&limit=20`);
      setSpotifyRecentlyPlayed(res.items || []);
    } catch (err) {
      console.error('Failed to fetch recently played:', err);
    }
  };

  // Fetch currently playing track
  const fetchSpotifyCurrentTrack = async () => {
    if (!spotifyToken) return;
    try {
      const res = await apiCall(`/spotify/currently-playing?access_token=${spotifyToken}`);
      console.log('Currently playing response:', res);
      if (res.track) {
        console.log('Track name:', res.track.name, 'Album art:', res.track.album_art);
        // Map the backend response to the expected format
        const trackData = {
          ...res.track,
          name: res.track.name,
          artists: [{ name: res.track.artist }],
          album: {
            name: res.track.album,
            images: res.track.album_art ? [{ url: res.track.album_art }] : []
          },
          duration_ms: res.track.duration,
          uri: res.track.uri
        };
        setSpotifyCurrentTrack(trackData);
        setSpotifyProgress(res.track.progress || 0);
        
        // Also update global Spotify context for the persistent player bar
        setGlobalCurrentTrack({
          name: res.track.name,
          artists: res.track.artist,
          album: res.track.album,
          image: res.track.album_art || ''
        });
        setGlobalIsPlaying(res.is_playing);
        // Show global player bar when a track is playing
        if (res.is_playing) {
          setGlobalShowPlayerBar(true);
        }
        setGlobalAccessToken(spotifyToken);
        setGlobalProgressMs(res.track.progress || 0);
        setGlobalDurationMs(res.track.duration || 0);
        
        // Update shuffle and repeat state from Spotify
        if (res.shuffle_state !== undefined) {
          setGlobalShuffleState(res.shuffle_state);
        }
        if (res.repeat_state !== undefined) {
          setGlobalRepeatState(res.repeat_state);
        }
        // Sync volume from Spotify
        if (res.volume_percent !== undefined) {
          setGlobalVolume(res.volume_percent);
        }
      } else {
        console.log('No track playing');
        setSpotifyCurrentTrack(null);
        // Clear global context when no track is playing
        setGlobalCurrentTrack(null);
        setGlobalIsPlaying(false);
      }
      setSpotifyIsPlaying(res.is_playing);
    } catch (err) {
      console.error('Failed to fetch current track:', err);
    }
  };

  // Always close Now Playing modal when entering Music tab - regardless of Spotify connection
  useEffect(() => {
    if (activeTab === 'spotify-music') {
      setShowNowPlaying(false);
    }
  }, [activeTab, setShowNowPlaying]);

  // Poll for current track when on Spotify Music tab - reduced frequency to avoid rate limits
  useEffect(() => {
    if (activeTab !== 'spotify-music' || !spotifyToken) return;
    
    fetchSpotifyCurrentTrack();
    // Poll every 5 seconds instead of 1 second to avoid rate limiting
    // Progress bar will be smoother via local interpolation
    const interval = setInterval(fetchSpotifyCurrentTrack, 5000);
    return () => clearInterval(interval);
  }, [activeTab, spotifyToken]);

  // Local progress interpolation for smoother progress bar (doesn't hit API)
  useEffect(() => {
    if (!spotifyIsPlaying || !spotifyCurrentTrack) return;
    
    const progressInterval = setInterval(() => {
      setSpotifyProgress(prev => {
        const newProgress = prev + 1000;
        // Don't exceed track duration
        if (newProgress >= (spotifyCurrentTrack.duration_ms || 0)) {
          return prev;
        }
        // Also update global progress for the Now Playing modal
        setGlobalProgressMs(newProgress);
        return newProgress;
      });
    }, 1000);
    
    return () => clearInterval(progressInterval);
  }, [spotifyIsPlaying, spotifyCurrentTrack, setGlobalProgressMs]);

  // Spotify login
  const handleSpotifyLogin = async () => {
    try {
      // Pass current full origin URL to help backend determine correct redirect URI
      const currentOrigin = window.location.origin;
      const res = await apiCall(`/spotify/auth/login?redirect_host=${encodeURIComponent(currentOrigin)}`);
      if (res.auth_url) {
        // Redirect in same window - the callback will redirect back with tokens
        window.location.href = res.auth_url;
      }
    } catch (err) {
      console.error('Spotify login error:', err);
    }
  };

  // Handle Spotify callback (called from URL params)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for tokens directly in URL (from redirect callback)
    const accessToken = urlParams.get('spotify_access_token');
    const refreshToken = urlParams.get('spotify_refresh_token');
    
    if (accessToken && refreshToken) {
      console.log('Spotify tokens received from callback redirect');
      setSpotifyToken(accessToken);
      setSpotifyRefreshToken(refreshToken);
      localStorage.setItem('spotify_access_token', accessToken);
      localStorage.setItem('spotify_refresh_token', refreshToken);
      // Switch to music tab
      setActiveTab('spotify-music');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // Legacy: Check for authorization code (old flow)
    const spotifyCode = urlParams.get('code');
    if (spotifyCode && !spotifyToken) {
      handleSpotifyCallback(spotifyCode);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleSpotifyCallback = async (code) => {
    try {
      const res = await apiCall(`/spotify/auth/callback?code=${code}`);
      if (res.access_token) {
        setSpotifyToken(res.access_token);
        setSpotifyRefreshToken(res.refresh_token);
        localStorage.setItem('spotify_access_token', res.access_token);
        localStorage.setItem('spotify_refresh_token', res.refresh_token);
      }
    } catch (err) {
      console.error('Spotify callback error:', err);
    }
  };

  // Spotify logout
  const handleSpotifyLogout = () => {
    setSpotifyToken(null);
    setSpotifyRefreshToken(null);
    setSpotifyUser(null);
    setSpotifyPlaylists([]);
    setSpotifyRecentlyPlayed([]);
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
  };

  // Play track on Spotify
  const playSpotifyTrack = async (trackUri, contextUri = null, trackList = null, trackIndex = -1) => {
    if (!spotifyToken) return;
    setSpotifyPlayError(null);
    
    try {
      // First, check for available devices
      const devicesRes = await apiCall(`/spotify/devices?access_token=${spotifyToken}`);
      const devices = devicesRes.devices || [];
      setSpotifyDevices(devices);
      
      if (devices.length === 0) {
        setSpotifyPlayError('No Spotify devices found. Please open Spotify on your phone, computer, or web browser first.');
        setShowDeviceSelector(true);
        return;
      }
      
      // Find active device or use first available
      let targetDevice = devices.find(d => d.is_active) || devices[0];
      setSpotifyActiveDevice(targetDevice);
      
      // If no active device, transfer playback first
      if (!devices.some(d => d.is_active)) {
        await apiCall(`/spotify/playback/transfer?access_token=${spotifyToken}&device_id=${targetDevice.id}&play=true`, { method: 'POST' });
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Store the context in global state for next/previous navigation
      if (contextUri) {
        setCurrentContextUri(contextUri);
      }
      
      // If we have a track list (custom order), play the track and queue the rest
      if (trackList && trackList.length > 0 && trackIndex >= 0) {
        console.log('Playing with custom queue order, starting at index:', trackIndex);
        
        // Play the selected track first (single track, no context)
        let playUrl = `/spotify/playback/play?access_token=${spotifyToken}&track_uri=${trackUri}&device_id=${targetDevice.id}`;
        await apiCall(playUrl, { method: 'POST' });
        
        // Turn off shuffle
        try {
          await apiCall(`/spotify/playback/shuffle?access_token=${spotifyToken}&state=false`, { method: 'POST' });
          setGlobalShuffleState(false);
        } catch (e) {
          console.log('Could not disable shuffle:', e);
        }
        
        // Queue the next tracks in order (up to 20)
        const nextTracks = trackList.slice(trackIndex + 1, trackIndex + 21);
        if (nextTracks.length > 0) {
          const trackUris = nextTracks
            .filter(item => item?.track?.uri || item?.uri)
            .map(item => item?.track?.uri || item?.uri);
          
          if (trackUris.length > 0) {
            try {
              await apiCall(`/spotify/playback/queue-multiple?access_token=${spotifyToken}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ track_uris: trackUris, device_id: targetDevice.id })
              });
              console.log('Queued', trackUris.length, 'tracks');
            } catch (e) {
              console.log('Could not queue tracks:', e);
            }
          }
        }
      } else if (contextUri) {
        // Use context URI for Spotify's native playlist order
        console.log('Playing with Spotify context:', contextUri);
        let playUrl = `/spotify/playback/play?access_token=${spotifyToken}&track_uri=${trackUri}&device_id=${targetDevice.id}&context_uri=${encodeURIComponent(contextUri)}`;
        await apiCall(playUrl, { method: 'POST' });
        
        // Turn off shuffle
        try {
          await apiCall(`/spotify/playback/shuffle?access_token=${spotifyToken}&state=false`, { method: 'POST' });
          setGlobalShuffleState(false);
        } catch (e) {
          console.log('Could not disable shuffle:', e);
        }
      } else {
        // Single track playback
        console.log('Playing single track:', trackUri);
        let playUrl = `/spotify/playback/play?access_token=${spotifyToken}&track_uri=${trackUri}&device_id=${targetDevice.id}`;
        await apiCall(playUrl, { method: 'POST' });
      }
      
      // Reset dismissed state and show the global player bar immediately
      setPlayerDismissed(false);
      setGlobalShowPlayerBar(true);
      
      setTimeout(fetchSpotifyCurrentTrack, 500);
    } catch (err) {
      console.error('Failed to play track:', err);
      if (err.message?.includes('No active device') || err.message?.includes('404')) {
        setSpotifyPlayError('No active Spotify device. Please open Spotify app on any device first.');
        setShowDeviceSelector(true);
      } else {
        setSpotifyPlayError('Failed to play track. Make sure Spotify is open on a device.');
      }
    }
  };

  // Fetch available Spotify devices
  const fetchSpotifyDevices = async () => {
    if (!spotifyToken) return;
    try {
      const res = await apiCall(`/spotify/devices?access_token=${spotifyToken}`);
      setSpotifyDevices(res.devices || []);
      const active = res.devices?.find(d => d.is_active);
      if (active) setSpotifyActiveDevice(active);
    } catch (err) {
      console.error('Failed to fetch devices:', err);
    }
  };

  // Transfer playback to a specific device
  const transferToDevice = async (deviceId) => {
    if (!spotifyToken) return;
    try {
      await apiCall(`/spotify/playback/transfer?access_token=${spotifyToken}&device_id=${deviceId}&play=true`, { method: 'POST' });
      setShowDeviceSelector(false);
      setSpotifyPlayError(null);
      setTimeout(fetchSpotifyCurrentTrack, 500);
      setTimeout(fetchSpotifyDevices, 1000);
    } catch (err) {
      console.error('Failed to transfer playback:', err);
    }
  };

  // Play/Pause Spotify
  const toggleSpotifyPlayback = async () => {
    if (!spotifyToken) return;
    try {
      if (spotifyIsPlaying) {
        await apiCall(`/spotify/playback/pause?access_token=${spotifyToken}`, { method: 'POST' });
      } else {
        await apiCall(`/spotify/playback/play?access_token=${spotifyToken}`, { method: 'POST' });
      }
      setTimeout(fetchSpotifyCurrentTrack, 500);
    } catch (err) {
      console.error('Failed to toggle playback:', err);
    }
  };

  // Skip to next track
  const skipSpotifyNext = async () => {
    if (!spotifyToken) {
      console.log('No Spotify token available');
      return;
    }
    console.log('Skipping to next track...');
    try {
      const deviceParam = spotifyActiveDevice?.id ? `&device_id=${spotifyActiveDevice.id}` : '';
      const response = await apiCall(`/spotify/playback/next?access_token=${spotifyToken}${deviceParam}`, { method: 'POST' });
      console.log('Skip next response:', response);
      // Force immediate UI update then refresh again
      setSpotifyProgress(0); // Reset progress
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchSpotifyCurrentTrack();
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchSpotifyCurrentTrack();
    } catch (err) {
      console.error('Failed to skip to next:', err);
      setSpotifyPlayError('Failed to skip to next track. Make sure Spotify is playing on a device.');
    }
  };

  // Skip to previous track
  const skipSpotifyPrevious = async () => {
    if (!spotifyToken) {
      console.log('No Spotify token available');
      return;
    }
    console.log('Skipping to previous track...');
    try {
      const deviceParam = spotifyActiveDevice?.id ? `&device_id=${spotifyActiveDevice.id}` : '';
      const response = await apiCall(`/spotify/playback/previous?access_token=${spotifyToken}${deviceParam}`, { method: 'POST' });
      console.log('Skip previous response:', response);
      // Force immediate UI update then refresh again
      setSpotifyProgress(0); // Reset progress
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchSpotifyCurrentTrack();
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchSpotifyCurrentTrack();
    } catch (err) {
      console.error('Failed to skip to previous:', err);
      setSpotifyPlayError('Failed to skip to previous track. Make sure Spotify is playing on a device.');
    }
  };

  // Set Spotify volume
  const setSpotifyVolumeLevel = async (volume) => {
    if (!spotifyToken) return;
    setSpotifyVolume(volume);
    try {
      await apiCall(`/spotify/playback/volume?access_token=${spotifyToken}&volume=${volume}`, { method: 'POST' });
    } catch (err) {
      console.error('Failed to set volume:', err);
    }
  };

  // Seek to position
  const seekToPosition = async (positionMs) => {
    if (!spotifyToken) return;
    setSpotifyProgress(positionMs);
    try {
      await apiCall(`/spotify/playback/seek?access_token=${spotifyToken}&position_ms=${positionMs}`, { method: 'POST' });
    } catch (err) {
      console.error('Failed to seek:', err);
    }
  };

  // Progress bar drag handlers
  const calculateProgressFromEvent = useCallback((e) => {
    if (!progressBarRef.current || !spotifyCurrentTrack?.duration_ms) return null;
    const rect = progressBarRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    return Math.floor(percent * spotifyCurrentTrack.duration_ms);
  }, [spotifyCurrentTrack?.duration_ms]);

  const handleProgressMouseDown = useCallback((e) => {
    const positionMs = calculateProgressFromEvent(e);
    if (positionMs !== null) {
      setIsDraggingProgress(true);
      setDragProgressValue(positionMs);
    }
  }, [calculateProgressFromEvent]);

  const handleProgressMouseMove = useCallback((e) => {
    if (isDraggingProgress) {
      const positionMs = calculateProgressFromEvent(e);
      if (positionMs !== null) {
        setDragProgressValue(positionMs);
      }
    }
  }, [isDraggingProgress, calculateProgressFromEvent]);

  const handleProgressMouseUp = useCallback((e) => {
    if (isDraggingProgress) {
      const positionMs = calculateProgressFromEvent(e);
      if (positionMs !== null) {
        seekToPosition(positionMs);
      }
      setIsDraggingProgress(false);
    }
  }, [isDraggingProgress, calculateProgressFromEvent, seekToPosition]);

  // Global mouse event listeners for progress bar dragging
  useEffect(() => {
    if (isDraggingProgress) {
      document.addEventListener('mousemove', handleProgressMouseMove);
      document.addEventListener('mouseup', handleProgressMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleProgressMouseMove);
        document.removeEventListener('mouseup', handleProgressMouseUp);
      };
    }
  }, [isDraggingProgress, handleProgressMouseMove, handleProgressMouseUp]);

  // Current display progress (drag progress takes precedence when dragging)
  const displayProgress = isDraggingProgress ? dragProgressValue : spotifyProgress;

  // Drag and drop handlers for playlist reordering
  const handleDragStart = (e, index) => {
    setDraggedTrackIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedTrackIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedTrackIndex === null || draggedTrackIndex === dropIndex) {
      setDraggedTrackIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Calculate the correct insert position for Spotify API
    // Spotify's reorder API uses: range_start (original position), insert_before (new position)
    const rangeStart = draggedTrackIndex;
    const insertBefore = dropIndex > draggedTrackIndex ? dropIndex + 1 : dropIndex;

    // Reorder the tracks in state first (optimistic update)
    const newTracks = [...fullPlaylistData.tracks];
    const [draggedTrack] = newTracks.splice(draggedTrackIndex, 1);
    newTracks.splice(dropIndex, 0, draggedTrack);

    setFullPlaylistData(prev => ({
      ...prev,
      tracks: newTracks
    }));

    // Also update the spotifyPlaylistTracks state so it stays in sync
    setSpotifyPlaylistTracks(newTracks);

    // Save to Spotify permanently
    try {
      const response = await apiCall(
        `/spotify/playlist/${fullPlaylistData.playlist.id}/reorder?access_token=${spotifyToken}&range_start=${rangeStart}&insert_before=${insertBefore}`,
        { method: 'PUT' }
      );
      console.log('✅ Playlist reordered and saved to Spotify:', response);
    } catch (err) {
      console.error('❌ Failed to save reorder to Spotify:', err);
      // Revert the change if API call fails
      const revertedTracks = [...newTracks];
      const [movedTrack] = revertedTracks.splice(dropIndex, 1);
      revertedTracks.splice(draggedTrackIndex, 0, movedTrack);
      setFullPlaylistData(prev => ({
        ...prev,
        tracks: revertedTracks
      }));
      setSpotifyPlaylistTracks(revertedTracks);
      alert('Could not save playlist order. You may not have permission to edit this playlist.');
    }

    setDraggedTrackIndex(null);
    setDragOverIndex(null);
  };

  // Toggle shuffle
  const toggleSpotifyShuffle = async () => {
    if (!spotifyToken) return;
    const newState = !spotifyShuffle;
    setSpotifyShuffle(newState);
    try {
      await apiCall(`/spotify/playback/shuffle?access_token=${spotifyToken}&state=${newState}`, { method: 'POST' });
    } catch (err) {
      console.error('Failed to toggle shuffle:', err);
      setSpotifyShuffle(!newState); // Revert on error
    }
  };

  // Toggle repeat mode
  const toggleSpotifyRepeat = async () => {
    if (!spotifyToken) return;
    const modes = ['off', 'context', 'track'];
    const currentIdx = modes.indexOf(spotifyRepeat);
    const newMode = modes[(currentIdx + 1) % modes.length];
    setSpotifyRepeat(newMode);
    try {
      await apiCall(`/spotify/playback/repeat?access_token=${spotifyToken}&state=${newMode}`, { method: 'POST' });
    } catch (err) {
      console.error('Failed to toggle repeat:', err);
      setSpotifyRepeat(modes[currentIdx]); // Revert on error
    }
  };

  // Fetch playlist tracks
  const fetchSpotifyPlaylistTracks = async (playlistId) => {
    if (!spotifyToken) return;
    try {
      const res = await apiCall(`/spotify/playlist/${playlistId}/tracks?access_token=${spotifyToken}`);
      setSpotifyPlaylistTracks(res.items || []);
    } catch (err) {
      console.error('Failed to fetch playlist tracks:', err);
    }
  };

  // Search Spotify
  const searchSpotify = async (query) => {
    if (!spotifyToken || !query.trim()) {
      setSpotifySearchResults([]);
      return;
    }
    try {
      const res = await apiCall(`/spotify/search?q=${encodeURIComponent(query)}&access_token=${spotifyToken}&limit=20`);
      setSpotifySearchResults(res.tracks?.items || []);
    } catch (err) {
      console.error('Spotify search error:', err);
    }
  };

  // Debounced Spotify search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (spotifySearchQuery && activeTab === 'spotify-music') {
        searchSpotify(spotifySearchQuery);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [spotifySearchQuery, activeTab, spotifyToken]);

  // Format duration from ms to mm:ss
  const formatDurationMs = (ms) => {
    if (!ms) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format play count - handles both real playcounts and popularity-based estimates
  // If track.playcount exists (from Spotify81 API), use real data
  // Otherwise fall back to popularity-based estimation
  const formatPlayCount = (track) => {
    // First check for real playcount from Spotify81 API
    if (track.playcount) {
      const count = parseInt(track.playcount, 10);
      if (isNaN(count)) return '—';
      
      if (count >= 1_000_000_000) {
        return `${(count / 1_000_000_000).toFixed(1)}B`;
      } else if (count >= 1_000_000) {
        return `${(count / 1_000_000).toFixed(0)}M`;
      } else if (count >= 1_000) {
        return `${(count / 1_000).toFixed(0)}K`;
      }
      return count.toLocaleString();
    }
    
    // Fallback to popularity-based estimate
    const popularity = track.popularity;
    if (popularity == null || popularity === 0) return '—';
    
    // Spotify popularity is exponentially weighted - higher values are much rarer
    if (popularity >= 95) return `~${Math.round(1.5 + (popularity - 95) * 0.3)}B`;
    if (popularity >= 90) return `~${Math.round(500 + (popularity - 90) * 200)}M`;
    if (popularity >= 80) return `~${Math.round(100 + (popularity - 80) * 40)}M`;
    if (popularity >= 70) return `~${Math.round(30 + (popularity - 70) * 7)}M`;
    if (popularity >= 60) return `~${Math.round(10 + (popularity - 60) * 2)}M`;
    if (popularity >= 50) return `~${Math.round(3 + (popularity - 50) * 0.7)}M`;
    if (popularity >= 40) return `~${Math.round(800 + (popularity - 40) * 220)}K`;
    if (popularity >= 30) return `~${Math.round(200 + (popularity - 30) * 60)}K`;
    if (popularity >= 20) return `~${Math.round(50 + (popularity - 20) * 15)}K`;
    if (popularity >= 10) return `~${Math.round(10 + (popularity - 10) * 4)}K`;
    return `~${Math.round(popularity * 1)}K`;
  };

  // Legacy function for backwards compatibility
  const formatPopularityAsPlays = (popularity) => {
    if (popularity == null || popularity === 0) return '—';
    
    // Spotify popularity is exponentially weighted - higher values are much rarer
    // Top songs (90-100) have billions of plays
    // Average popular songs (50-70) have tens of millions
    // Lesser known songs (10-30) have thousands to hundreds of thousands
    
    if (popularity >= 95) return `${Math.round(1.5 + (popularity - 95) * 0.3)}B+`;
    if (popularity >= 90) return `${Math.round(500 + (popularity - 90) * 200)}M`;
    if (popularity >= 80) return `${Math.round(100 + (popularity - 80) * 40)}M`;
    if (popularity >= 70) return `${Math.round(30 + (popularity - 70) * 7)}M`;
    if (popularity >= 60) return `${Math.round(10 + (popularity - 60) * 2)}M`;
    if (popularity >= 50) return `${Math.round(3 + (popularity - 50) * 0.7)}M`;
    if (popularity >= 40) return `${Math.round(800 + (popularity - 40) * 220)}K`;
    if (popularity >= 30) return `${Math.round(200 + (popularity - 30) * 60)}K`;
    if (popularity >= 20) return `${Math.round(50 + (popularity - 20) * 15)}K`;
    if (popularity >= 10) return `${Math.round(10 + (popularity - 10) * 4)}K`;
    return `${Math.round(popularity * 1)}K`;
  };

  // ========== END SPOTIFY INTEGRATION ==========

  // Carousel navigation
  const handleCarouselNav = (direction, carouselId, maxItems) => {
    setCarouselIndices(prev => {
      const current = prev[carouselId] || 0;
      const maxIndex = Math.max(0, maxItems - 7);
      if (direction === 'left') {
        return { ...prev, [carouselId]: Math.max(0, current - 1) };
      } else {
        return { ...prev, [carouselId]: Math.min(maxIndex, current + 1) };
      }
    });
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  // Refresh data
  const handleRefresh = async () => {
    setLibraryContent({});
    setError(null);
    setLoading(true);
    try {
      // Re-fetch Plex status
      const statusRes = await apiCall('/plex/status');
      setPlexStatus(statusRes);
      
      if (!statusRes.connected) {
        setError('Plex server is not connected. Make sure you\'re on the same network.');
        setLoading(false);
        return;
      }
      
      // Re-fetch libraries
      const librariesRes = await apiCall('/plex/libraries');
      setLibraries(librariesRes.libraries || []);
      
      // Re-fetch featured movies (all movies)
      const moviesLib = librariesRes.libraries?.find(lib => lib.type === 'movie' && lib.title === 'Movies');
      if (moviesLib) {
        const moviesRes = await apiCall(`/plex/library/${moviesLib.id}?limit=0`);
        setHomePageMovies(moviesRes.items || []);
      }
      
      // Re-fetch recently added
      const recentRes = await apiCall('/plex/recently-added?limit=50');
      setRecentlyAdded(recentRes.items || []);
      
      // Re-fetch on-deck
      const deckRes = await apiCall('/plex/on-deck?limit=20');
      setOnDeck(deckRes.items || []);
      
    } catch (err) {
      console.error('Plex refresh error:', err);
      setError(err.message || 'Failed to connect to Plex');
    } finally {
      setLoading(false);
    }
  };

  // Group recently added by library section
  const groupedRecent = useMemo(() => {
    const groups = {
      movies: [],
      musicVideos: [],
      music: [],
      episodes: [],
      other: []
    };
    
    recentlyAdded.forEach(item => {
      const libTitle = item.librarySectionTitle?.toLowerCase() || '';
      const type = item.type || 'other';
      
      if (libTitle === 'movies' || (libTitle === '' && type === 'movie' && !item.librarySectionID)) {
        // Movies library or fallback for items without library info
        groups.movies.push(item);
      } else if (libTitle === 'music videos' || libTitle.includes('music video')) {
        groups.musicVideos.push(item);
      } else if (type === 'track' || libTitle === 'music') {
        groups.music.push(item);
      } else if (type === 'episode') {
        groups.episodes.push(item);
      } else {
        groups.other.push(item);
      }
    });
    
    return groups;
  }, [recentlyAdded]);

  const navTabs = getNavTabs();

  // Render carousel section - Mobile responsive
  const renderCarousel = (title, items, carouselId, emoji = '🎬') => {
    if (!items || items.length === 0) return null;
    
    const currentIndex = carouselIndices[carouselId] || 0;
    
    return (
      <section className="mb-6 md:mb-10">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="flex items-center gap-2">
            <span className="text-lg md:text-2xl">{emoji}</span>
            <h2 className="text-base md:text-xl font-semibold text-white">{title}</h2>
            <span className="text-xs md:text-sm text-gray-500">({items.length})</span>
          </div>
          {/* Hide carousel nav on mobile - use horizontal scroll instead */}
          <div className="hidden md:flex items-center gap-2">
            <button 
              onClick={() => handleCarouselNav('left', carouselId, items.length)}
              disabled={currentIndex === 0}
              className="p-2 rounded-full border border-gray-600 text-gray-400 hover:border-gray-400 hover:text-white hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft size={18} />
            </button>
            <button 
              onClick={() => handleCarouselNav('right', carouselId, items.length)}
              disabled={currentIndex >= items.length - 7}
              className="p-2 rounded-full border border-gray-600 text-gray-400 hover:border-gray-400 hover:text-white hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
        
        {/* Mobile: Horizontal scroll, Desktop: Transform-based carousel */}
        <div className="relative overflow-x-auto md:overflow-hidden -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
          <div 
            className="flex gap-3 md:gap-4 md:transition-transform md:duration-500 md:ease-out"
            style={{ transform: `translateX(-${currentIndex * 176}px)` }}
          >
            {items.map((item, idx) => (
              <div key={`${item.ratingKey}-${idx}`} className="flex-shrink-0 w-28 sm:w-32 md:w-40">
                <MediaCard 
                  item={item} 
                  onClick={handleItemClick}
                  onMenuAction={handleMenuAction}
                  plexUrl={plexUrl}
                  isInWatchlist={isInWatchlist}
                  onToggleWatchlist={toggleWatchlistItem}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-cyan-400 mx-auto mb-4" />
          <p className="text-gray-400">Connecting to Plex...</p>
        </div>
      </div>
    );
  }

  // Don't block entire page for Plex error - just show warning in relevant sections
  // This allows other tabs like Live TV to still work
  const plexError = error;

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6">
      {/* Show Plex connection error as a banner if applicable */}
      {plexError && activeTab !== 'live-tv' && activeTab !== 'youtube-music-videos' && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-500/30 rounded-xl flex items-center gap-3">
          <AlertCircle size={24} className="text-red-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-red-400 font-medium">Plex Connection Error</p>
            <p className="text-red-400/70 text-sm">{plexError}</p>
          </div>
          <button 
            onClick={handleRefresh}
            className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-1"
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}
      
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col gap-4 mb-6 md:mb-8">
        {/* Top Row: Logo and Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <Film className="w-6 h-6 md:w-8 md:h-8 text-cyan-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                Media Factory
              </h1>
            </div>
          </div>
          
          {/* Mobile: Just refresh button, Desktop: Spotify Profile + Search + Refresh */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Spotify Profile Indicator - Only show when connected and on music tab */}
            {spotifyToken && activeTab === 'spotify-music' && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gray-900/80 rounded-full border border-white/30">
                <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
                {spotifyUser ? (
                  <div className="flex items-center gap-2">
                    {spotifyUser.images?.[0]?.url ? (
                      <img src={spotifyUser.images[0].url} alt={spotifyUser.display_name} className="w-5 h-5 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-xs font-bold">
                        {spotifyUser.display_name?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="text-white text-xs font-medium">{spotifyUser.display_name || 'Connected'}</span>
                  </div>
                ) : (
                  <span className="text-white text-xs font-medium">Connected</span>
                )}
                <button onClick={handleSpotifyLogout} className="p-0.5 hover:bg-white/10 rounded-full transition-colors ml-1">
                  <X size={12} className="text-gray-400" />
                </button>
              </div>
            )}
            
            {/* Search - Hidden on mobile, shown on md+ */}
            <div className="relative hidden md:block">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Plex..."
                className="w-48 pl-10 pr-4 py-2 bg-black border border-white/40 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white hover:border-white transition-colors"
              />
              {searching && (
                <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
              )}
            </div>
            <button 
              onClick={handleRefresh}
              className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={18} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Mobile Search - Only visible on small screens */}
        <div className="relative md:hidden">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search Plex..."
            className="w-full pl-9 pr-4 py-2 bg-black border border-white/40 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:border-white"
          />
          {searching && (
            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
          )}
        </div>
        
        {/* Navigation Tabs - Scrollable on mobile */}
        <nav className="flex items-center gap-2 md:gap-3 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
          {navTabs.map((tab) => {
            const IconComp = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 md:gap-2 text-xs md:text-sm font-medium whitespace-nowrap transition-all duration-200 px-4 py-2 md:px-5 md:py-2.5 rounded-lg flex-shrink-0 border ${
                  activeTab === tab.id 
                    ? 'bg-white text-black border-white' 
                    : 'bg-black text-white border-white/40 hover:border-white hover:shadow-[0_0_15px_rgba(255,255,255,0.3)]'
                }`}
              >
                <IconComp size={14} className="md:w-4 md:h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Search Results */}
      {searchQuery && searchResults.length > 0 && (
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-4">
            Search Results for &ldquo;{searchQuery}&rdquo;
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
            {searchResults.map((item, idx) => (
              <MediaCard 
                key={`search-${item.ratingKey}-${idx}`} 
                item={item} 
                onClick={handleItemClick}
                onMenuAction={handleMenuAction}
                plexUrl={plexUrl}
                isInWatchlist={isInWatchlist}
                onToggleWatchlist={toggleWatchlistItem}
              />
            ))}
          </div>
        </section>
      )}

      {/* Home Tab Content */}
      {activeTab === 'home' && !searchQuery && (
        <>
          {/* Hero Section + Now Watching Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
            {/* Hero Featured Movie Slideshow */}
            <div className="lg:col-span-3">
              {(() => {
                // Use randomized featured movies
                if (randomizedFeaturedMovies.length === 0) return null;
                
                const currentMovie = randomizedFeaturedMovies[heroSlideIndex % randomizedFeaturedMovies.length];
                if (!currentMovie) return null;
                
                return (
                  <div 
                    className={`relative rounded-2xl overflow-hidden bg-black cursor-pointer group transition-opacity duration-500 ${heroFading ? 'opacity-0' : 'opacity-100'}`}
                    onClick={() => handleItemClick(currentMovie)}
                  >
                    {/* Background Image - Taller */}
                    <div className="relative h-[350px] md:h-[480px]">
                      {currentMovie.art || currentMovie.thumb ? (
                        <img 
                          src={currentMovie.art || currentMovie.thumb} 
                          alt={currentMovie.title}
                          className="w-full h-full object-cover"
                          key={currentMovie.ratingKey}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900" />
                      )}
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
                    </div>
                    
                    {/* Content Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                      <div className="flex items-center gap-3 mb-3">
                        {currentMovie.year && (
                          <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm text-white">
                            {currentMovie.year}
                          </span>
                        )}
                        {currentMovie.contentRating && (
                          <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm text-white">
                            {currentMovie.contentRating}
                          </span>
                        )}
                        {currentMovie.rating && (
                          <span className="flex items-center gap-1 px-3 py-1 bg-yellow-500/20 backdrop-blur-sm rounded-full text-sm text-yellow-400">
                            <Star size={14} fill="currentColor" />
                            {currentMovie.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">{currentMovie.title}</h2>
                      {currentMovie.summary && (
                        <p className="text-gray-300 text-sm md:text-base line-clamp-2 max-w-2xl mb-6">
                          {currentMovie.summary}
                        </p>
                      )}
                      <button 
                        className="flex items-center gap-2 px-8 py-3 bg-white text-black rounded-full font-semibold hover:bg-gray-200 transition-colors group-hover:scale-105 transform duration-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemClick(currentMovie);
                        }}
                      >
                        <Play size={20} fill="black" />
                        Play Now
                      </button>
                    </div>
                    
                    {/* Slideshow Indicators */}
                    {randomizedFeaturedMovies.length > 1 && (
                      <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 flex gap-2">
                        {randomizedFeaturedMovies.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              setHeroFading(true);
                              setTimeout(() => {
                                setHeroSlideIndex(idx);
                                setHeroFading(false);
                              }, 300);
                            }}
                            className={`w-2 h-2 rounded-full transition-all ${
                              idx === heroSlideIndex % randomizedFeaturedMovies.length
                                ? 'bg-white w-6'
                                : 'bg-white/40 hover:bg-white/60'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            
            {/* Trending Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-black border border-white/20 rounded-2xl p-4 h-[350px] md:h-[480px] flex flex-col">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp size={18} className="text-white/70" />
                  Trending
                </h3>
                <div className="space-y-3 overflow-y-auto pr-1 flex-1">
                  {(() => {
                    // Get trending movies sorted by rating
                    const trendingMovies = homePageMovies
                      .filter(m => m.rating && m.rating >= 6)
                      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
                      .slice(0, 10);
                    
                    return trendingMovies.length > 0 ? (
                      trendingMovies.map((item, idx) => (
                      <div 
                        key={`trending-sidebar-${item.ratingKey}-${idx}`}
                        className="flex gap-3 p-2 rounded-lg hover:bg-white/10 hover:border-white/30 cursor-pointer transition-colors"
                        onClick={() => handleItemClick(item)}
                      >
                        <div className="w-12 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-800">
                          {item.thumb ? (
                            <img src={item.thumb} alt={item.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film size={20} className="text-gray-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-white truncate">{item.title}</h4>
                          <p className="text-xs text-gray-400">{item.year}</p>
                          {item.rating && (
                            <div className="flex items-center gap-1 mt-1">
                              <Star size={12} className="text-yellow-400" fill="currentColor" />
                              <span className="text-xs text-yellow-400">{item.rating.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-gray-500 text-sm">
                      <TrendingUp size={24} className="mx-auto mb-2 text-gray-600" />
                      No trending movies
                      <p className="text-xs mt-1 text-gray-600">Check back later!</p>
                    </div>
                  );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Genre Filters */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">
              Genres
            </h3>
            <div className="flex flex-wrap gap-2">
              {['All', 'Action', 'Comedy', 'Drama', 'Sci-Fi', 'Horror', 'Romance', 'Thriller', 'Documentary', 'Animation'].map((genre) => (
                <button
                  key={genre}
                  onClick={() => setFilterGenre(genre === 'All' ? '' : genre)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200 ${
                    (filterGenre === genre) || (genre === 'All' && !filterGenre)
                      ? 'bg-white text-black border-white'
                      : 'bg-black text-white border-white/30 hover:border-white hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                  }`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          {/* New Releases Section */}
          {(() => {
            // Filter by genre if selected, then sort by year
            let newMovies = filterGenre 
              ? homePageMovies.filter(m => m.genres?.some(g => g.toLowerCase().includes(filterGenre.toLowerCase())))
              : homePageMovies;
            newMovies = newMovies
              .filter(m => m.year && m.year >= 2020)
              .sort((a, b) => (b.year || 0) - (a.year || 0))
              .slice(0, 20);
            
            if (newMovies.length === 0) return null;
            
            return (
              <section className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-lg font-semibold text-white">New Movies</h3>
                  <span className="text-sm text-gray-500">({newMovies.length})</span>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                  {newMovies.map((item, idx) => (
                    <div key={`new-${item.ratingKey}-${idx}`} className="flex-shrink-0 w-36 md:w-44">
                      <div 
                        className="bg-black rounded-xl overflow-hidden hover:scale-105 transition-all duration-300 cursor-pointer group"
                        onClick={() => handleItemClick(item)}
                      >
                        <div className="relative aspect-[2/3]">
                          {item.thumb ? (
                            <img src={item.thumb} alt={item.title} className="w-full h-full object-cover rounded-xl" />
                          ) : (
                            <div className="w-full h-full bg-gray-800 flex items-center justify-center rounded-xl">
                              <Film size={32} className="text-gray-600" />
                            </div>
                          )}
                          {/* Rating Badge */}
                          {item.rating && (
                            <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-black/80 backdrop-blur-sm rounded text-xs text-yellow-400">
                              <Star size={10} fill="currentColor" />
                              {item.rating.toFixed(1)}
                            </div>
                          )}
                          {/* Year Badge */}
                          {item.year && (
                            <div className="absolute top-2 right-2 px-2 py-1 bg-black/80 backdrop-blur-sm rounded text-xs text-white">
                              {item.year}
                            </div>
                          )}
                          {/* Play Overlay */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-xl">
                            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                              <Play size={24} className="text-black ml-1" fill="black" />
                            </div>
                          </div>
                        </div>
                        <div className="p-3">
                          <h4 className="text-sm font-medium text-white truncate">{item.title}</h4>
                          <p className="text-xs text-gray-400">{item.year}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })()}

          {/* New Music Section - Recently Added from Library */}
          {spotifyRecentLibraryTracks.length > 0 && (
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-white">New Music</h3>
                <span className="text-sm text-gray-500">({spotifyRecentLibraryTracks.length})</span>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {spotifyRecentLibraryTracks.map((item, idx) => {
                  const track = item.track;
                  if (!track) return null;
                  const albumImage = track.album?.images?.[0]?.url;
                  const artistNames = track.artists?.map(a => a.name).join(', ') || 'Unknown Artist';
                  
                  return (
                    <div 
                      key={`new-music-${track.id}-${idx}`}
                      className="flex-shrink-0 w-40 md:w-44 group cursor-pointer"
                      onClick={() => {
                        if (spotifyToken) {
                          playSpotifyTrack(track.uri);
                        }
                      }}
                    >
                      <div className="relative aspect-square rounded-lg overflow-hidden mb-3 bg-black">
                        {albumImage ? (
                          <img 
                            src={albumImage} 
                            alt={track.name} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                            <Music size={32} className="text-gray-600" />
                          </div>
                        )}
                        {/* Play Overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/40">
                            <Play size={22} className="text-white ml-1" fill="white" />
                          </div>
                        </div>
                      </div>
                      <h4 className="text-sm font-medium text-white truncate">{track.name}</h4>
                      <p className="text-xs text-gray-400 truncate">{artistNames}</p>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Recently Published Music Videos - YouTube */}
          {youtubeMusicVideos.length > 0 && (
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-white">New Music Videos</h3>
                <span className="text-sm text-gray-500">(20)</span>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {[...youtubeMusicVideos]
                  .sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0))
                  .slice(0, 20)
                  .map((item, idx) => (
                  <div 
                    key={`mv-home-${item.id}-${idx}`}
                    className="flex-shrink-0 w-64 group relative bg-black border border-white/20 rounded-xl overflow-hidden hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.15)] transition-all cursor-pointer"
                    onClick={() => playYoutubeMusicVideo(item)}
                  >
                    <div className="relative aspect-video">
                      {item.thumbnail ? (
                        <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                          <Film size={32} className="text-gray-600" />
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                        <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                          <Play size={20} className="text-black ml-0.5" fill="black" />
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-medium text-white line-clamp-1">{item.songTitle || item.title}</h4>
                      <p className="text-xs text-gray-400 truncate">{item.artist}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Libraries Overview */}
          <section className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span>📁</span> Your Libraries
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {libraries.map((lib) => {
                let IconComp = Film;
                let gradient = 'from-cyan-500 to-blue-500';
                
                if (lib.type === 'artist' || lib.title.toLowerCase().includes('music')) {
                  IconComp = Music;
                  gradient = 'from-purple-500 to-pink-500';
                } else if (lib.type === 'show') {
                  IconComp = Tv;
                  gradient = 'from-green-500 to-emerald-500';
                } else if (lib.type === 'photo') {
                  IconComp = ImageIcon;
                  gradient = 'from-yellow-500 to-orange-500';
                }
                
                return (
                  <button
                    key={lib.id}
                    onClick={() => setActiveTab(`library-${lib.id}`)}
                    className="relative overflow-hidden rounded-xl p-6 bg-black border border-white/20 hover:bg-gray-800/70 transition-all duration-300 group text-left"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-10 group-hover:opacity-20 transition-opacity`} />
                    <IconComp size={32} className="text-white mb-3 relative" />
                    <h4 className="text-lg font-semibold text-white relative">{lib.title}</h4>
                    <p className="text-sm text-gray-400 relative capitalize">{lib.type}</p>
                  </button>
                );
              })}
            </div>
          </section>
        </>
      )}

      {/* Library Tab Content */}
      {activeTab.startsWith('library-') && !searchQuery && (
        <LibraryContent 
          activeTab={activeTab}
          libraries={libraries}
          libraryContent={libraryContent}
          libraryLoading={libraryLoading}
          selectedLetter={selectedLetter}
          setSelectedLetter={setSelectedLetter}
          filterByLetter={filterByLetter}
          handleItemClick={handleItemClick}
          handleMenuAction={handleMenuAction}
          plexUrl={plexUrl}
          alphabet={alphabet}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          filterGenre={filterGenre}
          setFilterGenre={setFilterGenre}
          filterYear={filterYear}
          setFilterYear={setFilterYear}
          filterActor={filterActor}
          setFilterActor={setFilterActor}
          filterRating={filterRating}
          setFilterRating={setFilterRating}
          isInWatchlist={isInWatchlist}
          toggleWatchlistItem={toggleWatchlistItem}
        />
      )}

      {/* Spotify Music Tab */}
      {activeTab === 'spotify-music' && !searchQuery && (
        <div className="min-h-screen">
          {/* Spotify Not Connected State */}
          {!spotifyToken ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-6">
                <Music size={40} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Connect to Spotify</h2>
              <p className="text-gray-400 mb-6 text-center max-w-md">
                Connect your Spotify account to access your playlists, recently played tracks, and control playback.
              </p>
              <button
                onClick={handleSpotifyLogin}
                className="flex items-center gap-3 px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
                Connect Spotify
              </button>
              
              {/* Premium Notice */}
              <div className="mt-6 p-4 bg-white/10 border border-white/30 rounded-lg max-w-lg">
                <p className="text-white text-sm text-center">
                  <strong>⚠️ Spotify Premium Required:</strong> Playback controls (play, pause, skip, shuffle) require a Spotify Premium account. 
                  Free accounts can only browse playlists and view track info.
                </p>
              </div>
              
              <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg max-w-lg">
                <p className="text-yellow-400 text-sm text-center">
                  <strong>Note:</strong> Make sure to add this redirect URI in your Spotify Developer Dashboard:<br/>
                  <code className="text-xs bg-black/30 px-2 py-1 rounded mt-2 inline-block break-all">
                    {window.location.origin}/api/spotify/auth/callback
                  </code>
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Rate Limit Warning Banner */}
              {spotifyRateLimited && (
                <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center gap-4">
                  <AlertCircle size={24} className="text-yellow-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-yellow-400 font-medium">Spotify Rate Limited</p>
                    <p className="text-yellow-400/70 text-sm mt-1">Too many requests to Spotify. Some data may be unavailable. Please wait a few minutes before refreshing.</p>
                  </div>
                  <button 
                    onClick={() => setSpotifyRateLimited(false)}
                    className="p-1 hover:bg-white/10 rounded-full"
                  >
                    <X size={18} className="text-yellow-400" />
                  </button>
                </div>
              )}

              {/* Loading State */}
              {spotifyLoading && (
                <div className="mb-6 p-4 bg-black border border-white/30 rounded-xl flex items-center gap-4">
                  <Loader2 size={24} className="text-white animate-spin" />
                  <p className="text-white">Loading your Spotify library...</p>
                </div>
              )}

              {/* Error Message Banner */}
              {spotifyPlayError && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-4">
                  <AlertCircle size={24} className="text-red-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-red-400 font-medium">{spotifyPlayError}</p>
                    <p className="text-red-400/70 text-sm mt-1">Open Spotify on your phone, computer, or web player, then try again.</p>
                  </div>
                  <button 
                    onClick={() => {
                      setSpotifyPlayError(null);
                      fetchSpotifyDevices();
                    }}
                    className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-medium"
                  >
                    Refresh Devices
                  </button>
                  <button 
                    onClick={() => setSpotifyPlayError(null)}
                    className="p-1 hover:bg-white/10 rounded-full"
                  >
                    <X size={18} className="text-red-400" />
                  </button>
                </div>
              )}

              {/* Device Selector Modal */}
              {showDeviceSelector && (
                <div className="mb-6 p-4 bg-gray-900/80 border border-white/30 rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Music size={20} className="text-white" />
                      Select Playback Device
                    </h3>
                    <button 
                      onClick={() => {
                        setShowDeviceSelector(false);
                        fetchSpotifyDevices();
                      }}
                      className="p-1 hover:bg-white/10 rounded-full flex items-center gap-1 text-gray-400 hover:text-white text-sm"
                    >
                      <RefreshCw size={14} />
                      Refresh
                    </button>
                  </div>
                  {spotifyDevices.length > 0 ? (
                    <div className="space-y-2">
                      {spotifyDevices.map((device) => (
                        <button
                          key={device.id}
                          onClick={() => transferToDevice(device.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                            device.is_active 
                              ? 'bg-white/20 border border-white/50' 
                              : 'bg-gray-800 hover:bg-gray-700 border border-transparent'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${device.is_active ? 'bg-green-500' : 'bg-gray-700'}`}>
                            {device.type === 'Computer' ? (
                              <Settings size={20} className="text-white" />
                            ) : device.type === 'Smartphone' ? (
                              <Music size={20} className="text-white" />
                            ) : (
                              <Tv size={20} className="text-white" />
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <p className={`font-medium ${device.is_active ? 'text-white' : 'text-white'}`}>{device.name}</p>
                            <p className="text-gray-400 text-sm">{device.type} {device.is_active && '• Currently Active'}</p>
                          </div>
                          {device.is_active && (
                            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Music size={40} className="mx-auto text-gray-600 mb-3" />
                      <p className="text-gray-400">No devices found</p>
                      <p className="text-gray-500 text-sm mt-1">Open Spotify on any device to play music</p>
                    </div>
                  )}
                  <button 
                    onClick={() => setShowDeviceSelector(false)}
                    className="mt-4 w-full py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                  >
                    Close
                  </button>
                </div>
              )}

              {/* Sub-tabs Navigation + Search Bar - ALWAYS at top */}
              <div className="flex items-center justify-between mb-6 gap-4 sticky top-0 z-10 bg-black/80 backdrop-blur-sm py-2 -mx-4 px-4">
                {/* Sub-tabs on the left */}
                <div className="flex items-center gap-1 bg-black border border-white/20 p-1 rounded-xl" data-testid="spotify-subtabs">
                  {[
                    { id: 'library', label: 'Home', icon: Heart },
                    { id: 'all-music', label: 'Library', icon: Music },
                    { id: 'store', label: 'Store', icon: TrendingUp },
                    { id: 'playlists', label: 'Playlists', icon: ListVideo },
                    { id: 'artists', label: 'Artists', icon: Users },
                    { id: 'albums', label: 'Albums', icon: Music }
                  ].map(tab => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setSpotifyMusicTab(tab.id)}
                        data-testid={`spotify-tab-${tab.id}`}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                          spotifyMusicTab === tab.id
                            ? 'bg-white text-black'
                            : 'text-gray-400 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <Icon size={16} />
                        <span className="hidden sm:inline">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Search Bar on the right - Always visible */}
                <div className="relative w-64 md:w-80">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search artists, songs..."
                    value={spotifySearchQuery}
                    onChange={(e) => setSpotifySearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 bg-black border border-white/20 rounded-full text-sm text-white placeholder-gray-500 focus:outline-none focus:border-white/50 hover:border-white/40 transition-colors"
                  />
                  {spotifySearchQuery && (
                    <button 
                      onClick={() => setSpotifySearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-full"
                    >
                      <X size={14} className="text-gray-400" />
                    </button>
                  )}
                </div>
              </div>

              {/* Search Results - Show BELOW tabs when searching */}
              {spotifySearchQuery && spotifySearchResults.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4">Search Results for &ldquo;{spotifySearchQuery}&rdquo;</h3>
                  <div className="bg-black border border-white/20 rounded-2xl overflow-hidden">
                    {spotifySearchResults.slice(0, 10).map((track, idx) => (
                      <div 
                        key={track.id}
                        onClick={() => handleTrackPlayPause(track.uri)}
                        className={`flex items-center gap-4 p-4 hover:bg-white/10 hover:border-white/30 cursor-pointer transition-colors ${idx !== 0 ? 'border-t border-white/5' : ''}`}
                      >
                        <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                          {track.album?.images?.[0]?.url ? (
                            <img src={track.album.images[0].url} alt={track.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                              <Music size={20} className="text-gray-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className={`font-medium truncate ${isTrackPlaying(track.uri) ? 'text-green-400' : 'text-white'}`}>{track.name}</h4>
                          <p className="text-gray-400 text-sm truncate">{track.artists?.map(a => a.name).join(', ')}</p>
                        </div>
                        <span className="text-gray-500 text-sm">{formatDurationMs(track.duration_ms)}</span>
                        <button className={`p-2 rounded-full transition-colors ${isTrackPlaying(track.uri) ? 'bg-green-500 hover:bg-green-400' : 'hover:bg-white/20'}`}>
                          {isTrackPlaying(track.uri) ? (
                            <Pause size={20} className="text-white" fill="currentColor" />
                          ) : (
                            <Play size={20} className="text-white" fill="currentColor" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Main Content - Only show when not searching */}
              {!spotifySearchQuery && (
                <>

                  {/* Library Tab - Liked Songs / Top Tracks */}
                  {spotifyMusicTab === 'library' && (
                    <div className="space-y-8">
                      {/* Recently Played */}
                      <section>
                        <h3 className="text-xl font-bold text-white mb-4">Recently Played</h3>
                        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                          {spotifyRecentlyPlayed.slice(0, 10).map((item, idx) => (
                            <div 
                              key={`${item.track?.id}-${idx}`}
                              className="flex-shrink-0 w-40 cursor-pointer group"
                            >
                              <div 
                                onClick={() => item.track && handleTrackPlayPause(item.track.uri)}
                                className="relative aspect-square rounded-lg overflow-hidden mb-3"
                              >
                                {item.track?.album?.images?.[0]?.url ? (
                                  <img 
                                    src={item.track.album.images[0].url} 
                                    alt={item.track.name} 
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                    <Music size={32} className="text-gray-600" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isTrackPlaying(item.track?.uri) ? 'bg-green-400' : 'bg-green-500'}`}>
                                    {isTrackPlaying(item.track?.uri) ? (
                                      <Pause size={24} className="text-white" fill="white" />
                                    ) : (
                                      <Play size={24} className="text-white ml-1" fill="white" />
                                    )}
                                  </div>
                                </div>
                                {isTrackPlaying(item.track?.uri) && (
                                  <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-green-500 px-2 py-1 rounded-full">
                                    <div className="flex items-center gap-0.5">
                                      <div className="w-0.5 h-2 bg-white rounded-full animate-pulse" />
                                      <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                                      <div className="w-0.5 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                                    </div>
                                  </div>
                                )}
                              </div>
                              <h4 
                                onClick={() => item.track && handleTrackPlayPause(item.track.uri)}
                                className={`text-sm font-medium truncate hover:underline ${isTrackPlaying(item.track?.uri) ? 'text-green-400' : 'text-white'}`}
                              >
                                {item.track?.name}
                              </h4>
                              <div className="text-xs truncate">
                                {item.track?.artists?.map((artist, i) => (
                                  <span key={artist.id}>
                                    <span 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openArtistPage(artist.id, artist.name);
                                      }}
                                      className="text-gray-400 hover:text-white hover:underline cursor-pointer transition-colors"
                                    >
                                      {artist.name}
                                    </span>
                                    {i < item.track.artists.length - 1 && <span className="text-gray-500">, </span>}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </section>

                      {/* Top Tracks */}
                      {spotifyTopTracks.length > 0 && (
                        <section>
                          <h3 className="text-xl font-bold text-white mb-4">Your Top Tracks</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {spotifyTopTracks.slice(0, 10).map((track, idx) => (
                              <div 
                                key={track.id}
                                className={`flex items-center gap-4 p-3 bg-black border rounded-xl hover:bg-white/10 cursor-pointer transition-colors ${isTrackPlaying(track.uri) ? 'border-green-500/50 bg-green-500/10' : 'border-white/20 hover:border-white/30'}`}
                              >
                                <span className={`font-bold text-lg w-6 ${isTrackPlaying(track.uri) ? 'text-green-400' : 'text-white'}`}>{idx + 1}</span>
                                <div 
                                  onClick={() => handleTrackPlayPause(track.uri)}
                                  className="w-14 h-14 rounded overflow-hidden flex-shrink-0 relative group"
                                >
                                  {track.album?.images?.[0]?.url ? (
                                    <img src={track.album.images[0].url} alt={track.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                      <Music size={24} className="text-gray-600" />
                                    </div>
                                  )}
                                  {isTrackPlaying(track.uri) && (
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                      <div className="flex items-center gap-0.5">
                                        <div className="w-0.5 h-3 bg-green-400 rounded-full animate-pulse" />
                                        <div className="w-0.5 h-4 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                                        <div className="w-0.5 h-3 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 
                                    onClick={() => handleTrackPlayPause(track.uri)}
                                    className={`font-medium truncate hover:underline ${isTrackPlaying(track.uri) ? 'text-green-400' : 'text-white'}`}
                                  >
                                    {track.name}
                                  </h4>
                                  <div className="text-sm truncate">
                                    {track.artists?.map((artist, i) => (
                                      <span key={artist.id}>
                                        <span 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openArtistPage(artist.id, artist.name);
                                          }}
                                          className="text-gray-400 hover:text-white hover:underline cursor-pointer transition-colors"
                                        >
                                          {artist.name}
                                        </span>
                                        {i < track.artists.length - 1 && <span className="text-gray-500">, </span>}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleTrackPlayPause(track.uri)}
                                  className={`p-2 rounded-full flex-shrink-0 transition-all ${isTrackPlaying(track.uri) ? 'bg-green-500 hover:bg-green-400' : 'hover:bg-white/20'}`}
                                >
                                  {isTrackPlaying(track.uri) ? (
                                    <Pause size={20} className="text-white" fill="currentColor" />
                                  ) : (
                                    <Play size={20} className="text-white" fill="currentColor" />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        </section>
                      )}

                      {/* Liked Songs Section - Now at the bottom */}
                      <section>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded flex items-center justify-center">
                            <Heart size={20} className="text-white" fill="white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white">Liked Songs</h3>
                            <p className="text-gray-400 text-sm">{spotifySavedTracks.length} songs</p>
                          </div>
                        </div>
                        <div className="bg-black border border-white/20 rounded-2xl overflow-hidden" style={{ maxHeight: '594px', overflowY: 'auto' }}>
                          {spotifySavedTracks.length > 0 ? (
                            spotifySavedTracks.map((item, idx) => (
                              <div 
                                key={`${item.track?.id}-${idx}`}
                                data-testid={`liked-song-${idx}`}
                                className={`flex items-center gap-4 p-4 hover:bg-white/10 hover:border-white/30 cursor-pointer transition-colors ${idx !== 0 ? 'border-t border-white/5' : ''}`}
                              >
                                <span className="text-gray-500 text-sm w-6 text-center">{idx + 1}</span>
                                <div 
                                  onClick={() => item.track && playSpotifyTrack(item.track.uri)}
                                  className="w-12 h-12 rounded overflow-hidden flex-shrink-0"
                                >
                                  {item.track?.album?.images?.[0]?.url ? (
                                    <img src={item.track.album.images[0].url} alt={item.track?.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                      <Music size={20} className="text-gray-600" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 
                                    onClick={() => item.track && playSpotifyTrack(item.track.uri)}
                                    className="text-white font-medium truncate hover:underline cursor-pointer"
                                  >
                                    {item.track?.name}
                                  </h4>
                                  <div className="text-sm truncate">
                                    {item.track?.artists?.map((artist, i) => (
                                      <span key={artist.id}>
                                        <span 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openArtistPage(artist.id, artist.name);
                                          }}
                                          className="text-gray-400 hover:text-white hover:underline cursor-pointer transition-colors"
                                        >
                                          {artist.name}
                                        </span>
                                        {i < item.track.artists.length - 1 && <span className="text-gray-500">, </span>}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <span className="text-gray-500 text-sm hidden sm:block">{item.track?.album?.name}</span>
                                <span className="text-gray-500 text-sm">{formatDurationMs(item.track?.duration_ms)}</span>
                                <button 
                                  onClick={() => item.track && playSpotifyTrack(item.track.uri)}
                                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                >
                                  <Play size={18} className="text-white" fill="currentColor" />
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="p-12 text-center text-gray-400">
                              <Heart size={48} className="mx-auto mb-4 text-gray-600" />
                              <p>No liked songs yet</p>
                              <p className="text-sm mt-2">Songs you like will appear here</p>
                            </div>
                          )}
                        </div>
                      </section>
                    </div>
                  )}

                  {/* All Music Library Tab - Song list with playlist sidebar */}
                  {spotifyMusicTab === 'all-music' && (
                    <div className="flex gap-6 min-h-[calc(100vh-280px)]">
                      {/* Main Song List */}
                      <div className="flex-1 flex flex-col min-w-0">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-baseline gap-3">
                            <h1 className="text-4xl font-bold text-white tracking-tight">
                              {allMusicPlaylist?.name || 'ALL MUSIC'}
                            </h1>
                            <span className="text-gray-400 text-lg">
                              • {allMusicTracks.length} of {allMusicTotal} songs (Page {allMusicPage}/{Math.ceil(allMusicTotal / MUSIC_PAGE_SIZE) || 1})
                              {allMusicLoadingMore && (
                                <span className="ml-2 text-white text-sm">
                                  <Loader2 size={14} className="inline animate-spin mr-1" />
                                  Loading more...
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="relative">
                              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                              <input
                                type="text"
                                placeholder="Search in playlist..."
                                value={allMusicSearch}
                                onChange={(e) => setAllMusicSearch(e.target.value)}
                                className="pl-10 pr-4 py-2.5 bg-gray-900/80 border border-gray-800 rounded-lg text-white text-sm w-64 focus:outline-none focus:border-gray-600"
                              />
                            </div>
                            <button 
                              onClick={() => {
                                if (allMusicPlaylist) {
                                  loadPlaylistTracks(allMusicPlaylist, true, allMusicPage);
                                }
                              }}
                              className="p-2 text-gray-400 hover:text-white transition-colors"
                            >
                              <X size={20} />
                            </button>
                          </div>
                        </div>

                        {/* Column Headers - Clickable for sorting */}
                        <div 
                          className="grid gap-4 px-4 py-3 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-800"
                          style={{ gridTemplateColumns: '40px 2fr 1.5fr 80px 100px 50px 60px 100px' }}
                        >
                          <div 
                            onClick={() => {
                              if (librarySortColumn === 'index') {
                                setLibrarySortDirection(d => d === 'asc' ? 'desc' : 'asc');
                              } else {
                                setLibrarySortColumn('index');
                                setLibrarySortDirection('asc');
                              }
                            }}
                            className="flex items-center gap-1 cursor-pointer hover:text-white transition-colors"
                            title={librarySortColumn === 'index' ? 'Drag rows to reorder' : 'Sort by order (enables drag reorder)'}
                          >
                            <span>#</span>
                            {librarySortColumn === 'index' && (
                              <>
                                <ChevronUp size={12} className={`text-white transition-transform ${librarySortDirection === 'desc' ? 'rotate-180' : ''}`} />
                                <span className="text-white/60 text-[9px] ml-1">(drag)</span>
                              </>
                            )}
                          </div>
                          <div 
                            onClick={() => {
                              if (librarySortColumn === 'title') {
                                setLibrarySortDirection(d => d === 'asc' ? 'desc' : 'asc');
                              } else {
                                setLibrarySortColumn('title');
                                setLibrarySortDirection('asc');
                              }
                            }}
                            className="flex items-center gap-1 cursor-pointer hover:text-white transition-colors"
                          >
                            <span>Title</span>
                            {librarySortColumn === 'title' && (
                              <ChevronUp size={12} className={`text-white transition-transform ${librarySortDirection === 'desc' ? 'rotate-180' : ''}`} />
                            )}
                          </div>
                          <div 
                            onClick={() => {
                              if (librarySortColumn === 'album') {
                                setLibrarySortDirection(d => d === 'asc' ? 'desc' : 'asc');
                              } else {
                                setLibrarySortColumn('album');
                                setLibrarySortDirection('asc');
                              }
                            }}
                            className="flex items-center gap-1 cursor-pointer hover:text-white transition-colors"
                          >
                            <span>Album</span>
                            {librarySortColumn === 'album' && (
                              <ChevronUp size={12} className={`text-white transition-transform ${librarySortDirection === 'desc' ? 'rotate-180' : ''}`} />
                            )}
                          </div>
                          <div className="relative group">
                            <div 
                              className={`flex items-center gap-1 cursor-pointer hover:text-white transition-colors ${libraryFilterGenre ? 'text-white' : ''}`}
                            >
                              <span>Genre</span>
                              {libraryFilterGenre && <span className="text-white text-[10px]">●</span>}
                            </div>
                          </div>
                          <div className="relative group">
                            <div 
                              className="flex items-center gap-1 cursor-default"
                            >
                              <span>PLAYS</span>
                            </div>
                          </div>
                          <div>BPM</div>
                          <div 
                            onClick={() => {
                              if (librarySortColumn === 'duration') {
                                setLibrarySortDirection(d => d === 'asc' ? 'desc' : 'asc');
                              } else {
                                setLibrarySortColumn('duration');
                                setLibrarySortDirection('asc');
                              }
                            }}
                            className="flex items-center gap-1 cursor-pointer hover:text-white transition-colors"
                          >
                            <span>Duration</span>
                            {librarySortColumn === 'duration' && (
                              <ChevronUp size={12} className={`text-white transition-transform ${librarySortDirection === 'desc' ? 'rotate-180' : ''}`} />
                            )}
                          </div>
                          <div 
                            onClick={() => {
                              if (librarySortColumn === 'dateAdded') {
                                setLibrarySortDirection(d => d === 'asc' ? 'desc' : 'asc');
                              } else {
                                setLibrarySortColumn('dateAdded');
                                setLibrarySortDirection('desc');
                              }
                            }}
                            className="flex items-center gap-1 cursor-pointer hover:text-white transition-colors whitespace-nowrap"
                          >
                            <span>Date Added</span>
                            {librarySortColumn === 'dateAdded' && (
                              <ChevronUp size={12} className={`text-white transition-transform ${librarySortDirection === 'desc' ? 'rotate-180' : ''}`} />
                            )}
                          </div>
                        </div>

                        {/* Filter Bar - Shows when filters are active */}
                        {libraryFilterGenre && (
                          <div className="flex items-center gap-2 px-4 py-2 bg-black border border-white/20 border-b border-gray-800">
                            <span className="text-gray-500 text-xs">Filters:</span>
                            <button 
                              onClick={() => setLibraryFilterGenre('')}
                              className="flex items-center gap-1 px-2 py-1 bg-white/20 text-white rounded text-xs"
                            >
                              Genre: {libraryFilterGenre}
                              <X size={12} />
                            </button>
                            <button 
                              onClick={() => setLibraryFilterGenre('')}
                              className="text-gray-500 text-xs hover:text-white ml-2"
                            >
                              Clear all
                            </button>
                          </div>
                        )}

                        {/* Song List */}
                        <div className="flex-1 overflow-y-scroll max-h-[calc(100vh-280px)] scrollbar-visible">
                          {allMusicLoading ? (
                            <div className="flex items-center justify-center py-20">
                              <Loader2 size={32} className="animate-spin text-white" />
                            </div>
                          ) : allMusicTracks.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                              <Music size={48} className="mb-4 text-gray-600" />
                              <p className="text-lg">No songs in this playlist</p>
                              <p className="text-sm mt-2">Select a playlist from the sidebar to view its songs</p>
                            </div>
                          ) : (
                            sortedLibraryTracks.map((item, displayIdx) => {
                                const track = item.track;
                                if (!track) return null;
                                
                                // Use originalIdx for drag operations (actual position in allMusicTracks)
                                const actualIndex = item.originalIdx;
                                
                                const isCurrentTrack = spotifyCurrentTrack?.uri === track.uri;
                                const dateAdded = item.added_at ? new Date(item.added_at).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                }) : '—';
                                
                                // Check if this row is being dragged or hovered - use actualIndex for tracking
                                const isDragging = libraryDragIndex === actualIndex;
                                const isDragOver = libraryDragOverIndex === actualIndex && libraryDragIndex !== actualIndex;
                                const canReorder = librarySortColumn === 'index' && !allMusicSearch.trim();
                                
                                return (
                                  <div
                                    key={`${track.id}-${actualIndex}`}
                                    draggable={canReorder}
                                    onDragStart={(e) => canReorder && handleLibraryDragStart(e, item, actualIndex)}
                                    onDragEnd={handleLibraryDragEnd}
                                    onDragOver={(e) => canReorder && handleLibraryDragOver(e, actualIndex)}
                                    onDragLeave={handleLibraryDragLeave}
                                    onDrop={(e) => canReorder && handleLibraryDrop(e, actualIndex)}
                                    onClick={() => handleTrackPlayPause(track.uri, null, sortedLibraryTracks, displayIdx)}
                                    className={`grid gap-4 px-4 py-1.5 cursor-pointer transition-all border-b group ${
                                      isCurrentTrack ? 'bg-green-500/10' : ''
                                    } ${isDragging ? 'opacity-50 bg-gray-800' : ''} ${
                                      isDragOver ? 'border-t-2 border-t-white bg-white/5' : 'border-gray-900/50'
                                    } ${canReorder ? 'cursor-grab active:cursor-grabbing' : ''} hover:bg-black border border-white/10`}
                                    style={{ gridTemplateColumns: '40px 2fr 1.5fr 80px 100px 50px 60px 100px' }}
                                  >
                                    {/* # with drag handle when sorted by index */}
                                    <div className="flex items-center gap-1">
                                      {canReorder && (
                                        <div className="text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
                                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                            <circle cx="5" cy="5" r="2"/>
                                            <circle cx="12" cy="5" r="2"/>
                                            <circle cx="5" cy="12" r="2"/>
                                            <circle cx="12" cy="12" r="2"/>
                                            <circle cx="5" cy="19" r="2"/>
                                            <circle cx="12" cy="19" r="2"/>
                                          </svg>
                                        </div>
                                      )}
                                      {isTrackPlaying(track.uri) ? (
                                        <div className="flex items-center gap-0.5">
                                          <div className="w-0.5 h-3 bg-green-400 rounded-full animate-pulse" />
                                          <div className="w-0.5 h-4 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                                          <div className="w-0.5 h-3 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                                        </div>
                                      ) : isCurrentTrack ? (
                                        <Play size={14} className="text-green-400" fill="currentColor" />
                                      ) : (
                                        <span className="text-sm text-gray-500 group-hover:hidden">
                                          {displayIdx + 1}
                                        </span>
                                      )}
                                      {!isTrackPlaying(track.uri) && !isCurrentTrack && (
                                        <Play size={14} className="text-white hidden group-hover:block" fill="currentColor" />
                                      )}
                                    </div>
                                    
                                    {/* Title + Artist */}
                                    <div className="flex flex-col justify-center min-w-0 leading-tight">
                                      <span className={`text-sm font-medium truncate ${isTrackPlaying(track.uri) ? 'text-green-400' : 'text-white'}`}>
                                        {track.name}
                                      </span>
                                      <div className="text-xs truncate">
                                        {track.artists?.map((artist, i) => (
                                          <span key={artist.id}>
                                            <span 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openArtistPage(artist.id, artist.name);
                                              }}
                                              className="text-gray-400 hover:text-white hover:underline cursor-pointer transition-colors"
                                            >
                                              {artist.name}
                                            </span>
                                            {i < track.artists.length - 1 && <span className="text-gray-500">, </span>}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                    
                                    {/* Album */}
                                    <div className="flex items-center">
                                      <span className="text-gray-400 text-xs truncate">{track.album?.name}</span>
                                    </div>
                                    
                                    {/* Genre - Derived from album type or explicit flag */}
                                    <div className="flex items-center">
                                      {(() => {
                                        // Try to derive genre from various signals
                                        const albumType = track.album?.album_type;
                                        const isExplicit = track.explicit;
                                        let genre = '—';
                                        
                                        // Simple genre inference based on available data
                                        if (albumType === 'single') genre = 'Single';
                                        else if (albumType === 'compilation') genre = 'Compilation';
                                        else if (track.album?.name?.toLowerCase().includes('soundtrack')) genre = 'Soundtrack';
                                        else if (track.album?.name?.toLowerCase().includes('acoustic')) genre = 'Acoustic';
                                        else if (track.album?.name?.toLowerCase().includes('live')) genre = 'Live';
                                        else if (track.album?.name?.toLowerCase().includes('remix')) genre = 'Remix';
                                        else if (isExplicit) genre = 'Hip-Hop/Rap';
                                        else genre = 'Pop';
                                        
                                        return (
                                          <span 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setLibraryFilterGenre(libraryFilterGenre === genre ? '' : genre);
                                            }}
                                            className="text-gray-400 text-xs cursor-pointer hover:text-white transition-colors"
                                          >
                                            {genre}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                    
                                    {/* Plays - Real stream count from Spotify81 API */}
                                    <div className="flex items-center">
                                      <span className="text-gray-400 text-xs">
                                        {formatPlayCount(track)}
                                      </span>
                                    </div>
                                    
                                    {/* BPM - Estimated from tempo */}
                                    <div className="flex items-center">
                                      <span className="text-gray-500 text-xs">
                                        {Math.floor(80 + (track.duration_ms % 60))}
                                      </span>
                                    </div>
                                    
                                    {/* Duration */}
                                    <div className="flex items-center">
                                      <span className="text-gray-400 text-xs">{formatDurationMs(track.duration_ms)}</span>
                                    </div>
                                    
                                    {/* Date Added */}
                                    <div className="flex items-center">
                                      <span className="text-gray-500 text-xs">{dateAdded}</span>
                                    </div>
                                  </div>
                                );
                              })
                          )}
                        </div>
                        
                        {/* Pagination Controls */}
                        {allMusicTotal > MUSIC_PAGE_SIZE && (
                          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-800 bg-black/50">
                            <span className="text-gray-500 text-sm">
                              Showing {((allMusicPage - 1) * MUSIC_PAGE_SIZE) + 1}-{Math.min(allMusicPage * MUSIC_PAGE_SIZE, allMusicTotal)} of {allMusicTotal}
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => allMusicPlaylist && loadPlaylistTracks(allMusicPlaylist, false, allMusicPage - 1)}
                                disabled={allMusicPage <= 1 || allMusicLoading}
                                className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
                                data-testid="music-prev-page"
                              >
                                Previous
                              </button>
                              {Array.from({ length: Math.ceil(allMusicTotal / MUSIC_PAGE_SIZE) }, (_, i) => i + 1)
                                .filter(p => Math.abs(p - allMusicPage) <= 2 || p === 1 || p === Math.ceil(allMusicTotal / MUSIC_PAGE_SIZE))
                                .map((p, idx, arr) => (
                                  <React.Fragment key={p}>
                                    {idx > 0 && arr[idx - 1] !== p - 1 && <span className="text-gray-600">...</span>}
                                    <button
                                      onClick={() => allMusicPlaylist && loadPlaylistTracks(allMusicPlaylist, false, p)}
                                      disabled={allMusicLoading}
                                      className={`w-8 h-8 rounded-lg text-sm transition-colors ${
                                        p === allMusicPage ? 'bg-white text-black font-bold' : 'bg-gray-800 hover:bg-gray-700 text-gray-400'
                                      }`}
                                    >
                                      {p}
                                    </button>
                                  </React.Fragment>
                                ))}
                              <button
                                onClick={() => allMusicPlaylist && loadPlaylistTracks(allMusicPlaylist, false, allMusicPage + 1)}
                                disabled={allMusicPage >= Math.ceil(allMusicTotal / MUSIC_PAGE_SIZE) || allMusicLoading}
                                className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
                                data-testid="music-next-page"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="w-64 flex-shrink-0 bg-black border border-white/10 rounded-2xl p-4 overflow-y-scroll max-h-[calc(100vh-200px)] scrollbar-visible">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-white font-bold text-sm uppercase tracking-wider">
                            Your Playlists
                          </h3>
                          <button
                            onClick={() => setShowCreateSpotifyPlaylist(true)}
                            className="p-1.5 bg-white/20 hover:bg-green-500/30 rounded-lg transition-colors"
                            title="Create new playlist"
                          >
                            <Plus size={16} className="text-white" />
                          </button>
                        </div>
                        
                        {/* Create Playlist Input */}
                        {showCreateSpotifyPlaylist && (
                          <div className="mb-4 p-3 bg-gray-800 rounded-xl">
                            <input
                              type="text"
                              placeholder="New playlist name..."
                              value={newSpotifyPlaylistName}
                              onChange={(e) => setNewSpotifyPlaylistName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newSpotifyPlaylistName.trim()) {
                                  handleCreateSpotifyPlaylist();
                                } else if (e.key === 'Escape') {
                                  setShowCreateSpotifyPlaylist(false);
                                  setNewSpotifyPlaylistName('');
                                }
                              }}
                              className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-white/50 mb-2"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={handleCreateSpotifyPlaylist}
                                disabled={!newSpotifyPlaylistName.trim() || creatingPlaylist}
                                className="flex-1 px-3 py-1.5 bg-green-500 hover:bg-green-400 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
                              >
                                {creatingPlaylist ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <Plus size={14} />
                                )}
                                Create
                              </button>
                              <button
                                onClick={() => {
                                  setShowCreateSpotifyPlaylist(false);
                                  setNewSpotifyPlaylistName('');
                                }}
                                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 text-sm transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                        
                        <p className="text-gray-500 text-xs mb-4">
                          Click to view • Drag songs to add
                        </p>
                        <div className="space-y-2">
                          {spotifyPlaylists
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((playlist) => (
                              <div
                                key={playlist.id}
                                onClick={() => loadPlaylistTracks(playlist)}
                                onDragOver={(e) => handlePlaylistDragOver(e, playlist.id)}
                                onDragLeave={() => setDragOverPlaylist(null)}
                                onDrop={(e) => handlePlaylistDrop(e, playlist.id)}
                                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                                  dragOverPlaylist === playlist.id 
                                    ? 'bg-white/20 ring-2 ring-white scale-[1.02]' 
                                    : allMusicPlaylist?.id === playlist.id
                                      ? 'bg-white/10 ring-1 ring-white/50'
                                      : 'bg-gray-800/50 hover:bg-gray-800'
                                }`}
                              >
                                <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0">
                                  {playlist.images?.[0]?.url ? (
                                    <img 
                                      src={playlist.images[0].url} 
                                      alt={playlist.name} 
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-green-600 to-green-900 flex items-center justify-center">
                                      <Music size={16} className="text-white/50" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${allMusicPlaylist?.id === playlist.id ? 'text-white' : 'text-white'}`}>{playlist.name}</p>
                                  <p className="text-gray-500 text-xs">{playlist.tracks?.total || 0} songs</p>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Store Tab - Search, New Releases & Browse */}
                  {spotifyMusicTab === 'store' && (
                    <div className="space-y-6">
                      {/* Search Bar */}
                      <div className="relative">
                        <div className="flex items-center gap-3 bg-white/10 rounded-full px-4 py-3 focus-within:ring-2 focus-within:ring-green-500 transition-all">
                          <Search size={20} className="text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search for songs, albums, or artists..."
                            value={storeSearchQuery}
                            onChange={(e) => {
                              setStoreSearchQuery(e.target.value);
                              if (e.target.value.length > 2) {
                                searchSpotifyStore(e.target.value);
                              } else if (e.target.value.length === 0) {
                                setStoreSearchResults({ tracks: [], albums: [], artists: [] });
                                setSpotifyArtistDetails(null);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && storeSearchQuery.trim()) {
                                searchSpotifyStore(storeSearchQuery);
                              }
                            }}
                            data-testid="store-search-input"
                            className="flex-1 bg-transparent outline-none text-white placeholder-gray-400"
                          />
                          {storeSearchQuery && (
                            <button 
                              onClick={() => {
                                setStoreSearchQuery('');
                                setStoreSearchResults({ tracks: [], albums: [], artists: [] });
                                setSpotifyArtistDetails(null);
                              }}
                              className="p-1 hover:bg-white/10 rounded-full"
                            >
                              <X size={16} className="text-gray-400" />
                            </button>
                          )}
                          {storeSearchLoading && <Loader2 size={20} className="animate-spin text-green-500" />}
                        </div>
                      </div>

                      {/* Search Results */}
                      {storeSearchQuery && (storeSearchResults.tracks.length > 0 || storeSearchResults.albums.length > 0 || storeSearchResults.artists.length > 0) && !spotifyArtistDetails && (
                        <div className="space-y-6">
                          {/* Search Tabs */}
                          <div className="flex gap-2">
                            {['all', 'tracks', 'albums', 'artists'].map((tab) => (
                              <button
                                key={tab}
                                onClick={() => setStoreSearchTab(tab)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                  storeSearchTab === tab
                                    ? 'bg-white text-black'
                                    : 'bg-white/10 text-white hover:bg-white/20'
                                }`}
                              >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                              </button>
                            ))}
                          </div>

                          {/* Artists Results */}
                          {(storeSearchTab === 'all' || storeSearchTab === 'artists') && storeSearchResults.artists.length > 0 && (
                            <section>
                              <h3 className="text-lg font-bold text-white mb-4">Artists</h3>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                {storeSearchResults.artists.slice(0, storeSearchTab === 'artists' ? 18 : 6).map((artist) => (
                                  <div
                                    key={artist.id}
                                    onClick={() => openArtistPage(artist.id, artist.name)}
                                    className="cursor-pointer group text-center"
                                    data-testid={`search-artist-${artist.id}`}
                                  >
                                    <div className="relative aspect-square rounded-full overflow-hidden mb-3 mx-auto w-32 h-32">
                                      {artist.images?.[0]?.url ? (
                                        <img 
                                          src={artist.images[0].url} 
                                          alt={artist.name} 
                                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                        />
                                      ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                                          <Users size={32} className="text-white/50" />
                                        </div>
                                      )}
                                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                        <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                          <Play size={24} className="text-white ml-1" fill="white" />
                                        </div>
                                      </div>
                                    </div>
                                    <h4 className="text-white text-sm font-medium truncate">{artist.name}</h4>
                                    <p className="text-gray-400 text-xs">{artist.followers?.toLocaleString() || 0} followers</p>
                                  </div>
                                ))}
                              </div>
                            </section>
                          )}

                          {/* Albums Results */}
                          {(storeSearchTab === 'all' || storeSearchTab === 'albums') && storeSearchResults.albums.length > 0 && (
                            <section>
                              <h3 className="text-lg font-bold text-white mb-4">Albums</h3>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                {storeSearchResults.albums.slice(0, storeSearchTab === 'albums' ? 18 : 6).map((album) => (
                                  <div
                                    key={album.id}
                                    onClick={() => {
                                      setSelectedSpotifyAlbum(album);
                                      fetchSpotifyAlbumTracks(album.id);
                                    }}
                                    className="cursor-pointer group"
                                    data-testid={`search-album-${album.id}`}
                                  >
                                    <div className="relative aspect-square rounded-lg overflow-hidden mb-3">
                                      {album.images?.[0]?.url ? (
                                        <img 
                                          src={album.images[0].url} 
                                          alt={album.name} 
                                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                        />
                                      ) : (
                                        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                          <Music size={32} className="text-gray-600" />
                                        </div>
                                      )}
                                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                        <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                          <Play size={24} className="text-white ml-1" fill="white" />
                                        </div>
                                      </div>
                                    </div>
                                    <h4 className="text-white text-sm font-medium truncate">{album.name}</h4>
                                    <p className="text-gray-400 text-xs truncate">{album.artists?.map(a => a.name).join(', ')}</p>
                                    <p className="text-gray-500 text-xs">{album.release_date?.split('-')[0]} • {album.total_tracks} tracks</p>
                                  </div>
                                ))}
                              </div>
                            </section>
                          )}

                          {/* Tracks Results */}
                          {(storeSearchTab === 'all' || storeSearchTab === 'tracks') && storeSearchResults.tracks.length > 0 && (
                            <section>
                              <h3 className="text-lg font-bold text-white mb-4">Songs</h3>
                              <div className="bg-black/30 rounded-xl overflow-hidden">
                                {storeSearchResults.tracks.slice(0, storeSearchTab === 'tracks' ? 20 : 5).map((track, idx) => (
                                  <div
                                    key={track.id}
                                    onClick={() => playSpotifyTrack(track.uri)}
                                    className={`flex items-center gap-4 p-3 hover:bg-white/10 cursor-pointer transition-colors ${idx !== 0 ? 'border-t border-white/5' : ''}`}
                                    data-testid={`search-track-${track.id}`}
                                  >
                                    <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 relative group">
                                      {track.album?.images?.[0]?.url ? (
                                        <img src={track.album.images[0].url} alt={track.name} className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                          <Music size={20} className="text-gray-600" />
                                        </div>
                                      )}
                                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                        <Play size={16} className="text-white" fill="white" />
                                      </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-white font-medium truncate">{track.name}</h4>
                                      <p className="text-gray-400 text-sm truncate">{track.artists?.map(a => a.name).join(', ')}</p>
                                    </div>
                                    <span className="text-gray-500 text-sm">{formatDurationMs(track.duration_ms)}</span>
                                  </div>
                                ))}
                              </div>
                            </section>
                          )}
                        </div>
                      )}

                      {/* Artist Details View */}
                      {spotifyArtistDetails && (
                        <div className="space-y-6">
                          {/* Back Button */}
                          <button
                            onClick={() => setSpotifyArtistDetails(null)}
                            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                          >
                            <ChevronLeft size={20} />
                            <span>Back to search</span>
                          </button>

                          {/* Artist Header */}
                          <div className="flex items-end gap-6">
                            <div className="w-48 h-48 rounded-full overflow-hidden flex-shrink-0 shadow-2xl">
                              {spotifyArtistDetails.images?.[0]?.url ? (
                                <img 
                                  src={spotifyArtistDetails.images[0].url} 
                                  alt={spotifyArtistDetails.name} 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                                  <Users size={64} className="text-white/50" />
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-gray-400 text-sm mb-1">Artist</p>
                              <h2 className="text-4xl md:text-6xl font-bold text-white mb-2">{spotifyArtistDetails.name}</h2>
                              <p className="text-gray-400">{spotifyArtistDetails.followers?.toLocaleString()} followers</p>
                              {spotifyArtistDetails.genres?.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {spotifyArtistDetails.genres.slice(0, 4).map((genre) => (
                                    <span key={genre} className="px-3 py-1 bg-white/10 rounded-full text-xs text-gray-300 capitalize">
                                      {genre}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Play Button */}
                          <button
                            onClick={() => playSpotifyTrack(null, spotifyArtistDetails.uri || `spotify:artist:${spotifyArtistDetails.id}`)}
                            className="flex items-center gap-2 px-8 py-3 bg-green-500 hover:bg-green-400 rounded-full text-black font-semibold transition-all"
                          >
                            <Play size={24} fill="currentColor" />
                            Play
                          </button>

                          {/* Top Tracks */}
                          {spotifyArtistDetails.top_tracks?.length > 0 && (
                            <section>
                              <h3 className="text-xl font-bold text-white mb-4">Popular</h3>
                              <div className="space-y-1">
                                {spotifyArtistDetails.top_tracks.slice(0, 5).map((track, idx) => (
                                  <div
                                    key={track.id}
                                    onClick={() => handleTrackPlayPause(track.uri)}
                                    className={`flex items-center gap-4 p-3 rounded-lg hover:bg-white/10 cursor-pointer transition-colors group ${isTrackPlaying(track.uri) ? 'bg-green-500/10' : ''}`}
                                  >
                                    {isTrackPlaying(track.uri) ? (
                                      <div className="w-6 flex items-center justify-center">
                                        <div className="flex items-center gap-0.5">
                                          <div className="w-0.5 h-3 bg-green-400 rounded-full animate-pulse" />
                                          <div className="w-0.5 h-4 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                                          <div className="w-0.5 h-3 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <span className="text-gray-500 w-6 text-center group-hover:hidden">{idx + 1}</span>
                                        <Play size={16} className="text-white hidden group-hover:block w-6" fill="white" />
                                      </>
                                    )}
                                    <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                                      {track.album_art ? (
                                        <img src={track.album_art} alt={track.name} className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="w-full h-full bg-gray-800" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className={`font-medium truncate ${isTrackPlaying(track.uri) ? 'text-green-400' : 'text-white'}`}>{track.name}</h4>
                                    </div>
                                    <span className="text-gray-500 text-sm">{formatDurationMs(track.duration_ms)}</span>
                                  </div>
                                ))}
                              </div>
                            </section>
                          )}

                          {/* Discography */}
                          {spotifyArtistDetails.albums?.length > 0 && (
                            <section>
                              <h3 className="text-xl font-bold text-white mb-4">Discography</h3>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                {spotifyArtistDetails.albums.map((album) => (
                                  <div
                                    key={album.id}
                                    onClick={() => {
                                      setSelectedSpotifyAlbum(album);
                                      fetchSpotifyAlbumTracks(album.id);
                                    }}
                                    className="cursor-pointer group"
                                  >
                                    <div className="relative aspect-square rounded-lg overflow-hidden mb-3">
                                      {album.images?.[0]?.url ? (
                                        <img 
                                          src={album.images[0].url} 
                                          alt={album.name} 
                                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                        />
                                      ) : (
                                        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                          <Music size={32} className="text-gray-600" />
                                        </div>
                                      )}
                                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                        <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                          <Play size={24} className="text-white ml-1" fill="white" />
                                        </div>
                                      </div>
                                    </div>
                                    <h4 className="text-white text-sm font-medium truncate">{album.name}</h4>
                                    <p className="text-gray-400 text-xs">{album.release_date?.split('-')[0]} • {album.album_type}</p>
                                  </div>
                                ))}
                              </div>
                            </section>
                          )}

                          {/* Related Artists */}
                          {spotifyArtistDetails.related_artists?.length > 0 && (
                            <section>
                              <h3 className="text-xl font-bold text-white mb-4">Fans also like</h3>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                {spotifyArtistDetails.related_artists.map((artist) => (
                                  <div
                                    key={artist.id}
                                    onClick={() => openArtistPage(artist.id, artist.name)}
                                    className="cursor-pointer group text-center"
                                  >
                                    <div className="relative aspect-square rounded-full overflow-hidden mb-3 mx-auto w-32 h-32">
                                      {artist.image ? (
                                        <img 
                                          src={artist.image} 
                                          alt={artist.name} 
                                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                        />
                                      ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                                          <Users size={32} className="text-white/50" />
                                        </div>
                                      )}
                                    </div>
                                    <h4 className="text-white text-sm font-medium truncate">{artist.name}</h4>
                                    <p className="text-gray-400 text-xs">Artist</p>
                                  </div>
                                ))}
                              </div>
                            </section>
                          )}
                        </div>
                      )}

                      {/* Default Store View (when no search) */}
                      {!storeSearchQuery && !spotifyArtistDetails && (
                        <>
                          {/* New Releases */}
                          <section>
                            <h3 className="text-xl font-bold text-white mb-4">New Releases</h3>
                            {spotifyNewReleases.length > 0 ? (
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                {spotifyNewReleases.slice(0, 12).map((album) => (
                                  <div
                                    key={album.id}
                                    onClick={() => {
                                      setSelectedSpotifyAlbum(album);
                                      fetchSpotifyAlbumTracks(album.id);
                                    }}
                                    className="cursor-pointer group"
                                    data-testid={`new-release-${album.id}`}
                                  >
                                    <div className="relative aspect-square rounded-lg overflow-hidden mb-3">
                                      {album.images?.[0]?.url ? (
                                        <img 
                                          src={album.images[0].url} 
                                          alt={album.name} 
                                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                        />
                                      ) : (
                                        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                          <Music size={32} className="text-gray-600" />
                                        </div>
                                      )}
                                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                        <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                          <Play size={24} className="text-white ml-1" fill="white" />
                                        </div>
                                      </div>
                                    </div>
                                    <h4 className="text-white text-sm font-medium truncate">{album.name}</h4>
                                    <p className="text-gray-400 text-xs truncate">{album.artists?.map(a => a.name).join(', ')}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex items-center justify-center py-12">
                                <Loader2 size={32} className="animate-spin text-white" />
                              </div>
                            )}
                          </section>

                          {/* Browse Categories */}
                          {spotifyCategories.length > 0 && !selectedCategory && (
                            <section>
                              <h3 className="text-xl font-bold text-white mb-4">Browse Categories</h3>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                {spotifyCategories.slice(0, 12).map((category) => (
                                  <div
                                    key={category.id}
                                    onClick={() => fetchCategoryPlaylists(category)}
                                    data-testid={`category-card-${category.id}`}
                                    className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group hover:ring-2 hover:ring-white/50 transition-all"
                                  >
                                    {category.icons?.[0]?.url ? (
                                      <img 
                                        src={category.icons[0].url} 
                                        alt={category.name} 
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                      />
                                    ) : (
                                      <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600" />
                                    )}
                                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <span className="text-white font-bold text-center px-2">{category.name}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </section>
                          )}

                          {/* Category Playlists View */}
                          {selectedCategory && (
                            <section data-testid="category-playlists-view">
                              {/* Category Header */}
                              <div className="flex items-center gap-4 mb-6">
                                <button
                                  onClick={closeCategoryView}
                                  data-testid="back-from-category"
                                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                                >
                                  <ChevronLeft className="w-5 h-5 text-white" />
                                </button>
                                <div className="flex items-center gap-4">
                                  {selectedCategory.icons?.[0]?.url && (
                                    <img 
                                      src={selectedCategory.icons[0].url} 
                                      alt={selectedCategory.name} 
                                      className="w-16 h-16 rounded-lg object-cover shadow-lg"
                                    />
                                  )}
                                  <div>
                                    <p className="text-white/60 text-xs uppercase tracking-wider">Category</p>
                                    <h3 className="text-3xl font-bold text-white">{selectedCategory.name}</h3>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Popular Playlists Section */}
                              <h4 className="text-xl font-bold text-white mb-4">Popular Playlists</h4>
                              
                              {categoryPlaylistsLoading ? (
                                <div className="flex items-center justify-center py-12">
                                  <Loader2 size={32} className="animate-spin text-white" />
                                </div>
                              ) : categoryPlaylists.length > 0 ? (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                  {categoryPlaylists.map((playlist) => (
                                    <div
                                      key={playlist.id}
                                      onClick={() => {
                                        fetchSpotifyPlaylistTracks(playlist.id);
                                        setSelectedSpotifyPlaylist(playlist);
                                        closeCategoryView();
                                      }}
                                      data-testid={`category-playlist-${playlist.id}`}
                                      className="cursor-pointer group"
                                    >
                                      <div className="relative aspect-square rounded-lg overflow-hidden mb-3">
                                        {playlist.images?.[0]?.url ? (
                                          <img 
                                            src={playlist.images[0].url} 
                                            alt={playlist.name} 
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                          />
                                        ) : (
                                          <div className="w-full h-full bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center">
                                            <Music size={32} className="text-white/60" />
                                          </div>
                                        )}
                                        {/* Play button overlay on hover */}
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                          <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                                            <Play size={24} className="text-white ml-1" fill="white" />
                                          </div>
                                        </div>
                                      </div>
                                      <h4 className="text-white text-sm font-medium truncate">{playlist.name}</h4>
                                      <p className="text-gray-400 text-xs truncate mt-1">
                                        {playlist.description ? playlist.description.replace(/<[^>]*>/g, '').substring(0, 50) : `By ${playlist.owner}`}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-12 text-white/60">
                                  <Music className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                  <p>No playlists found in this category</p>
                                </div>
                              )}
                            </section>
                          )}
                        </>
                      )}

                      {/* Album Detail View - shown when an album is selected */}
                      {selectedSpotifyAlbum && (
                        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedSpotifyAlbum(null)}>
                          <div className="bg-gray-900 border border-white/20 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                            {/* Album Header */}
                            <div className="p-6 bg-gradient-to-b from-white/10 to-transparent">
                              <div className="flex items-start gap-6">
                                <div className="w-48 h-48 rounded-lg overflow-hidden flex-shrink-0 shadow-2xl">
                                  {selectedSpotifyAlbum.images?.[0]?.url ? (
                                    <img src={selectedSpotifyAlbum.images[0].url} alt={selectedSpotifyAlbum.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                      <Music size={48} className="text-gray-600" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 pt-4">
                                  <p className="text-gray-400 text-sm uppercase tracking-wider mb-1">Album</p>
                                  <h2 className="text-3xl font-bold text-white mb-2">{selectedSpotifyAlbum.name}</h2>
                                  <p className="text-gray-300">{selectedSpotifyAlbum.artists?.map(a => a.name).join(', ')}</p>
                                  <p className="text-gray-500 text-sm mt-2">
                                    {selectedSpotifyAlbum.release_date?.split('-')[0]} • {selectedSpotifyAlbum.total_tracks || spotifyAlbumTracks.length} songs
                                  </p>
                                  <div className="flex items-center gap-3 mt-4">
                                    <button
                                      onClick={() => playSpotifyTrack(null, selectedSpotifyAlbum?.uri)}
                                      className="flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-400 rounded-full text-black font-semibold transition-all"
                                      data-testid="play-album-btn"
                                    >
                                      <Play size={20} fill="currentColor" />
                                      Play
                                    </button>
                                    <button
                                      onClick={() => setSelectedSpotifyAlbum(null)}
                                      className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                                    >
                                      <X size={20} className="text-white" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Track List */}
                            <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 300px)' }}>
                              {spotifyAlbumTracks.length > 0 ? (
                                <div className="space-y-1">
                                  {spotifyAlbumTracks.map((track, idx) => (
                                    <div 
                                      key={track.id}
                                      onClick={() => handleTrackPlayPause(track.uri, selectedSpotifyAlbum?.uri)}
                                      className={`flex items-center gap-4 p-3 hover:bg-white/10 rounded-lg cursor-pointer transition-colors group ${isTrackPlaying(track.uri) ? 'bg-green-500/10' : ''}`}
                                      data-testid={`album-track-${track.id}`}
                                    >
                                      {isTrackPlaying(track.uri) ? (
                                        <div className="w-6 flex items-center justify-center">
                                          <div className="flex items-center gap-0.5">
                                            <div className="w-0.5 h-3 bg-green-400 rounded-full animate-pulse" />
                                            <div className="w-0.5 h-4 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                                            <div className="w-0.5 h-3 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <span className="text-gray-500 text-sm w-6 text-center group-hover:hidden">{idx + 1}</span>
                                          <Play size={16} className="text-white hidden group-hover:block w-6" fill="white" />
                                        </>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <h4 className={`font-medium truncate ${isTrackPlaying(track.uri) ? 'text-green-400' : 'text-white'}`}>{track.name}</h4>
                                        <p className="text-gray-400 text-sm truncate">{track.artists?.map(a => a.name).join(', ')}</p>
                                      </div>
                                      <span className="text-gray-500 text-sm">{formatDurationMs(track.duration_ms)}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center py-12">
                                  <Loader2 size={32} className="animate-spin text-white" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Playlists Tab */}
                  {spotifyMusicTab === 'playlists' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Playlist Tracks View */}
                      <div>
                        <h3 className="text-xl font-bold text-white mb-4">
                          {selectedSpotifyPlaylist ? selectedSpotifyPlaylist.name : 'Select a Playlist'}
                        </h3>
                        <div className="bg-black border border-white/20 rounded-2xl overflow-hidden" style={{ maxHeight: '594px', overflowY: 'auto' }}>
                          {selectedSpotifyPlaylist ? (
                            spotifyPlaylistTracks.length > 0 ? (
                              spotifyPlaylistTracks.map((item, idx) => (
                                <div 
                                  key={`${item.track?.id}-${idx}`}
                                  onClick={() => item.track && playSpotifyTrack(item.track.uri, selectedSpotifyPlaylist.uri)}
                                  data-testid={`playlist-track-${idx}`}
                                  className={`flex items-center gap-4 p-4 hover:bg-white/10 hover:border-white/30 cursor-pointer transition-colors ${idx !== 0 ? 'border-t border-white/5' : ''}`}
                                >
                                  <span className="text-gray-500 text-sm w-6 text-center">{idx + 1}</span>
                                  <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                                    {item.track?.album?.images?.[0]?.url ? (
                                      <img src={item.track.album.images[0].url} alt={item.track?.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                        <Music size={20} className="text-gray-600" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-white font-medium truncate">{item.track?.name}</h4>
                                    <p className="text-gray-400 text-sm truncate">{item.track?.artists?.map(a => a.name).join(', ')}</p>
                                  </div>
                                  <span className="text-gray-500 text-sm">{formatDurationMs(item.track?.duration_ms)}</span>
                                  <button className="p-2 hover:bg-white/20 rounded-full transition-colors">
                                    <Play size={18} className="text-white" fill="currentColor" />
                                  </button>
                                </div>
                              ))
                            ) : (
                              <div className="p-8 text-center text-gray-400">
                                <Loader2 size={32} className="animate-spin mx-auto mb-4 text-white" />
                                <p>Loading tracks...</p>
                              </div>
                            )
                          ) : (
                            <div className="p-12 text-center text-gray-400">
                              <Music size={48} className="mx-auto mb-4 text-gray-600" />
                              <p>Select a playlist to see tracks</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Playlists Grid */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold text-white">Your Playlists ({spotifyPlaylists.length})</h3>
                          {selectedSpotifyPlaylist && spotifyPlaylistTracks.length > 0 && (
                            <button
                              onClick={() => {
                                setFullPlaylistData({
                                  playlist: selectedSpotifyPlaylist,
                                  tracks: spotifyPlaylistTracks
                                });
                                setShowFullPlaylistView(true);
                              }}
                              className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm font-medium hover:bg-white/10 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all"
                              data-testid="view-all-playlist-btn"
                            >
                              <Eye size={16} />
                              View All
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 min-h-[1800px] overflow-y-auto pr-2 p-1">
                          {spotifyPlaylists.map((playlist) => (
                            <div
                              key={playlist.id}
                              onClick={() => {
                                setSelectedSpotifyPlaylist(playlist);
                                fetchSpotifyPlaylistTracks(playlist.id);
                              }}
                              data-testid={`playlist-${playlist.id}`}
                              className={`relative rounded-xl overflow-hidden cursor-pointer group transition-all ${
                                selectedSpotifyPlaylist?.id === playlist.id ? 'ring-2 ring-white ring-offset-2 ring-offset-black' : ''
                              }`}
                            >
                              <div className="aspect-square">
                                {playlist.images?.[0]?.url ? (
                                  <img 
                                    src={playlist.images[0].url} 
                                    alt={playlist.name} 
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-green-600 to-green-900 flex items-center justify-center">
                                    <Music size={40} className="text-white/50" />
                                  </div>
                                )}
                              </div>
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                              <div className="absolute bottom-0 left-0 right-0 p-3">
                                <h4 className="text-white font-semibold truncate text-sm">{playlist.name}</h4>
                                <p className="text-gray-300 text-xs">{playlist.tracks?.total || 0} songs</p>
                              </div>
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                  <Play size={24} className="text-white ml-1" fill="white" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Artists Tab */}
                  {spotifyMusicTab === 'artists' && (
                    <div className="space-y-8">
                      {/* Followed Artists */}
                      <section>
                        <h3 className="text-xl font-bold text-white mb-4">
                          Followed Artists ({spotifyFollowedArtists.length})
                        </h3>
                        {spotifyFollowedArtists.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                            {spotifyFollowedArtists.map((artist) => (
                              <div
                                key={artist.id}
                                onClick={() => openArtistPage(artist.id, artist.name)}
                                className="cursor-pointer group text-center"
                                data-testid={`followed-artist-${artist.id}`}
                              >
                                <div className="relative aspect-square rounded-full overflow-hidden mb-3 mx-auto max-w-[160px]">
                                  {artist.images?.[0]?.url ? (
                                    <img 
                                      src={artist.images[0].url} 
                                      alt={artist.name} 
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                      <User size={48} className="text-gray-600" />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                    <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                      <Play size={24} className="text-white ml-1" fill="white" />
                                    </div>
                                  </div>
                                </div>
                                <h4 className="text-white font-medium truncate">{artist.name}</h4>
                                <p className="text-gray-400 text-xs">Artist</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-12 text-center text-gray-400 bg-black border border-white/20 rounded-2xl">
                            <Users size={48} className="mx-auto mb-4 text-gray-600" />
                            <p>No followed artists yet</p>
                            <p className="text-sm mt-2">Artists you follow will appear here</p>
                          </div>
                        )}
                      </section>

                      {/* Top Artists */}
                      {spotifyTopArtists.length > 0 && (
                        <section>
                          <h3 className="text-xl font-bold text-white mb-4">Your Top Artists</h3>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                            {spotifyTopArtists.slice(0, 12).map((artist, idx) => (
                              <div
                                key={artist.id}
                                onClick={() => openArtistPage(artist.id, artist.name)}
                                className="cursor-pointer group text-center"
                              >
                                <div className="relative">
                                  <div className="aspect-square rounded-full overflow-hidden mb-3 mx-auto max-w-[160px]">
                                    {artist.images?.[0]?.url ? (
                                      <img 
                                        src={artist.images[0].url} 
                                        alt={artist.name} 
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                      />
                                    ) : (
                                      <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                        <User size={48} className="text-gray-600" />
                                      </div>
                                    )}
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-full">
                                      <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                        <Play size={24} className="text-white ml-1" fill="white" />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="absolute -top-1 -left-1 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                    {idx + 1}
                                  </div>
                                </div>
                                <h4 className="text-white font-medium truncate">{artist.name}</h4>
                                <p className="text-gray-400 text-xs">{artist.genres?.[0] || 'Artist'}</p>
                              </div>
                            ))}
                          </div>
                        </section>
                      )}
                    </div>
                  )}

                  {/* Albums Tab */}
                  {spotifyMusicTab === 'albums' && (
                    <div className="space-y-8">
                      {/* Saved Albums */}
                      <section>
                        <h3 className="text-xl font-bold text-white mb-4">
                          Saved Albums ({spotifySavedAlbums.length})
                        </h3>
                        {spotifySavedAlbums.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {spotifySavedAlbums.map((item) => (
                              <div
                                key={item.album?.id}
                                onClick={() => {
                                  setSelectedSpotifyAlbum(item.album);
                                  fetchSpotifyAlbumTracks(item.album?.id);
                                }}
                                className="cursor-pointer group"
                                data-testid={`saved-album-${item.album?.id}`}
                              >
                                <div className="relative aspect-square rounded-lg overflow-hidden mb-3">
                                  {item.album?.images?.[0]?.url ? (
                                    <img 
                                      src={item.album.images[0].url} 
                                      alt={item.album?.name} 
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                      <Music size={32} className="text-gray-600" />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                    <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                      <Play size={24} className="text-white ml-1" fill="white" />
                                    </div>
                                  </div>
                                </div>
                                <h4 className="text-white text-sm font-medium truncate">{item.album?.name}</h4>
                                <p className="text-gray-400 text-xs truncate">{item.album?.artists?.map(a => a.name).join(', ')}</p>
                                <p className="text-gray-500 text-xs">{item.album?.release_date?.split('-')[0]}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-12 text-center text-gray-400 bg-black border border-white/20 rounded-2xl">
                            <Music size={48} className="mx-auto mb-4 text-gray-600" />
                            <p>No saved albums yet</p>
                            <p className="text-sm mt-2">Albums you save will appear here</p>
                          </div>
                        )}
                      </section>
                    </div>
                  )}
                </>
              )}

            </>
          )}
        </div>
      )}

      {/* YouTube Music Videos Tab */}
      {activeTab === 'youtube-music-videos' && !searchQuery && (
        <YouTubeMusicVideosSection
          loading={youtubeMusicLoading}
          playlists={youtubeMusicPlaylists}
          activePlaylist={activeYoutubePlaylist}
          onPlaylistChange={handleYoutubePlaylistChange}
          featuredVideo={youtubeMusicFeaturedVideo}
          artists={youtubeMusicArtists}
          videos={youtubeMusicVideos}
          filteredVideos={filteredYoutubeMusicVideos}
          selectedArtist={selectedYoutubeMusicArtist}
          onArtistSelect={setSelectedYoutubeMusicArtist}
          onPlayVideo={playYoutubeMusicVideo}
          onShufflePlay={shuffleAndPlayMusicVideos}
          sortOption={youtubeMusicSortOption}
          onSortChange={setYoutubeMusicSortOption}
        />
      )}

      {/* Live TV Tab */}
      {activeTab === 'live-tv' && !searchQuery && (
        <LiveTVSection apiUrl={process.env.REACT_APP_BACKEND_URL || ''} />
      )}

      {/* TV Shows Tab */}
      {activeTab === 'tv-shows' && !searchQuery && (
        <TVShowsSection />
      )}

      {/* Playlists Tab */}
      {activeTab === 'playlists' && !searchQuery && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <ListVideo size={28} className="text-cyan-400" />
              <h2 className="text-xl md:text-2xl font-bold text-white">Playlists</h2>
              {!playlistsLoading && (
                <span className="text-gray-400 text-sm">({playlists.length})</span>
              )}
            </div>
            <button
              onClick={() => setShowCreatePlaylist(true)}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-black rounded-lg font-medium hover:bg-cyan-400 transition-colors"
            >
              <Plus size={18} />
              <span>Create Playlist</span>
            </button>
          </div>

          {/* Loading State */}
          {playlistsLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-cyan-400" />
            </div>
          )}

          {/* Empty State */}
          {!playlistsLoading && playlists.length === 0 && (
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 text-center">
              <ListVideo size={48} className="text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-300 mb-2">No playlists yet</h3>
              <p className="text-gray-500 text-sm mb-4">
                Create a playlist to organize your favorite movies and music videos.
              </p>
              <button
                onClick={() => setShowCreatePlaylist(true)}
                className="px-4 py-2 bg-cyan-500 text-black rounded-lg font-medium hover:bg-cyan-400 transition-colors"
              >
                Create Your First Playlist
              </button>
            </div>
          )}

          {/* Playlists Grid */}
          {!playlistsLoading && playlists.length > 0 && !selectedPlaylist && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {playlists.map((playlist) => (
                <button
                  key={playlist.ratingKey}
                  onClick={() => handlePlaylistClick(playlist)}
                  className="group relative bg-gray-900 border border-gray-700 rounded-xl p-4 text-left hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(6,182,212,0.1)] transition-all duration-300"
                  data-testid={`playlist-${playlist.ratingKey}`}
                >
                  <div className="aspect-square bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-lg mb-3 flex items-center justify-center relative">
                    <ListVideo size={40} className="text-cyan-400" />
                    {playlist.smart && (
                      <span className="absolute top-1 right-1 px-1.5 py-0.5 bg-purple-500/80 text-white text-[9px] font-bold rounded">
                        SMART
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-white truncate">{playlist.title}</h3>
                  <p className="text-xs text-gray-400 mt-1">{playlist.leafCount || 0} items</p>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePlaylist(playlist.ratingKey);
                      }}
                      className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                      title="Delete playlist"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Selected Playlist View */}
          {selectedPlaylist && (
            <div className="space-y-4">
              {/* Back button and playlist header */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    setSelectedPlaylist(null);
                    setPlaylistItems([]);
                  }}
                  className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <ChevronLeft size={20} className="text-gray-400" />
                </button>
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedPlaylist.title}</h3>
                  <p className="text-sm text-gray-400">
                    {playlistItemsLoading ? 'Loading...' : `${playlistItems.length} items`}
                  </p>
                </div>
              </div>

              {/* Playlist Items Loading */}
              {playlistItemsLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 size={32} className="animate-spin text-cyan-400 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">Loading playlist items...</p>
                  </div>
                </div>
              )}

              {/* Playlist Items */}
              {!playlistItemsLoading && playlistItems.length === 0 && (
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 text-center">
                  <Film size={48} className="text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-300 mb-2">This playlist is empty</h3>
                  <p className="text-gray-500 text-sm">
                    Add items from your library using the 3-dot menu on any media card.
                  </p>
                </div>
              )}

              {!playlistItemsLoading && playlistItems.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 md:gap-4">
                  {playlistItems.map((item, idx) => (
                    <div key={`playlist-item-${item.ratingKey}-${idx}`} className="relative group">
                      <MediaCard
                        item={item}
                        onClick={handleItemClick}
                        onMenuAction={handleMenuAction}
                        plexUrl={plexUrl}
                        isInWatchlist={isInWatchlist}
                        onToggleWatchlist={toggleWatchlistItem}
                      />
                      <button
                        onClick={() => handleRemoveFromPlaylist(selectedPlaylist.ratingKey, item.playlistItemID || item.ratingKey)}
                        className="absolute top-1 right-1 p-1.5 bg-red-500/80 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        title="Remove from playlist"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Watchlist Tab */}
      {activeTab === 'watchlist' && !searchQuery && (
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 hover:border-gray-500 hover:shadow-[0_0_20px_rgba(255,255,255,0.05)] transition-all duration-300">
          {watchlistItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center">
              <Heart size={48} className="text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-300 mb-2">Your watchlist is empty</h3>
              <p className="text-gray-500 text-sm mb-4">
                Browse your Plex libraries and click the heart icon to add items to your list.
              </p>
              <button 
                onClick={() => setActiveTab('home')}
                className="px-4 py-2 bg-cyan-500 text-black rounded-lg font-medium hover:bg-cyan-400 transition-colors"
              >
                Browse Libraries
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Heart size={24} className="text-red-500" fill="currentColor" />
                  My List ({watchlistItems.length})
                </h2>
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to clear your entire watchlist?')) {
                      setWatchlistItems([]);
                    }
                  }}
                  className="text-sm text-gray-400 hover:text-red-400 transition-colors"
                >
                  Clear All
                </button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 md:gap-4">
                {watchlistItems.map((item, idx) => (
                  <div key={`watchlist-${item.ratingKey}-${idx}`} className="relative group">
                    <MediaCard
                      item={item}
                      onClick={handleItemClick}
                      onMenuAction={handleMenuAction}
                      plexUrl={plexUrl}
                      isInWatchlist={isInWatchlist}
                      onToggleWatchlist={toggleWatchlistItem}
                    />
                    <button
                      onClick={() => toggleWatchlistItem(item)}
                      className="absolute top-1 right-1 p-1.5 bg-red-500/80 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      title="Remove from My List"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Detail Modal - Full Page View */}
      {selectedItem && (
        <MediaDetailModal 
          item={selectedItem} 
          onClose={() => {
            setSelectedItem(null);
            setAutoPlayNext(false);
          }}
          plexUrl={plexUrl || 'http://76.201.4.251:32400'}
          relatedMovies={homePageMovies.filter(m => m.ratingKey !== selectedItem.ratingKey).slice(0, 8)}
          allMovies={homePageMovies}
          autoPlay={autoPlayNext}
          onNavigate={(newItem, options = {}) => {
            console.log('onNavigate called:', newItem?.title, 'options:', options);
            // Set autoPlay flag before updating selected item
            if (options.autoPlay) {
              console.log('Setting autoPlayNext to true');
              setAutoPlayNext(true);
            }
            setSelectedItem(newItem);
          }}
        />
      )}

      {/* Edit Modal */}
      {editItem && (
        <EditMediaModal 
          item={editItem}
          onClose={() => setEditItem(null)}
          onSave={handleEditSave}
        />
      )}

      {/* Play History Modal */}
      {playHistoryItem && (
        <PlayHistoryModal 
          item={playHistoryItem}
          onClose={() => setPlayHistoryItem(null)}
        />
      )}

      {/* Match Modal */}
      {matchItem && (
        <MatchModal 
          item={matchItem}
          onClose={() => setMatchItem(null)}
          onMatch={handleMatchApplied}
        />
      )}

      {/* Create Playlist Modal */}
      {showCreatePlaylist && (
        <Portal>
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
            style={{ zIndex: 99999 }}
            onClick={() => {
              setShowCreatePlaylist(false);
              setNewPlaylistName('');
            }}
          >
            <div 
              className="bg-gray-900 border border-gray-600 rounded-xl w-full max-w-md p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <ListVideo size={20} className="text-cyan-400" />
                  Create Playlist
                </h2>
                <button
                  onClick={() => {
                    setShowCreatePlaylist(false);
                    setNewPlaylistName('');
                  }}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Playlist Name
                  </label>
                  <input
                    type="text"
                    value={newPlaylistName}
                    onChange={(e) => setNewPlaylistName(e.target.value)}
                    placeholder="Enter playlist name..."
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newPlaylistName.trim()) {
                        handleCreatePlaylist();
                      }
                    }}
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowCreatePlaylist(false);
                      setNewPlaylistName('');
                    }}
                    className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreatePlaylist}
                    disabled={!newPlaylistName.trim()}
                    className="flex-1 px-4 py-2 bg-cyan-500 text-black rounded-lg font-medium hover:bg-cyan-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Add to Playlist Modal */}
      {addToPlaylistItem && (
        <Portal>
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
            style={{ zIndex: 99999 }}
            onClick={() => setAddToPlaylistItem(null)}
          >
            <div 
              className="bg-gray-900 border border-gray-600 rounded-xl w-full max-w-md shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Plus size={18} className="text-cyan-400" />
                  Add to Playlist
                </h2>
                <button
                  onClick={() => setAddToPlaylistItem(null)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              
              <div className="p-4">
                <p className="text-gray-400 text-sm mb-4">
                  Adding &ldquo;{addToPlaylistItem.title}&rdquo; to:
                </p>
                
                {playlistsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={24} className="animate-spin text-cyan-400" />
                  </div>
                ) : playlists.filter(p => !p.smart).length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-gray-500 mb-2">No regular playlists yet</p>
                    <p className="text-gray-600 text-xs mb-4">Smart playlists don't support adding items</p>
                    <button
                      onClick={() => {
                        setAddToPlaylistItem(null);
                        setShowCreatePlaylist(true);
                      }}
                      className="px-4 py-2 bg-cyan-500 text-black rounded-lg font-medium hover:bg-cyan-400 transition-colors"
                    >
                      Create Playlist
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {playlists.filter(p => !p.smart).map((playlist) => (
                      <button
                        key={playlist.ratingKey}
                        onClick={() => handleAddToPlaylist(playlist.ratingKey, addToPlaylistItem.ratingKey)}
                        className="w-full flex items-center gap-3 p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-left"
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <ListVideo size={20} className="text-cyan-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{playlist.title}</p>
                          <p className="text-xs text-gray-400">{playlist.leafCount || 0} items</p>
                        </div>
                        <Plus size={18} className="text-gray-400" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setAddToPlaylistItem(null);
                    setShowCreatePlaylist(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Plus size={16} />
                  <span>Create New Playlist</span>
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Full Playlist View Modal - Library Style */}
      {showFullPlaylistView && fullPlaylistData && (
        <Portal>
          <div 
            className="fixed inset-0 bg-black flex flex-col"
            style={{ zIndex: 99998 }}
          >
            {/* Header */}
            <div className="px-8 py-6 bg-black">
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-3">
                  <h1 className="text-4xl font-bold text-white">{fullPlaylistData.playlist.name}</h1>
                  <span className="text-gray-400 text-lg">• {fullPlaylistData.tracks.length} songs</span>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Search Filter */}
                  <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search in playlist..."
                      value={playlistFilter.search}
                      onChange={(e) => setPlaylistFilter(prev => ({ ...prev, search: e.target.value }))}
                      className="w-64 pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
                    />
                  </div>
                  
                  {/* Close Button */}
                  <button
                    onClick={() => {
                      setShowFullPlaylistView(false);
                      setPlaylistFilter({ genre: '', search: '' });
                      setPlaylistSortBy({ column: 'index', direction: 'asc' });
                    }}
                    className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                  >
                    <X size={24} className="text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Table Header - Clickable Columns */}
            <div className="px-8 py-3 border-b border-gray-800 bg-black sticky top-0">
              <div className="grid gap-4 text-gray-500 text-xs font-medium uppercase tracking-wider" 
                   style={{ gridTemplateColumns: '40px 2fr 1.5fr 80px 100px 50px 60px 100px' }}>
                {/* # */}
                <button 
                  onClick={() => setPlaylistSortBy(prev => ({ 
                    column: 'index', 
                    direction: prev.column === 'index' && prev.direction === 'asc' ? 'desc' : 'asc' 
                  }))}
                  className={`text-left hover:text-white transition-colors ${playlistSortBy.column === 'index' ? 'text-white' : ''}`}
                >
                  # {playlistSortBy.column === 'index' && (playlistSortBy.direction === 'asc' ? '↑' : '↓')}
                </button>
                
                {/* Title */}
                <button 
                  onClick={() => setPlaylistSortBy(prev => ({ 
                    column: 'title', 
                    direction: prev.column === 'title' && prev.direction === 'asc' ? 'desc' : 'asc' 
                  }))}
                  className={`text-left hover:text-white transition-colors ${playlistSortBy.column === 'title' ? 'text-white' : ''}`}
                >
                  Title {playlistSortBy.column === 'title' && (playlistSortBy.direction === 'asc' ? '↑' : '↓')}
                </button>
                
                {/* Album */}
                <button 
                  onClick={() => setPlaylistSortBy(prev => ({ 
                    column: 'album', 
                    direction: prev.column === 'album' && prev.direction === 'asc' ? 'desc' : 'asc' 
                  }))}
                  className={`text-left hover:text-white transition-colors ${playlistSortBy.column === 'album' ? 'text-white' : ''}`}
                >
                  Album {playlistSortBy.column === 'album' && (playlistSortBy.direction === 'asc' ? '↑' : '↓')}
                </button>
                
                {/* Genre */}
                <button 
                  onClick={() => setPlaylistSortBy(prev => ({ 
                    column: 'genre', 
                    direction: prev.column === 'genre' && prev.direction === 'asc' ? 'desc' : 'asc' 
                  }))}
                  className={`text-left hover:text-white transition-colors ${playlistSortBy.column === 'genre' ? 'text-white' : ''}`}
                >
                  Genre {playlistSortBy.column === 'genre' && (playlistSortBy.direction === 'asc' ? '↑' : '↓')}
                </button>
                
                {/* Plays */}
                <div className="text-left">Plays</div>
                
                {/* BPM */}
                <button 
                  onClick={() => setPlaylistSortBy(prev => ({ 
                    column: 'bpm', 
                    direction: prev.column === 'bpm' && prev.direction === 'asc' ? 'desc' : 'asc' 
                  }))}
                  className={`text-left hover:text-white transition-colors ${playlistSortBy.column === 'bpm' ? 'text-white' : ''}`}
                >
                  BPM {playlistSortBy.column === 'bpm' && (playlistSortBy.direction === 'asc' ? '↑' : '↓')}
                </button>
                
                {/* Duration */}
                <button 
                  onClick={() => setPlaylistSortBy(prev => ({ 
                    column: 'duration', 
                    direction: prev.column === 'duration' && prev.direction === 'asc' ? 'desc' : 'asc' 
                  }))}
                  className={`text-left hover:text-white transition-colors ${playlistSortBy.column === 'duration' ? 'text-white' : ''}`}
                >
                  Duration {playlistSortBy.column === 'duration' && (playlistSortBy.direction === 'asc' ? '↑' : '↓')}
                </button>
                
                {/* Date Added */}
                <button 
                  onClick={() => setPlaylistSortBy(prev => ({ 
                    column: 'dateAdded', 
                    direction: prev.column === 'dateAdded' && prev.direction === 'asc' ? 'desc' : 'asc' 
                  }))}
                  className={`text-left hover:text-white transition-colors whitespace-nowrap ${playlistSortBy.column === 'dateAdded' ? 'text-white' : ''}`}
                >
                  Date Added {playlistSortBy.column === 'dateAdded' && (playlistSortBy.direction === 'asc' ? '↑' : '↓')}
                </button>
              </div>
            </div>
            
            {/* Track List */}
            <div className="flex-1 overflow-y-scroll bg-black max-h-[calc(100vh-350px)] scrollbar-visible">
              {fullPlaylistData.tracks
                .filter(item => {
                  if (!playlistFilter.search) return true;
                  const searchLower = playlistFilter.search.toLowerCase();
                  return (
                    item.track?.name?.toLowerCase().includes(searchLower) ||
                    item.track?.artists?.some(a => a.name.toLowerCase().includes(searchLower)) ||
                    item.track?.album?.name?.toLowerCase().includes(searchLower)
                  );
                })
                .map((item, idx) => ({ ...item, originalIndex: idx }))
                .sort((a, b) => {
                  const { column, direction } = playlistSortBy;
                  const multiplier = direction === 'asc' ? 1 : -1;
                  
                  switch (column) {
                    case 'index':
                      return multiplier * (a.originalIndex - b.originalIndex);
                    case 'title':
                      return multiplier * (a.track?.name || '').localeCompare(b.track?.name || '');
                    case 'album':
                      return multiplier * (a.track?.album?.name || '').localeCompare(b.track?.album?.name || '');
                    case 'dateAdded':
                      return multiplier * (new Date(a.added_at || 0) - new Date(b.added_at || 0));
                    case 'duration':
                      return multiplier * ((a.track?.duration_ms || 0) - (b.track?.duration_ms || 0));
                    default:
                      return 0;
                  }
                })
                .map((item, displayIdx) => {
                  const track = item.track;
                  if (!track) return null;
                  
                  const isCurrentTrack = spotifyCurrentTrack?.uri === track.uri;
                  const dateAdded = item.added_at ? new Date(item.added_at).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  }) : '—';
                  
                  // Generate mock tags based on track name (since Spotify doesn't provide tags)
                  const isDraggable = playlistSortBy.column === 'index';
                  const isDraggedOver = dragOverIndex === item.originalIndex;
                  const isBeingDragged = draggedTrackIndex === item.originalIndex;
                  
                  return (
                    <div
                      key={`${track.id}-${displayIdx}`}
                      draggable={isDraggable}
                      onDragStart={(e) => isDraggable && handleDragStart(e, item.originalIndex)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => isDraggable && handleDragOver(e, item.originalIndex)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => isDraggable && handleDrop(e, item.originalIndex)}
                      onClick={() => playSpotifyTrack(track.uri, fullPlaylistData.playlist.uri)}
                      className={`grid gap-4 px-8 py-1.5 cursor-pointer transition-all border-b border-gray-900 hover:bg-black border border-white/20 group ${
                        isCurrentTrack ? 'bg-white/10' : ''
                      } ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''} ${
                        isDraggedOver ? 'border-t-2 border-t-white bg-white/5' : ''
                      } ${isBeingDragged ? 'opacity-50' : ''}`}
                      style={{ gridTemplateColumns: '40px 2fr 1.5fr 80px 100px 50px 60px 100px' }}
                    >
                      {/* # / Playing Indicator / Drag Handle */}
                      <div className="flex items-center gap-2">
                        {isDraggable && (
                          <div className="text-gray-600 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="5" cy="5" r="2"/>
                              <circle cx="12" cy="5" r="2"/>
                              <circle cx="5" cy="12" r="2"/>
                              <circle cx="12" cy="12" r="2"/>
                              <circle cx="5" cy="19" r="2"/>
                              <circle cx="12" cy="19" r="2"/>
                            </svg>
                          </div>
                        )}
                        {isCurrentTrack && spotifyIsPlaying ? (
                          <span className="text-white font-medium">▶</span>
                        ) : (
                          <span className={`text-sm ${isCurrentTrack ? 'text-white' : 'text-gray-500'}`}>
                            {item.originalIndex + 1}
                          </span>
                        )}
                      </div>
                      
                      {/* Title + Artist */}
                      <div className="flex flex-col justify-center min-w-0 leading-tight">
                        <span className={`text-sm font-medium truncate ${isCurrentTrack ? 'text-white' : 'text-white'}`}>
                          {track.name}
                        </span>
                        <span className="text-gray-500 text-xs truncate">
                          {track.artists?.map(a => a.name).join(', ')}
                        </span>
                      </div>
                      
                      {/* Album */}
                      <div className="flex items-center">
                        <span className="text-gray-400 text-xs truncate">{track.album?.name}</span>
                      </div>
                      
                      {/* Genre - Placeholder */}
                      <div className="flex items-center">
                        <span className="text-gray-500 text-xs">—</span>
                      </div>
                      
                      {/* Plays - Real stream count from Spotify81 API */}
                      <div className="flex items-center">
                        <span className="text-gray-400 text-xs">
                          {formatPlayCount(track)}
                        </span>
                      </div>
                      
                      {/* BPM - Placeholder */}
                      <div className="flex items-center">
                        <span className="text-gray-500 text-xs">120</span>
                      </div>
                      
                      {/* Duration */}
                      <div className="flex items-center">
                        <span className="text-gray-400 text-xs">{formatDurationMs(track.duration_ms)}</span>
                      </div>
                      
                      {/* Date Added */}
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-xs">{dateAdded}</span>
                        {/* Action Icons */}
                        <div className="flex items-center gap-1 ml-auto opacity-0 group-hover:opacity-100">
                          <button 
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 hover:bg-gray-800 rounded transition-colors"
                          >
                            <Play size={14} className="text-gray-400" />
                          </button>
                          <button 
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 hover:bg-gray-800 rounded transition-colors"
                          >
                            <Heart size={14} className="text-gray-400" />
                          </button>
                          <button 
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 hover:bg-gray-800 rounded transition-colors"
                          >
                            <MoreVertical size={14} className="text-gray-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </Portal>
      )}

      {/* Album Art Full-Size Popup */}
      {showAlbumArtPopup && spotifyCurrentTrack?.album?.images?.[0]?.url && (
        <Portal>
          <div 
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-8 animate-in fade-in duration-200"
            style={{ zIndex: 999999 }}
            onClick={() => setShowAlbumArtPopup(false)}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowAlbumArtPopup(false)}
              className="absolute top-6 right-6 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
            >
              <X size={24} className="text-white" />
            </button>
            
            {/* Album Art Container */}
            <div 
              className="relative max-w-2xl max-h-[80vh] animate-in zoom-in-95 duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Large Album Art */}
              <img 
                src={spotifyCurrentTrack.album.images[0].url}
                alt={spotifyCurrentTrack.album.name || spotifyCurrentTrack.name}
                className="w-full h-auto max-h-[70vh] object-contain rounded-2xl shadow-2xl shadow-black/50"
              />
              
              {/* Track Info Below */}
              <div className="mt-6 text-center">
                <h2 className="text-2xl font-bold text-white mb-2">{spotifyCurrentTrack.name}</h2>
                <p className="text-gray-400 text-lg">{spotifyCurrentTrack.artists?.map(a => a.name).join(', ')}</p>
                <p className="text-gray-500 mt-2">{spotifyCurrentTrack.album.name}</p>
                {spotifyCurrentTrack.album.release_date && (
                  <p className="text-gray-600 text-sm mt-1">
                    Released: {new Date(spotifyCurrentTrack.album.release_date).getFullYear()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Album Detail Modal */}
      {showAlbumModal && selectedAlbum && (
        <Portal>
          <div 
            className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
            style={{ zIndex: 999997 }}
            onClick={closeAlbumModal}
            data-testid="album-detail-modal"
          >
            <div 
              className="w-full max-w-3xl max-h-[90vh] bg-black border border-white/20 rounded-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300"
              onClick={(e) => e.stopPropagation()}
            >
                {/* Album Header - Fixed */}
                <div className="relative flex-shrink-0">
                  {/* Close Button */}
                  <button
                    onClick={closeAlbumModal}
                    className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                  >
                    <X size={20} className="text-white" />
                  </button>
                  
                  {/* Album Cover and Info */}
                  <div className="flex flex-col md:flex-row gap-6 p-6 bg-gradient-to-b from-white/10 to-transparent">
                    <div className="w-48 h-48 flex-shrink-0 mx-auto md:mx-0">
                      {selectedAlbum.images?.[0]?.url ? (
                        <img 
                          src={selectedAlbum.images[0].url} 
                          alt={selectedAlbum.name} 
                          className="w-full h-full object-cover rounded-xl shadow-2xl"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-800 rounded-xl flex items-center justify-center">
                          <Music size={48} className="text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col justify-end text-center md:text-left">
                      <p className="text-white/60 text-sm uppercase tracking-wider">{selectedAlbum.album_type || 'Album'}</p>
                      <h2 className="text-2xl md:text-3xl font-bold text-white mt-1">{selectedAlbum.name}</h2>
                      <p className="text-white/70 mt-2">
                        {selectedAlbum.artists?.map(a => a.name).join(', ')}
                      </p>
                      <p className="text-white/50 text-sm mt-1">
                        {selectedAlbum.release_date?.split('-')[0]} • {selectedAlbum.total_tracks || albumTracks.length} tracks
                      </p>
                      
                      {/* Play Album Button */}
                      <button
                        onClick={() => playAlbum(selectedAlbum.uri)}
                        className="mt-4 inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-400 text-white font-bold rounded-full transition-all hover:scale-105 w-fit mx-auto md:mx-0"
                      >
                        <Play size={20} fill="white" />
                        Play Album
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Track List - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6 min-h-0">
                  <h3 className="text-lg font-bold text-white mb-4">Tracks</h3>
                  
                  {albumLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={32} className="animate-spin text-white" />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {albumTracks.map((track, idx) => (
                        <div
                          key={track.id}
                          onClick={() => playSpotifyTrack(track.uri, selectedAlbum.uri)}
                          className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/10 cursor-pointer group transition-colors"
                          data-testid={`album-track-${track.id}`}
                        >
                          <span className="w-6 text-center text-white/50 group-hover:hidden">{idx + 1}</span>
                          <Play size={16} className="text-white hidden group-hover:block w-6" fill="white" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{track.name}</p>
                            <p className="text-white/50 text-sm truncate">
                              {track.artists?.map(a => a.name).join(', ')}
                            </p>
                          </div>
                          <span className="text-white/40 text-sm">{formatDurationMs(track.duration_ms)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
          </div>
        </Portal>
      )}

      {/* YouTube Video Player Modal */}
      {showYouTubePlayer && selectedYouTubeVideo && (
        <Portal>
          <div 
            className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
            style={{ zIndex: 999999 }}
            onClick={closeYouTubePlayer}
            data-testid="youtube-player-modal"
          >
            {/* Navigation Arrow - Previous */}
            <button
              onClick={(e) => { e.stopPropagation(); playPreviousVideo(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-4 bg-black/70 hover:bg-red-600 rounded-full transition-all hover:scale-110 z-20 group"
              data-testid="youtube-prev-btn"
            >
              <ChevronLeft size={32} className="text-white group-hover:scale-110 transition-transform" />
            </button>
            
            {/* Navigation Arrow - Next */}
            <button
              onClick={(e) => { e.stopPropagation(); playNextVideo(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-4 bg-black/70 hover:bg-red-600 rounded-full transition-all hover:scale-110 z-20 group"
              data-testid="youtube-next-btn"
            >
              <ChevronRight size={32} className="text-white group-hover:scale-110 transition-transform" />
            </button>

            <div 
              className="w-full max-w-5xl animate-in zoom-in-95 duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeYouTubePlayer}
                className="absolute top-4 right-4 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors z-10"
              >
                <X size={24} className="text-white" />
              </button>
              
              {/* YouTube Embed with enablejsapi for auto-play next */}
              <div className="relative aspect-video rounded-2xl overflow-hidden bg-black border border-white/20 shadow-2xl">
                <iframe
                  ref={youtubePlayerRef}
                  key={selectedYouTubeVideo.id}
                  src={`${selectedYouTubeVideo.embed_url}?autoplay=1&rel=0&enablejsapi=1&origin=${window.location.origin}`}
                  title={selectedYouTubeVideo.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              
              {/* Video Info */}
              <div className="mt-4 px-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedYouTubeVideo.title}</h3>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-white/60">{selectedYouTubeVideo.channel}</span>
                      <span className="text-white/40 text-sm">
                        Video {currentVideoIndex + 1} of {artistYouTubeVideos.length}
                      </span>
                    </div>
                  </div>
                  <a 
                    href={selectedYouTubeVideo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-full transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    Watch on YouTube
                  </a>
                </div>
                
                {/* Keyboard Shortcuts Hint */}
                <div className="mt-4 flex items-center gap-4 text-white/40 text-sm">
                  <span>← Previous</span>
                  <span>→ Next</span>
                  <span>ESC Close</span>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Artist Detail Page Modal */}
      {showArtistPage && (
        <Portal>
          <div 
            className="fixed inset-0 bg-black overflow-y-auto"
            style={{ zIndex: 999998 }}
          >
            {/* Header with back button */}
            <div className="sticky top-0 z-10 bg-black/80 backdrop-blur-xl border-b border-white/10">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
                <button
                  onClick={closeArtistPage}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <ChevronLeft size={24} className="text-white" />
                </button>
                <h1 className="text-xl font-bold text-white">{selectedArtist?.name}</h1>
              </div>
            </div>

            {artistLoading ? (
              <div className="flex items-center justify-center py-40">
                <Loader2 size={48} className="animate-spin text-white" />
              </div>
            ) : artistDetails ? (
              <div className="max-w-7xl mx-auto px-6 pb-32">
                {/* Hero Section */}
                <div className="relative h-80 md:h-96 rounded-3xl overflow-hidden mt-6 bg-black border border-white/20 hover:border-white/40 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-all">
                  {artistDetails.images?.[0]?.url ? (
                    <img 
                      src={artistDetails.images[0].url} 
                      alt={artistDetails.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-800 to-black flex items-center justify-center">
                      <Users size={80} className="text-white/20" />
                    </div>
                  )}
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  
                  {/* Artist Info Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="flex items-end gap-6">
                      {/* Profile Picture */}
                      <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-black shadow-2xl flex-shrink-0">
                        {artistDetails.images?.[1]?.url || artistDetails.images?.[0]?.url ? (
                          <img 
                            src={artistDetails.images[1]?.url || artistDetails.images[0]?.url} 
                            alt={artistDetails.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                            <User size={48} className="text-white/50" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <p className="text-white/60 text-sm mb-1">Artist</p>
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">{artistDetails.name}</h1>
                        <div className="flex items-center gap-4 text-white/60 text-sm">
                          <span>{artistDetails.followers?.toLocaleString()} followers</span>
                          {artistDetails.genres?.length > 0 && (
                            <>
                              <span>•</span>
                              <span>{artistDetails.genres.slice(0, 3).join(', ')}</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={() => artistDetails.top_tracks?.[0]?.uri && playSpotifyTrack(artistDetails.top_tracks[0].uri)}
                          className="px-6 py-3 bg-white text-black font-bold rounded-full hover:scale-105 transition-transform flex items-center gap-2"
                        >
                          <Play size={20} fill="black" /> Play
                        </button>
                        <button className="px-6 py-3 bg-black border border-white/30 text-white font-medium rounded-full hover:bg-white/10 hover:border-white/50 transition-all">
                          Follow
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
                  {/* Left Column - Music & Videos */}
                  <div className="lg:col-span-2 space-y-8">
                    {/* Music Section - Songs from all playlists */}
                    <section className="bg-black border border-white/20 rounded-2xl p-6 hover:border-white/30 hover:shadow-[0_0_25px_rgba(255,255,255,0.08)] transition-all">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-white">Music</h2>
                        <div className="flex items-center gap-2">
                          {artistSongsLoading && (
                            <Loader2 size={16} className="animate-spin text-white/40" />
                          )}
                          <span className="text-white/40 text-sm">
                            {artistPlaylistSongs.length > 0 
                              ? `${artistPlaylistSongs.length} songs from your playlists`
                              : artistDetails?.top_tracks?.length 
                                ? `${artistDetails.top_tracks.length} top songs`
                                : '0 songs'
                            }
                          </span>
                        </div>
                      </div>
                      {/* Show all songs - height calculated to show ~15-20 songs before scrolling */}
                      <div className="space-y-1 max-h-[calc(100vh-450px)] min-h-[400px] overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}>
                        {(artistPlaylistSongs.length > 0 ? artistPlaylistSongs : artistDetails?.top_tracks || []).map((track, idx) => {
                          const isPlaying = spotifyCurrentTrack?.uri === track.uri;
                          return (
                            <div 
                              key={`${track.id}-${idx}`}
                              onClick={() => {
                                playSpotifyTrack(track.uri);
                                setShowPlayerBar(true);
                              }}
                              className={`flex items-center gap-4 p-3 rounded-xl cursor-pointer transition-all group ${
                                isPlaying ? 'bg-white/10 border border-white/30' : 'hover:bg-white/5'
                              }`}
                            >
                              <span className={`text-sm w-6 text-center ${isPlaying ? 'text-white' : 'text-white/40'}`}>
                                {isPlaying ? '▶' : idx + 1}
                              </span>
                              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-black border border-white/10">
                                {track.album_art ? (
                                  <img src={track.album_art} alt={track.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Music size={20} className="text-white/30" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium truncate ${isPlaying ? 'text-white' : 'text-white'}`}>{track.name}</p>
                                <p className="text-white/50 text-sm truncate">
                                  {track.album}
                                  {track.playlist_name && <span className="text-white/30"> • {track.playlist_name}</span>}
                                </p>
                              </div>
                              <span className="text-white/40 text-sm">{formatDurationMs(track.duration_ms)}</span>
                              <button className="p-2 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded-full transition-all">
                                <Heart size={18} className="text-white/60" />
                              </button>
                              <button className="p-2 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded-full transition-all">
                                <MoreVertical size={18} className="text-white/60" />
                              </button>
                            </div>
                          );
                        })}
                        {artistPlaylistSongs.length === 0 && (!artistDetails?.top_tracks || artistDetails.top_tracks.length === 0) && !artistSongsLoading && (
                          <div className="text-center py-8 text-white/40">
                            <Music size={32} className="mx-auto mb-2" />
                            <p>No songs found in your playlists</p>
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Videos Section - YouTube Integration */}
                    <section className="bg-black border border-white/20 rounded-2xl p-6 hover:border-white/30 hover:shadow-[0_0_25px_rgba(255,255,255,0.08)] transition-all">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <h2 className="text-xl font-bold text-white">Videos</h2>
                          <div className="flex items-center gap-1 px-2 py-1 bg-red-600 rounded">
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                            <span className="text-white text-xs font-bold">YouTube</span>
                          </div>
                          {youtubeChannelName && (
                            <span className="text-white/50 text-xs">from {youtubeChannelName}</span>
                          )}
                        </div>
                        <span className="text-white/40 text-sm">
                          {youtubeLoading ? 'Loading...' : `${artistYouTubeVideos.length} videos`}
                        </span>
                      </div>
                      
                      {youtubeLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 size={32} className="animate-spin text-white" />
                        </div>
                      ) : artistYouTubeVideos.length > 0 ? (
                        <div className="grid grid-cols-2 gap-4 max-h-[500px] overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}>
                          {artistYouTubeVideos.map((video, index) => (
                            <div 
                              key={video.id}
                              className="group cursor-pointer"
                              onClick={() => openYouTubePlayer(video, index)}
                              data-testid={`youtube-video-${video.id}`}
                            >
                              <div className="relative aspect-video rounded-xl overflow-hidden bg-black border border-white/10 hover:border-red-500/50 transition-all">
                                {video.thumbnail ? (
                                  <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
                                    <Video size={32} className="text-white/30" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                  <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
                                    <Play size={32} className="text-white ml-1" fill="white" />
                                  </div>
                                </div>
                                {/* Video number badge */}
                                <div className="absolute top-2 left-2 w-6 h-6 bg-black/80 rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                                  {index + 1}
                                </div>
                              </div>
                              <p className="text-white font-medium mt-2 truncate text-sm">{video.title}</p>
                              <p className="text-white/50 text-xs truncate">{video.channel}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-white/40">
                          <Video size={32} className="mx-auto mb-2" />
                          <p>No videos found for this artist</p>
                        </div>
                      )}
                    </section>
                  </div>

                  {/* Right Column - Albums & Info */}
                  <div className="space-y-8">
                    {/* Albums Section */}
                    <section className="bg-black border border-white/20 rounded-2xl p-6 hover:border-white/30 hover:shadow-[0_0_25px_rgba(255,255,255,0.08)] transition-all">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-white">Albums</h2>
                        <span className="text-white/40 text-sm">{artistDetails.albums?.length || 0} albums</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                        {artistDetails.albums?.map((album) => (
                          <div 
                            key={album.id}
                            className="group cursor-pointer"
                            onClick={() => openAlbumModal(album)}
                            data-testid={`album-card-${album.id}`}
                          >
                            <div className="relative aspect-square rounded-xl overflow-hidden bg-black border border-white/10 hover:border-white/30 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all">
                              {album.images?.[0]?.url ? (
                                <img src={album.images[0].url} alt={album.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
                                  <Music size={32} className="text-white/30" />
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                                  <Play size={24} className="text-black ml-0.5" fill="black" />
                                </div>
                              </div>
                            </div>
                            <p className="text-white font-medium mt-2 truncate text-sm">{album.name}</p>
                            <p className="text-white/50 text-xs">{album.release_date?.split('-')[0]} • {album.album_type}</p>
                          </div>
                        ))}
                      </div>
                    </section>

                    {/* Artist Info */}
                    <section className="bg-black border border-white/20 rounded-2xl p-6 hover:border-white/30 hover:shadow-[0_0_25px_rgba(255,255,255,0.08)] transition-all">
                      <h2 className="text-xl font-bold text-white mb-4">About</h2>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-white/60">Followers</span>
                          <span className="text-white font-medium">{artistDetails.followers?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-white/60">Popularity</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-white rounded-full"
                                style={{ width: `${artistDetails.popularity}%` }}
                              />
                            </div>
                            <span className="text-white font-medium text-sm">{artistDetails.popularity}%</span>
                          </div>
                        </div>
                        {artistDetails.genres?.length > 0 && (
                          <div>
                            <span className="text-white/60 block mb-2">Genres</span>
                            <div className="flex flex-wrap gap-2">
                              {artistDetails.genres.map((genre, idx) => (
                                <span 
                                  key={idx}
                                  className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-white text-xs"
                                >
                                  {genre}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {artistDetails.external_urls?.spotify && (
                          <a 
                            href={artistDetails.external_urls.spotify}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mt-4"
                          >
                            <ExternalLink size={16} />
                            <span className="text-sm">Open in Spotify</span>
                          </a>
                        )}
                      </div>
                    </section>

                    {/* Related Artists */}
                    {artistDetails.related_artists?.length > 0 && (
                      <section className="bg-black border border-white/20 rounded-2xl p-6 hover:border-white/30 hover:shadow-[0_0_25px_rgba(255,255,255,0.08)] transition-all">
                        <h2 className="text-xl font-bold text-white mb-4">Related Artists</h2>
                        <div className="grid grid-cols-3 gap-3">
                          {artistDetails.related_artists.map((related) => (
                            <div 
                              key={related.id}
                              onClick={() => openArtistPage(related.id, related.name)}
                              className="text-center cursor-pointer group"
                            >
                              <div className="w-full aspect-square rounded-full overflow-hidden bg-black border border-white/10 group-hover:border-white/30 transition-all mb-2">
                                {related.image ? (
                                  <img src={related.image} alt={related.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <User size={24} className="text-white/30" />
                                  </div>
                                )}
                              </div>
                              <p className="text-white text-xs truncate">{related.name}</p>
                            </div>
                          ))}
                        </div>
                      </section>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-40 text-white/60">
                <AlertCircle size={48} className="mb-4" />
                <p>Could not load artist information</p>
                <button 
                  onClick={() => openArtistPage(selectedArtist?.id, selectedArtist?.name)}
                  className="mt-4 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </Portal>
      )}
    </div>
  );
};

export default MediaFactory;
